import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  untracked,
  ViewChild,
  WritableSignal
} from '@angular/core';

import cytoscape, {Core, EdgeSingular, Layouts, NodeDataDefinition, NodeSingular} from 'cytoscape';
import {HttpClientModule} from '@angular/common/http';
import {GraphMap, GraphNode, GraphNodeLink} from '../api/interfaces/graph-node.interface';
import {
  DefaultNodeProperties,
  DefaultValues,
  EdgeArrowType,
  MapStateDto,
  metadataAfterFanOut,
  OtherLinksMode,
  OtherNodesMode,
  PACKAGE_NAMES
} from '@maporium-workspace/shared';
import {MatDrawer, MatSidenavModule} from '@angular/material/sidenav';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {CommonModule} from '@angular/common';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatSelectModule} from '@angular/material/select';
import {ReactiveFormsModule} from '@angular/forms';
import {LayoutTypes} from '../shared/layouts';
import {CytoscapeService} from '../services/cy-services/cytoscape.service';
import {NodeService} from '../api/services/node.service';
import {MatInputModule} from '@angular/material/input';
import {ColorSketchModule} from 'ngx-color/sketch';
import {MapService} from '../api/services/map.service';
import {BlockUIModule} from 'ng-block-ui';
import {catchError, EMPTY, filter, finalize, of, retry, Subscription, switchMap, tap, throwError, timer} from 'rxjs';
import {UserService} from '../api/services/user.service';
import {DefaultSidebarWidth, LinkViewMode, resolveOtherLinksMode, resolveOtherNodesMode, SettingsService} from '../api/services/setting.service';
import {applyOtherLinksClasses, applyOtherNodesClasses, clearOtherClasses, otherLinksClassNames} from './other-elements-display';
import {MapInfoSectionComponent} from '../sidebar/map-info-section.component';
import {GeneralInfo} from '../api/interfaces/general-info';
// eslint-disable-next-line @typescript-eslint/no-var-requires
// @ts-ignore
import {GptService} from '../api/services/gpt.service';
import {GptMapResponse} from '../api/interfaces/gpt-map.response';
import {SETTINGS_STORAGE_KEY} from '../nav/local-storage.settings';
import {ButtonPressService} from '../services/button-press.service';
import {AudioService} from '../services/audio.service';
import {drawGradientNode} from '../../cytoscape-extensions/draw-gradient-node';

import {
  centeringInProgress,
  destroyPanZoom,
  ExpandCollapseInProgress,
  restorePanZoomDynamic,
  savePanZoomState,
  storePanZoomDynamic
} from '../../cytoscape-extensions/pan-zoom-storage';
import {
  isRestoringPanZoom,
  lastMultiSelectionMetadataIds,
  multiSelectionMetadataChanged,
  undoRedoJustHappened,
  updateInProgress
} from '../shared/helpers/signal.helpers';
import {ResizableModule, ResizeEvent} from 'angular-resizable-element';
import {ViewService} from '../api/services/view.service';
import {ToursPanelComponent} from '../shared/tours-panel/tours-panel.component';
import {ScenePanelComponent} from '../shared/scene-panel/scene-panel.component';
import {LegendPanelComponent} from '../shared/legend-panel/legend-panel.component';
import {ToursService} from '../api/services/tour.service';
import {MapStateService} from '../api/services/map-state.service';
import {SidebarService} from '../sidebar-form/sidebar.service';
import {initCyPlugins} from './cy-plugins.import';
import {CyGridPluginOptions, setGridPlugin} from './grid-plugin.config';
import {SidebarFormComponent} from '../sidebar-form/sidebar-form.component';
import {isEmpty, isEqual, omit, orderBy, set, uniqBy} from 'lodash';
import {MultiUpdateEvent} from '../sidebar-form/dtos/node-allowed-multi-change';
import {MetaData} from '../api/interfaces/metadata';
import {AppWideStateService} from '../services/app-state/app-wide-state.service';
import {isInIframe} from '../shared/helpers/maporium.validators';
import {
  callAddAnchor,
  createNewAnchorsAndInitiateEdgeEditing,
  getEqualDistributedAnchorPoints,
  getPointDataFromAnchorsArray,
  mapAnchorsToXYArray
} from '../shared/helpers/edge-editing.helper';
import {isDoubleTapInProgress} from '../../cytoscape-extensions/double-tap.extension';
import {ActivatedRoute, Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {CreateNewMapDialogComponent} from '../dialogs/create-new-map-dialog.component';
import {LegendService} from '../api/services/legend.service';
import {
  ChangeContainerCommand,
  ContainerChangeSnapshot,
  CreateNodeCommand,
  CreateOverlayElementCommand,
  DeleteOverlayElementCommand,
  FocusSessionService,
  GraphNodeSnapshot,
  MoveOverlayElementCommand,
  MultiPropertyCommand,
  MultiPropertySnapshot,
  MultiTextChangeCommand,
  MultiTextSnapshot,
  OverlayElementMoveSnapshot,
  OverlayElementResizeSnapshot,
  OverlayElementSnapshot,
  PropertyCommand,
  ResizeOverlayElementCommand,
  StyleChangeSnapshot,
  TextChangeCommand,
  UndoRedoManagerService,
  UpdateStyleCommand
} from '../services/undo-redo';
import {LoadingAnimationComponent} from '../shared/components/loading-logo';
import {SmapLoadingAnimationComponent} from '../shared/components/smap-loading-animation';
import {BlockUiInterceptor} from '../interceptors/block-ui.interceptor';
import {TourMatMenuModule, TourService} from 'ngx-ui-tour-md-menu';
import {TourStepTemplateComponent} from '../shared/components/tour-step-template/tour-step-template.component';
import {Logger} from '../shared/helpers/logger.helper';
import {DashboardService} from './dashboard.service';
import {ThumbnailService} from '../api/services/thumbnail.service';
import {PackageService} from '../api/services/package.service';
import {SmapStylesDirective} from '../shared/directives/smap-styles.directive';
import {MapRoleService} from '../acl/map-role.service';
import {Permission} from '../acl/map-user-roles.enum';
import {ModelService} from '../api/services/model.service';
import {getAdaptiveGridColor} from '../shared/helpers/color-helpers';
import {LinkStateData, StateLessLink,} from '../../../../maporium/src/app/entities/types/link-state.data';
import {NodeStateData, StateLessNode} from '../../../../maporium/src/app/entities/types/node-state-data';
import {OverlayElementEvent, OverlayElementManagerService} from '../services/overlay-element-manager.service';
import {OverlayElementApiService} from '../api/services/overlay-element.service';
import {OverlayElement} from '../api/interfaces/overlay-element.interface';
import {OnboardingService} from '../services/onboarding.service';
import {takeUntilDestroyed, toObservable} from "@angular/core/rxjs-interop";
import {distinctUntilChanged} from "rxjs/operators";
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {CommentsStateManager} from '../shared/components/comments/comments.state-manager';

initCyPlugins();
Object.defineProperty(window, 'getTappedLink', {
  value: () => DashboardComponent.tappedLink,
  writable: false,
  configurable: true
});

@Component({
  selector: 'maporium-dashboard',
  templateUrl: './dashboard.component.html',
  standalone: true,
  styleUrls: ['./dashboard.component.scss'],
  imports: [
    HttpClientModule,
    MatSidenavModule,
    MatButtonModule,
    MatIconModule,
    CommonModule,
    MatFormFieldModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatInputModule,
    ColorSketchModule,
    BlockUIModule,
    MapInfoSectionComponent,
    ResizableModule,
    ToursPanelComponent,
    ScenePanelComponent,
    LegendPanelComponent,
    SidebarFormComponent,
    LoadingAnimationComponent,
    SmapLoadingAnimationComponent,
    TourMatMenuModule,
    TourStepTemplateComponent,
    SmapStylesDirective,
    TranslatePipe
  ]

})

export class DashboardComponent implements OnInit, OnDestroy {
  static tappedNode: GraphNode | null = null;
  static tappedLink: GraphNodeLink | null = null;
  static tappedOverlayElement: OverlayElement | null = null;

  // Maps EdgeArrowType to valid Cytoscape ArrowShape values
  private ftueInitilized = false;
  private onboardingTriggerSub?: Subscription;
  // True when the tour we're running was auto-presented on first sign-in (not
  // re-triggered from the help menu). Controls whether the create-smap dialog
  // is auto-opened from any bubble or only the last one (MAP-1245).
  private firstSigninAutoTour = false;
  private tourEndSub?: Subscription;
  private lastTourStepAnchorId: string | null = null;
  private tourArrowTimers: number[] = [];

  private mapEdgeArrowTypeToCytoscape(arrowType: EdgeArrowType): string {
    const mapping: Record<EdgeArrowType, string> = {
      'triangle': 'triangle',
      'circle-triangle': 'triangle', // Fallback to triangle
      'triangle-backcurve': 'triangle-backcurve',
      'tee': 'tee',
      'circle': 'circle',
      'chevron': 'chevron',
      'triangle-cross': 'triangle-cross',
      'vee': 'vee',
      'square': 'square',
      'diamond': 'diamond',
      'none': 'none'
    };
    return mapping[arrowType] || 'triangle';
  }

  @ViewChild('cytoscapeContainer', { static: true }) cytoscapeContainer: ElementRef | undefined;
  @ViewChild('tooltipContainer', { static: true }) tooltipContainer: ElementRef | undefined;
  @ViewChild('canvas', { static: true }) canvas: HTMLCanvasElement | undefined;
  private cy: cytoscape.Core | undefined;

  @ViewChild('drawer') drawer: MatDrawer | undefined;
  drawerWidth = DefaultSidebarWidth;

  // The sidebar width is persisted as raw px (it's resized on desktop) and
  // reused verbatim on every device, so a desktop-set width overflows a phone
  // and swallows the whole screen. Clamp what we actually apply — to the
  // binding and to `--sidebar-width` (which feeds the canvas width + centring
  // math) — so the panel always leaves a usable strip of canvas. Raw
  // drawerWidth is kept for persistence.
  get appliedSidebarWidth(): number {
    // Use the document's client width, not window.innerWidth: on mobile the
    // latter can report an inflated layout width (e.g. 634 on a 375px phone),
    // which would let the panel overflow the screen.
    const viewport = document.documentElement.clientWidth || window.innerWidth;
    const max = Math.max(240, Math.round(viewport * 0.9));
    return Math.min(this.drawerWidth, max);
  }


  public hasNoMap = false;
  public nodes: GraphNode[] = [];
  public selectedNodes: GraphNode[] = [];
  public selectedLinks: GraphNodeLink[] = [];
  public isReadOnly = false;
  public buildNumber = 'local';
  public hasNodesNoNodes = false;
  public sidebarResizable = true;
  public isChangingLinkSource = false;
  public isCanvasLoaded = true;

  public get focusedLink() {
    return DashboardComponent.tappedLink;
  }

  public get focusedNode() {
    return DashboardComponent.tappedNode;
  }

  public get focusedOverlayElement() {
    return DashboardComponent.tappedOverlayElement;
  }

  private drawerMaxWidth = 400;
  private drawerMinWidth = 200;
  private edgeViewMode: LinkViewMode = LinkViewMode.ALL;
  private nodeViewModePassScheduled = false;
  private isDetailsOpen = false;
  public isUnlinking = false;
  private cyLayout: Layouts | undefined;
  public currentMap: GraphMap | undefined;
  public isSideBarOpen = false;
  private settings: Partial<GeneralInfo> | undefined;
  private contextMenuInstance: any;
  private isLoading = false;
  private initialLoadComplete = false;
  private lastKnownSidebarWidth = 0;
  private isCentering = false;
  public readModeAppState = this.appWideStateService.readMode();

  private tooltipTimeout: any;
  private edgeEditingInstance: any;
  private linkEndpointSignal: WritableSignal<{
    point: 'source' | 'target',
    direction: 'top' | 'bottom' | 'right' | 'left'
  } | null> = signal(null);
  private pendingLinkUpdateIds = new Set<string>();
  private pendingLinkUpdateTimer: ReturnType<typeof setTimeout> | null = null;
  public static multiSelectedElements: (GraphNode | GraphNodeLink)[] = [];

  private destroyRef = inject(DestroyRef);
  private commentsStateManager = inject(CommentsStateManager);
  readonly commentDraftActive = this.commentsStateManager.draftActive;

  private copyPasteSub: Subscription | undefined;
  private createFromMenu = false;
  private menuSubscription: Subscription | undefined;
  private undoRedoSubscription: Subscription | undefined;
  private eh: any;
  private hasCommentsPollingSub = new Subscription();
  private renderHandlerRegistered = false;

  constructor(private nodeService: NodeService,
              private cyService: CytoscapeService,
              private userService: UserService,
              private packageService: PackageService,
              private buttonPressService: ButtonPressService,
              private audioService: AudioService,
              private settingsService: SettingsService,
              private toursService: ToursService,
              private route: ActivatedRoute,
              private mapService: MapService,
              private gptService: GptService,
              private mapRoleService: MapRoleService,
              private router: Router,
              private undoRedoManager: UndoRedoManagerService,
              private viewService: ViewService,
              private mapStateService: MapStateService,
              private appWideStateService: AppWideStateService,
              public tourService: TourService,
              private sidebarService: SidebarService,
              private dashboardService: DashboardService,
              private modelService: ModelService,
              private thumbnailService: ThumbnailService,
              private cdr: ChangeDetectorRef,
              private overlayManager: OverlayElementManagerService,
              private overlayElementService: OverlayElementApiService,
              private matDialog: MatDialog,
              private legendService: LegendService,
              private translate: TranslateService,
              private focusSessionService: FocusSessionService,
              private onboardingService: OnboardingService) {

    effect(() => {
      this.isCanvasLoaded = this.appWideStateService.canvasLoadedSignal();
      if (this.cy) {
        untracked(() => this.cy!.style().update());
      }
    });

    effect(() => {
      const layout = this.cyService.getLayoutChangedSignal();
      if (layout !== null) {
        if (this.cy) {
          untracked(() => {
            const options = this.cyService.getSafeLayoutOptions(layout, this.cy!);
            this.cy!.layout(options).run();
          });
        }
      }
    });
    effect(() => {
      this.readModeAppState = this.appWideStateService.readMode();
      if (this.cy) {
        untracked(() => this.cy!.style().update());
      }
    });

    // Update grid color when map styles change
    effect(() => {
      const map = this.mapService.getSelectedMapSignal();
      if (map?.styles && this.cy && this.settings) {
        untracked(() => {
          setGridPlugin(this.cy as any, {
            drawGrid: isInIframe() ? false : Boolean(this.settings!.showGrid),
            geometricGuideline: isInIframe() ? false : this.settings!.showAlignLines,
            snapToGridDuringDrag: isInIframe() ? false : this.settings!.snapToGrid,
            snapToAlignmentLocationDuringDrag: isInIframe() ? false : this.settings!.snapToAlignLines,
            gridSpacing: this.settings!.gridSize,
            panGrid: true,
            gridColor: this.getGridColor(map),
            gridStackOrder: 0,
            guidelinesStyle: {
              ...CyGridPluginOptions.guidelinesStyle,
              strokeStyle: this.getGridColor(map)
            }
          });
        });
      }
    });

    // Note: Undo/redo restoration is now handled internally by each command's undo() method
    // The UndoRedoManagerService manages the command stack and each command knows how to restore its state

    effect(() => {
      const state = this.mapStateService.getStateSignal();
      if (!this.cy || this.hasNoMap) return;  // Guard against no map state
      untracked(() => {
        this.listenToMapStateChanges(state);
        this.cy!.style().update();
      });
    }, {allowSignalWrites: true});

    effect(() => {
      const editMode = this.sidebarService.getEditModeSignal();
      if (!this.cy || this.hasNoMap) return;  // Guard against no map state
      untracked(() => {
        this.cy!.style().update();
        if (this.cyService.linkLabelRenderer) {
          setTimeout(() => {
            this.cyService.linkLabelRenderer?.clearClassCache();
            this.cyService.linkLabelRenderer?.redraw();
          }, 50);
        }
      });
    });

    // Listen to model elements refresh signal
    effect(() => {
      const refreshData = this.modelService.getElementsRefreshSignal()();
      if (!this.cy || this.hasNoMap) return;  // Guard against no map state
      if (refreshData) {
        this.refreshCanvasElements(refreshData.updatedElements);
      }
    }, {allowSignalWrites: true});

    // @TODO Rework to plain signal remember about the agressive build
    toObservable(this.settingsService.linkViewModeSignal)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((mode) => this.applyEdgeViewModeClasses(mode, this.settingsService.otherLinksMode));

    // The "other links" treatment is independent of the mode above, so it needs
    // its own reactive re-application when changed.
    toObservable(this.settingsService.otherLinksModeSignal)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((otherLinksMode) => this.applyEdgeViewModeClasses(this.settingsService.linkViewMode, otherLinksMode));

    toObservable(this.settingsService.otherNodesModeSignal)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.applyNodeViewModeClasses());

    // @TODO Rework to plain signal remember about the agressive build
    toObservable(this.mapService.selectedMap)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(() => this.cy !== undefined),
        distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
        retry({
          count: 3,
          delay: 200
        })
      )
      .subscribe((newMap) => {
        if (!newMap?.id) return;  // Guard against undefined map
        const sameMap = this.currentMap?.id === newMap?.id;
        if (isInIframe()) {
          this.currentMap = newMap;
        }
        if (!sameMap) {
          this.resetAllSelected(this.cy as cytoscape.Core);
        }
        this.listenToMapChanges(this.cy as cytoscape.Core, this.eh, newMap);
        // Update HTML edge label colors for the new map's appearance
        this.cyService.updateStylesForAppearanceChange();

        // Grid color will be handled by the effect below

        // Show FTUE tour after map has finished loading (only on first sign-in).
        if (this.shouldShowFTUETour()) {
          this.markTourAsAutoShown();
          this.firstSigninAutoTour = true;
          setTimeout(() => {
            // In smaplet mode, explicitly wait for the legend fetch so
            // hasLegendAnchor() sees fresh data when buildTourSteps decides
            // whether to include bubble 3 (MAP-1245).
            if (this.isInIframe() && newMap?.id) {
              this.legendService.get(newMap.id).subscribe({
                next: () => this.initializeAndStartFTUETour(),
                error: () => this.initializeAndStartFTUETour(),
              });
            } else {
              this.initializeAndStartFTUETour();
            }
          }, 1500)
        }
      });
  }

  // MAP-1245: the tour is auto-presented only on the very first sign-in (app)
  // or on the first smaplet ever opened in this browser. It can be re-run any
  // time from the help menu — see OnboardingService.
  private static readonly FIRST_SIGNIN_TOUR_KEY = 'maporium_first_signin_tour_shown';
  private static readonly SMAPLET_TOUR_KEY = 'maporium_smaplet_tour_shown';

  private shouldShowFTUETour(): boolean {
    // Smaplet: once shown (in any smaplet), don't auto-present again on refresh
    // or when loading a different smaplet. Clearing the browser cache resets
    // the localStorage flag and lets it show again.
    if (isInIframe()) {
      // A smaplet opened by clicking a link inside another smaplet carries
      // noTour=1. It launches in a fresh top-level window whose localStorage
      // partition doesn't see the "already shown" flag, so honour the explicit
      // flag to avoid re-showing the FTUE on every hop (MAP-1535).
      if (this.route.snapshot?.queryParams['noTour'] === '1') {
        return false;
      }
      return localStorage.getItem(DashboardComponent.SMAPLET_TOUR_KEY) !== 'true';
    }
    const alreadyShown = localStorage.getItem(DashboardComponent.FIRST_SIGNIN_TOUR_KEY) === 'true';
    return !alreadyShown && !this.hasNoMap;
  }

  private markTourAsAutoShown(): void {
    const key = isInIframe()
      ? DashboardComponent.SMAPLET_TOUR_KEY
      : DashboardComponent.FIRST_SIGNIN_TOUR_KEY;
    localStorage.setItem(key, 'true');
  }

  private tourStepShowSub?: Subscription;

  private initializeAndStartFTUETour(): void {
    if (this.ftueInitilized) {
      return;
    }
    const tourSteps = this.buildTourSteps();
    const lastStepAnchorId = tourSteps.length ? tourSteps[tourSteps.length - 1].anchorId : null;
    const mobile = this.isMobileDevice();
    // Drives the phone-only bubble shrink in tour-step-template.component.scss
    // (body.ftue-mobile); removed again when the tour ends.
    document.body.classList.toggle('ftue-mobile', mobile);
    this.tourService.initialize(
      tourSteps, {
        nextBtnTitle: this.translate.instant('DASHBOARD.TOUR_NEXT'),
        enableBackdrop: !mobile,
        popoverClass: 'tour-popover',
        backdropConfig: {
          backgroundColor: 'rgba(0,0,0,0.5)'
        },
        stepDimensions: mobile
          ? {minWidth: 'unset', maxWidth: '86vw'}
          : {minWidth: '300px', maxWidth: '400px'}
      }
    );

    // Set the stage on the current smap before the first bubble shows
    // (MAP-1245): legend open, sidebar open, assistant open+collapsed.
    this.prepareStageForTour();

    setTimeout(() => {
      this.tourService.start();
    }, 1000)

    this.tourStepShowSub?.unsubscribe();
    this.tourStepShowSub = this.tourService.stepShow$
      .subscribe((step) => {
        // Cancel pending alignment/reveal timers from the previous step so
        // late passes don't fight this step's work or reveal before we've
        // placed the arrow. Without this, prev/next spam stacks timers and
        // the outgoing step's 140ms pass can fire after the incoming panel
        // is already on screen.
        this.tourArrowTimers.forEach(id => clearTimeout(id));
        this.tourArrowTimers = [];

        const anchor = step.step.anchorId;
        this.lastTourStepAnchorId = anchor ?? null;
        if(anchor === 'canvas-anchor') {
          const popoverClass = step.step.popoverClass || 'canvas-popover';
          const element = document.querySelector(`.${popoverClass}`);
          if(element) {
            this.tourArrowTimers.push(window.setTimeout(() => {
              element.classList.add('position-absolute');
            }, 100));
          }
        }
        // Align the arrow tip with the centre of the highlighted anchor.
        // Run three passes — one sync and two short catches for late-settling
        // anchor-active / overlay positioning. Every tour bubble starts
        // hidden via SCSS (`.tour-step { opacity: 0 }`) so the panel never
        // flashes at CDK's pre-shift position; we reveal after the final
        // pass. Non-arrow bubbles (welcome, canvas) skip alignTourArrow but
        // still need the reveal, so the timer runs regardless of the anchor.
        if (anchor) {
          this.scrollAnchorIntoHorizontalView(anchor);
          this.alignTourArrow(anchor);
          [60, 140, 240].forEach(delay =>
            this.tourArrowTimers.push(
              window.setTimeout(() => {
                this.scrollAnchorIntoHorizontalView(anchor);
                this.alignTourArrow(anchor);
              }, delay)));
        }
        this.tourArrowTimers.push(window.setTimeout(() => {
          document
            .querySelectorAll('.mat-mdc-menu-panel.tour-step')
            .forEach(el => el.classList.add('tour-arrow-ready'));
        }, 260));
      });

    // MAP-1245: when the tour ends, auto-open the Create Smap dialog on the
    // prompt tab — from any bubble if this is the first-sign-in auto tour,
    // from the last bubble only if the user re-ran the tour from the menu.
    this.tourEndSub?.unsubscribe();
    this.tourEndSub = this.tourService.end$.subscribe(() => {
      this.tourArrowTimers.forEach(id => clearTimeout(id));
      this.tourArrowTimers = [];
      document.body.classList.remove('ftue-mobile');
      const endedOnLastBubble = !!lastStepAnchorId && this.lastTourStepAnchorId === lastStepAnchorId;
      if (this.firstSigninAutoTour || endedOnLastBubble) {
        this.openCreateSmapPromptTab();
      }
      this.firstSigninAutoTour = false;
      this.lastTourStepAnchorId = null;
    });
    this.ftueInitilized = true;
  }

  /**
   * Open the legend, open the sidebar, and open the smap assistant in its
   * collapsed state so all three tour anchors are present in the DOM when
   * the bubbles start (MAP-1245 AC "set the stage").
   */
  private prepareStageForTour(): void {
    try {
      // Legend: use the service so the panel reacts in the same tab.
      this.legendService.requestOpen();
    } catch { /* no legend available */ }

    try {
      // Sidebar — toggleSidebar publishes the change to subscribers.
      this.settingsService.toggleSidebar({forceOpen: true});
    } catch { /* no sidebar service */ }

    // AC for MAP-1245 says "open the assistant collapsed" before the tour,
    // but the MatBottomSheet overlay covers the tour backdrop, making every
    // subsequent highlight unusable. Leaving the assistant alone until the
    // ticket is clarified — bubble 10 still references the toolbar icon,
    // which remains visible.
  }

  private openCreateSmapPromptTab(): void {
    if (this.isInIframe()) return;
    this.matDialog.open(CreateNewMapDialogComponent, {width: '500px', data: {activeTab: 'prompt'}});
  }

  /**
   * Onboarding guide bubbles — content, titles, anchor setups and control buttons
   * come from MAP-1245. Keep the text verbatim: QA diffs against the ticket.
   * Smaplet (iframe) variants adjust copy and skip some bubbles per AC.
   */
  private buildTourSteps(): any[] {
    const steps: any[] = [];
    const isSmaplet = this.isInIframe();
    const mobile = this.isMobileDevice();
    const hasScenes = this.isScenesOrStoriesAvailable();

    // Bubble 1 — WELCOME TO SMAP™
    const welcomeOpening = isSmaplet
      ? this.translate.instant('DASHBOARD.TOUR_WELCOME_OPENING_SMAPLET')
      : this.translate.instant('DASHBOARD.TOUR_WELCOME_OPENING');
    steps.push({
      anchorId: 'welcome',
      title: this.translate.instant('DASHBOARD.TOUR_TITLE_WELCOME'),
      content:
        `${welcomeOpening}<br><br>` +
        this.translate.instant('DASHBOARD.TOUR_WELCOME_BODY'),
      nextBtnTitle: this.translate.instant('DASHBOARD.TOUR_NEXT'),
      showArrow: false,
      showProgress: true
    });

    // Bubble 2 — EXPLORE WITH EASE (centered in highlighted canvas)
    steps.push({
      anchorId: 'canvas-anchor',
      title: this.translate.instant('DASHBOARD.TOUR_TITLE_EXPLORE'),
      content: this.translate.instant('DASHBOARD.TOUR_EXPLORE_CONTENT'),
      prevBtnTitle: this.translate.instant('DASHBOARD.TOUR_PREVIOUS'),
      nextBtnTitle: this.translate.instant('DASHBOARD.TOUR_NEXT'),
      popoverClass: 'canvas-popover',
      showArrow: false,
      showProgress: true
    });

    // Bubble 3 — GET ORIENTED FAST (legend panel highlighted).
    // Skip when no legend-anchor will be in the DOM, otherwise the tour library
    // can't resolve the anchor and the sequence breaks (MAP-1245 smaplet issue).
    if (this.hasLegendAnchor()) {
      const legendSecond = isSmaplet
        ? ''
        : this.translate.instant('DASHBOARD.TOUR_ORIENT_LEGEND_MODELS');
      steps.push({
        anchorId: 'legend-anchor',
        title: this.translate.instant('DASHBOARD.TOUR_TITLE_ORIENT'),
        content:
          this.translate.instant('DASHBOARD.TOUR_ORIENT_CONTENT') +
          legendSecond,
        prevBtnTitle: this.translate.instant('DASHBOARD.TOUR_PREVIOUS'),
        nextBtnTitle: this.translate.instant('DASHBOARD.TOUR_NEXT'),
        enableBackdrop: !mobile,
        showProgress: true
      });
    }

    // Bubble 4 — FOCUS ON DETAILS (properties section in sidebar)
    steps.push({
      anchorId: 'properties-anchor',
      title: this.translate.instant('DASHBOARD.TOUR_TITLE_DETAILS'),
      content: this.translate.instant('DASHBOARD.TOUR_DETAILS_CONTENT'),
      prevBtnTitle: this.translate.instant('DASHBOARD.TOUR_PREVIOUS'),
      nextBtnTitle: this.translate.instant('DASHBOARD.TOUR_NEXT'),
      popoverClass: 'sidebar-popover',
      enableBackdrop: !mobile,
      placement: {horizontal: true},
      showProgress: true
    });

    // Bubble 5 — SPARK CONVERSATIONS (skipped when smaplet disables comments)
    if (this.isCommentingAvailable()) {
      steps.push({
        anchorId: 'commentsContainer',
        title: this.translate.instant('DASHBOARD.TOUR_TITLE_CONVERSATIONS'),
        content: this.translate.instant('DASHBOARD.TOUR_CONVERSATIONS_CONTENT'),
        prevBtnTitle: this.translate.instant('DASHBOARD.TOUR_PREVIOUS'),
        nextBtnTitle: this.translate.instant('DASHBOARD.TOUR_NEXT'),
        isAsync: true,
        asyncStepTimeout: 2000,
        popoverClass: 'sidebar-comments',
        enableBackdrop: !mobile,
        placement: {horizontal: true},
        showProgress: true
      });
    }

    // Bubble 6 — CONNECT THE DOTS (link display in toolbar)
    // Smaplet: previous + DONE if no scenes/stories step will follow.
    const connectIsLastForSmaplet = isSmaplet && !hasScenes;
    steps.push({
      anchorId: 'link-menu-anchor',
      title: this.translate.instant('DASHBOARD.TOUR_TITLE_CONNECT'),
      content: this.translate.instant('DASHBOARD.TOUR_CONNECT_CONTENT'),
      prevBtnTitle: this.translate.instant('DASHBOARD.TOUR_PREVIOUS'),
      ...(connectIsLastForSmaplet
        ? {endBtnTitle: this.translate.instant('DASHBOARD.TOUR_DONE')}
        : {nextBtnTitle: this.translate.instant('DASHBOARD.TOUR_NEXT')}),
      enableBackdrop: !mobile,
      placement: {xPosition: 'before'},
      showProgress: true
    });

    // Bubble 7 — FOLLOW THE GUIDE (home + scenes + stories group).
    // Skipped for smaplet with no visible scenes or stories.
    if (!isSmaplet || hasScenes) {
      const hasViews = this.hasScenes();
      const hasStories = this.hasStories();
      const showFirst = !isSmaplet || hasViews;
      const showSecond = !isSmaplet || hasStories;
      const para1 = showFirst
        ? this.translate.instant('DASHBOARD.TOUR_GUIDE_SCENES')
        : '';
      const para2 = showSecond
        ? this.translate.instant('DASHBOARD.TOUR_GUIDE_STORIES')
        : '';
      const followContent = [para1, para2].filter(Boolean).join('<br><br>');
      steps.push({
        anchorId: 'home-scenes-stories-group',
        title: this.translate.instant('DASHBOARD.TOUR_TITLE_GUIDE'),
        content: followContent,
        prevBtnTitle: this.translate.instant('DASHBOARD.TOUR_PREVIOUS'),
        ...(isSmaplet
          ? {endBtnTitle: this.translate.instant('DASHBOARD.TOUR_DONE')}
          : {nextBtnTitle: this.translate.instant('DASHBOARD.TOUR_NEXT')}),
        enableBackdrop: !mobile,
        showProgress: true
      });
    }

    // Non-smaplet-only bubbles 8–11
    if (!isSmaplet) {
      // Bubble 8 — MAKE YOURSELF AT HOME
      // The anchor sits at the right edge of the toolbar, so there's no room
      // to shift the bubble right enough for a left-side arrow to land on the
      // group. Use xPosition:'before' → bubble appears to the left of the
      // anchor with the arrow on its right side pointing at the group.
      steps.push({
        anchorId: 'help-user-mode-group',
        title: this.translate.instant('DASHBOARD.TOUR_TITLE_HOME'),
        content: this.translate.instant('DASHBOARD.TOUR_HOME_CONTENT'),
        prevBtnTitle: this.translate.instant('DASHBOARD.TOUR_PREVIOUS'),
        nextBtnTitle: this.translate.instant('DASHBOARD.TOUR_NEXT'),
        enableBackdrop: !mobile,
        placement: {xPosition: 'before'},
        showProgress: true
      });

      // Bubble 9 — SPREAD THE SMAP (smap icon + name group)
      steps.push({
        anchorId: 'smap-title-group',
        title: this.translate.instant('DASHBOARD.TOUR_TITLE_SPREAD'),
        content: this.translate.instant('DASHBOARD.TOUR_SPREAD_CONTENT'),
        prevBtnTitle: this.translate.instant('DASHBOARD.TOUR_PREVIOUS'),
        nextBtnTitle: this.translate.instant('DASHBOARD.TOUR_NEXT'),
        enableBackdrop: !mobile,
        showProgress: true
      });

      // Bubble 10 — ENLIST AI TO HELP
      // AC wants assistant in toolbar + canvas + generator in sidebar all
      // highlighted. The tour library highlights a single anchor at a time —
      // keep the toolbar assistant anchor (most visible) and let the
      // set-stage step open the assistant collapsed so it's present below.
      steps.push({
        anchorId: 'assistant-anchor',
        title: this.translate.instant('DASHBOARD.TOUR_TITLE_AI'),
        content: this.translate.instant('DASHBOARD.TOUR_AI_CONTENT'),
        prevBtnTitle: this.translate.instant('DASHBOARD.TOUR_PREVIOUS'),
        nextBtnTitle: this.translate.instant('DASHBOARD.TOUR_NEXT'),
        enableBackdrop: !mobile,
        showProgress: true
      });

      // Bubble 11 — SAVE PRECIOUS TIME (models menu)
      steps.push({
        anchorId: 'models-menu-anchor',
        title: this.translate.instant('DASHBOARD.TOUR_TITLE_MODELS'),
        content: this.translate.instant('DASHBOARD.TOUR_MODELS_CONTENT'),
        prevBtnTitle: this.translate.instant('DASHBOARD.TOUR_PREVIOUS'),
        nextBtnTitle: this.translate.instant('DASHBOARD.TOUR_NEXT'),
        enableBackdrop: !mobile,
        showProgress: true
      });

      // Bubble 12 — LET'S GET SMAPPING! (store + smaps group)
      steps.push({
        anchorId: 'store-smaps-group',
        title: this.translate.instant('DASHBOARD.TOUR_TITLE_SMAPPING'),
        content: this.translate.instant('DASHBOARD.TOUR_SMAPPING_CONTENT'),
        prevBtnTitle: this.translate.instant('DASHBOARD.TOUR_PREVIOUS'),
        endBtnTitle: this.translate.instant('DASHBOARD.TOUR_DONE'),
        enableBackdrop: !mobile,
        showProgress: true
      });
    }

    return steps;
  }

  /**
   * True when at least one visible scene exists for the current context.
   * Smaplet uses public-only views; non-smaplet uses all current views.
   */
  private hasScenes(): boolean {
    const views = isInIframe() ? this.viewService.currentPublicViews : this.viewService.currentViews;
    return !!(views && views.length > 0);
  }

  private hasStories(): boolean {
    const tours = isInIframe() ? this.toursService.currentPublicTours : this.toursService.currentTours;
    return !!(tours && tours.length > 0);
  }

  private isCommentingAvailable(): boolean {
    // Check if commenting is available based on role or guest comments
    const map = this.mapService.getCurrentSelectedMapFromStore();
    const isInIframeContext = this.isInIframe();

    // In iframe: check if guest comments are enabled
    if (isInIframeContext) {
      return map?.hasGuestComments || false;
    }

    // For starter maps, check if user has commenting permissions (commenter, editor, manager roles).
    // Guard against undefined map — buildTourSteps can fire before a map is fully
    // hydrated and an unguarded access here would throw and abort the tour build.
    if (map?.isStarter) {
      return this.hasMapPermission('comment');
    }

    // In application: check user permissions
    // User needs at least 'comment' permission to access commenting functionality
    return this.hasMapPermission('comment');
  }

  private isScenesOrStoriesAvailable(): boolean {
    let views = this.viewService.currentViews;
    let tours = this.toursService.currentTours;
    if (isInIframe()) {
      views = this.viewService.currentPublicViews;
      tours = this.toursService.currentPublicTours;
    }
    return (views && views.length > 0) || (tours && tours.length > 0);
  }

  private isStoreAnchorAvailable(): boolean {
    return !isInIframe()
      && !this.readModeAppState
      && !this.packageService.isDefaulter();
  }

  private isAssistantAnchorAvailable(): boolean {
    if (!this.isStoreAnchorAvailable()) return false;
    return this.packageService.hasPackage(PACKAGE_NAMES.creator)
      || this.packageService.hasPackage(PACKAGE_NAMES.publisher)
      || this.packageService.hasPackage(PACKAGE_NAMES.analyst)
      || this.packageService.hasPackage(PACKAGE_NAMES.developer);
  }

  /**
   * True when a legend anchor will exist in the DOM for bubble 3 to point at.
   * The tour library throws if it can't resolve an anchor, breaking the entire
   * sequence — so we must skip the step when no anchor is rendered. Smaplets
   * do not render the legend panel today; non-smaplets always render at least
   * the toggle button, so the anchor is available.
   */
  private hasLegendAnchor(): boolean {
    // Normal app always renders at least the legend toggle button so the
    // tour anchor is in the DOM. In smaplet, the button is gated on whether
    // the smap has a legend with included models — mirror that here so the
    // tour library doesn't get handed an anchor that isn't going to render.
    if (!this.isInIframe()) return true;
    const legend = this.legendService.getCurrentLegend();
    return !!legend?.legendModels?.some(lm => lm.model && lm.included);
  }

  /**
   * Position the tour bubble's arrow so its tip sits over the center of the
   * highlighted anchor. ngx-ui-tour-md-menu marks the active anchor element
   * with `touranchor--is-active`; we target that directly because the raw
   * `tourAnchor` attribute query is fragile across DOM case-normalisation.
   *
   * When the anchor is still zero-sized (the stepShow arrives before the
   * host element has been measured) we fall back to the attribute match, and
   * any remaining miss is covered by the second scheduled pass in stepShow$.
   */
  /**
   * The FTUE toolbar (`.toolbar-scroll`) scrolls horizontally when its icons
   * overflow on narrow / phone screens. ngx-ui-tour only scrolls anchors into
   * view vertically, so a toolbar button that's off to the side is never
   * revealed — the bubble and arrow then point at whatever happens to be on
   * screen. Centre the active anchor within its horizontal scroll container
   * before the arrow is measured. No-op on wide screens where the row fits.
   */
  private scrollAnchorIntoHorizontalView(anchorId: string): void {
    const anchorEl =
      (document.querySelector('.touranchor--is-active') ||
        document.querySelector(`[tourAnchor="${anchorId}"]`) ||
        document.querySelector(`[touranchor="${anchorId}"]`)) as HTMLElement | null;
    if (!anchorEl) return;
    const scroller = anchorEl.closest('.toolbar-scroll') as HTMLElement | null;
    if (!scroller || scroller.scrollWidth <= scroller.clientWidth) return;
    const anchorRect = anchorEl.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    const anchorCentre = anchorRect.left + anchorRect.width / 2;
    const scrollerCentre = scrollerRect.left + scrollerRect.width / 2;
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    const target = Math.max(
      0,
      Math.min(maxScroll, scroller.scrollLeft + (anchorCentre - scrollerCentre)),
    );
    if (Math.abs(target - scroller.scrollLeft) > 1) {
      scroller.scrollTo({left: target, behavior: 'auto'});
    }
  }

  private alignTourArrow(anchorId: string): void {
    const anchorEl =
      (document.querySelector('.touranchor--is-active') ||
        document.querySelector(`[tourAnchor="${anchorId}"]`) ||
        document.querySelector(`[touranchor="${anchorId}"]`)) as HTMLElement | null;
    // On prev/next, mat-menu's leave animation (~200ms) keeps the outgoing
    // panel in the DOM while the incoming one mounts. CDK appends each new
    // overlay pane at the end of `cdk-overlay-container`, so the incoming
    // bubble is always the last match — `querySelector` would return the
    // outgoing panel and align the wrong target, leaving the visible bubble
    // with its CSS vars unset (arrow falls back to 50%).
    const bubbles = document.querySelectorAll<HTMLElement>(
      '.mat-mdc-menu-panel.tour-step.arrow',
    );
    const bubble = bubbles.length ? bubbles[bubbles.length - 1] : null;
    if (!anchorEl || !bubble) return;
    // Reset any shift from a previous pass so the bubble's own rect reflects
    // the CDK-assigned position, not last pass's transform.
    bubble.style.transform = '';
    const anchorRect = anchorEl.getBoundingClientRect();
    const bubbleRect = bubble.getBoundingClientRect();
    if (bubbleRect.width === 0 || bubbleRect.height === 0) return;
    if (anchorRect.width === 0 && anchorRect.height === 0) return;
    const clamp = (value: number, min: number, max: number) =>
      Math.max(min, Math.min(max, value));
    // Rendered arrow is a 40px-wide triangle (20px border-width); adding the
    // 12px card radius gives a 32px safe pad, plus 8px breathing room for
    // the rounded corner.
    const SAFE_PAD = 40;
    // Material mat-menu aligns the panel's leading edge with the trigger's,
    // so a wide bubble sprawls off to one side of a small icon anchor. The
    // CDK overlay pane owns top/left and re-applies them on reflow, so we
    // shift the bubble itself via `transform: translate(...)` — purely visual,
    // won't be overwritten by CDK, and leaves the SCSS margin rules that
    // create the arrow's 10px gap intact. The arrow's `--tour-arrow-*` vars
    // are measured after the shift lands.
    const verticalBubble =
      bubbleRect.bottom <= anchorRect.top ||
      bubbleRect.top >= anchorRect.bottom;
    const anchorCentreX = anchorRect.left + anchorRect.width / 2;
    const anchorCentreY = anchorRect.top + anchorRect.height / 2;
    const VIEW_MARGIN = 8;
    let shiftX = 0;
    let shiftY = 0;
    if (verticalBubble) {
      const idealLeft = anchorCentreX - bubbleRect.width / 2;
      const maxLeft = window.innerWidth - bubbleRect.width - VIEW_MARGIN;
      const newLeft = clamp(idealLeft, VIEW_MARGIN, Math.max(VIEW_MARGIN, maxLeft));
      shiftX = newLeft - bubbleRect.left;
    } else {
      const idealTop = anchorCentreY - bubbleRect.height / 2;
      const maxTop = window.innerHeight - bubbleRect.height - VIEW_MARGIN;
      const newTop = clamp(idealTop, VIEW_MARGIN, Math.max(VIEW_MARGIN, maxTop));
      shiftY = newTop - bubbleRect.top;
    }
    if (Math.abs(shiftX) > 1 || Math.abs(shiftY) > 1) {
      bubble.style.transform = `translate(${shiftX}px, ${shiftY}px)`;
    }

    // Final arrow position is measured after any shift so the tip lands on
    // the anchor centre when possible, with the clamp keeping the arrow base
    // clear of the rounded corner.
    const finalRect = bubble.getBoundingClientRect();
    const finalX = clamp(
      anchorRect.left + anchorRect.width / 2 - finalRect.left,
      SAFE_PAD,
      Math.max(SAFE_PAD, finalRect.width - SAFE_PAD),
    );
    const finalOverlapCentreY = (
      Math.max(anchorRect.top, finalRect.top) +
      Math.min(anchorRect.bottom, finalRect.bottom)
    ) / 2;
    const finalY = clamp(
      (Number.isFinite(finalOverlapCentreY) && finalOverlapCentreY > 0
        ? finalOverlapCentreY
        : anchorRect.top + anchorRect.height / 2) - finalRect.top,
      SAFE_PAD,
      Math.max(SAFE_PAD, finalRect.height - SAFE_PAD),
    );
    bubble.style.setProperty('--tour-arrow-x', `${finalX}px`);
    bubble.style.setProperty('--tour-arrow-y', `${finalY}px`);
  }

  private getGridColor(map?: GraphMap): string {
    const resolvedMap = map || this.mapService.selectedMap();
    if (resolvedMap && resolvedMap.styles) {
      const settings = this.settingsService.getLocalStorageSettings();
      const gridIntensity = settings.gridIntensity || 0.3;
      return getAdaptiveGridColor(resolvedMap.styles.color, resolvedMap.styles.colorIntensity, gridIntensity);
    }
    return '#dedede';
  }

  nudgeCommentDraft(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.commentsStateManager.nudgeDraft();
  }

  isMapDarkMode(): boolean {
    return this.settingsService.isDarkMode(this.currentMap?.styles?.appearance);
  }

  private getScenesStoriesAnchorId(): string {
    let views = this.viewService.currentViews;
    let tours = this.toursService.currentTours;
    if (isInIframe()) {
      views = this.viewService.currentPublicViews;
      tours = this.toursService.currentPublicTours;
    }

    if (views && views.length > 0 && tours && tours.length > 0) {
      return 'scenes-stories-menu-anchor';
    }

    if (views && views.length > 0) {
      return 'views-menu-anchor';
    }
    if (tours && tours.length > 0) {
      return 'tours-menu-anchor';
    }
    return 'logo-anchor'; // Fallback if no views or tours are available
  }

  private isInIframe(): boolean {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }

  // Touch phones / small devices: the FTUE backdrop's spotlight cutout
  // mis-positions here, so we drop the backdrop and let the bubble arrow point
  // at the anchor instead. screen.* is the physical device (valid inside
  // smaplet iframes too), paired with a coarse-pointer check.
  private isMobileDevice(): boolean {
    try {
      const coarse = window.matchMedia('(pointer: coarse)').matches;
      const smallScreen = Math.min(window.screen.width, window.screen.height) <= 768;
      return coarse && smallScreen;
    } catch (e) {
      return false;
    }
  }

  ngOnDestroy() {
    // Release the editor page-shell lock (see ngOnInit / styles.scss).
    document.body.classList.remove('editor-active');

    // Clean up window reference if it points to this instance
    if ((window as any).__dashboardComponentRef === this) {
      delete (window as any).__dashboardComponentRef;
    }

    // Destroy Cytoscape instance
    if (this.cy) {
      try {
        this.cy.destroy();
      } catch (e) {
        // Cytoscape may already be destroyed
      }
      this.cy = undefined;
    }

    // Unsubscribe manual subscriptions
    this.copyPasteSub?.unsubscribe();
    this.menuSubscription?.unsubscribe();
    this.undoRedoSubscription?.unsubscribe();
    this.hasCommentsPollingSub?.unsubscribe();
    this.onboardingTriggerSub?.unsubscribe();
    this.tourStepShowSub?.unsubscribe();
    this.tourEndSub?.unsubscribe();
    this.tourArrowTimers.forEach(id => clearTimeout(id));
    this.tourArrowTimers = [];
  }

  async ngOnInit() {
    // Lock the page shell while the editor is mounted so canvas pinch/pan can't
    // scroll or zoom the whole page on touch devices (see styles.scss
    // `body.editor-active`). Removed in ngOnDestroy so other routes scroll.
    document.body.classList.add('editor-active');

    this.onboardingTriggerSub = this.onboardingService.trigger$.subscribe(() => {
      try {
        this.tourService.end();
      } catch { /* no active tour */
      }
      this.ftueInitilized = false;
      // Re-run from help menu — not the first-sign-in auto tour, so the
      // create-smap dialog should only open from the last bubble.
      this.firstSigninAutoTour = false;
      this.initializeAndStartFTUETour();
    });

    // Refresh user from server (non-blocking) and check delinquent status after refresh completes
    if (!isInIframe()) {
      this.userService.refreshLocalUser().subscribe(freshUser => {
        const sub = freshUser?.subscription;
        const isExpired = sub?.expirationDate && new Date(sub.expirationDate) <= new Date();
        const hasNoSub = !sub?.stripeSubscriptionId && !sub?.isTrial;

        let reason: 'expired' | 'payment_failed' | null = null;
        if (freshUser?.status === 'limited' && (isExpired || hasNoSub)) {
          reason = 'payment_failed';
        } else if (freshUser?.status !== 'limited' && isExpired && !sub?.isTrial) {
          reason = 'expired';
        }

        if (reason) {
          import('../dialogs/user-account-dialog.component').then(({UserAccountDialogComponent}) => {
            this.matDialog.open(UserAccountDialogComponent, {
              width: '50%',
              autoFocus: false,
            });
          });
        }
      });
    }

    // Initialize map and user data
    const { hasNoMap, currentMap } = await this.dashboardService.initializeMapAndUser(this.route);
    this.hasNoMap = hasNoMap;
    this.currentMap = currentMap;

    // Early exit if no map - prevents all downstream initialization
    if (this.hasNoMap) {
      this.isLoading = false;
      return;
    }

    // Setup edit mode configuration
    this.dashboardService.setupEditModeConfiguration(this.currentMap);

    // Initialize Cytoscape
    const cy = this.cyService.initializeCytoscape({
      //@ts-ignore
      cytoscapeContainer: this.cytoscapeContainer,
      currentMap: this.currentMap,
      isReadOnly: this.isReadOnly,
      isChangingLinkSource: this.isChangingLinkSource,
      //@ts-ignore
      settings: this.settings,
      cyService: this.cyService,
      componentInstance: this,
      edgeEditingInstance: this.edgeEditingInstance
    });
    this.dashboardService.initializeCytoscapeExtensions(cy);

    //@ts-ignore
    const eh = this.initDrawMode(cy);
    this.eh = eh;
    this.dashboardService.initCtxMenu2(cy, eh, {
      hasNoMap: this.hasNoMap,
      currentMap: this.currentMap,
      hasMapPermission: this.hasMapPermission.bind(this),
      selectedNodes: this.selectedNodes,
      selectedLinks: this.selectedLinks,
      centerCanvas: this.centerCanvas.bind(this),
      centerOnTarget: this.centerOnTarget.bind(this),
      updateNode: this.updateNode.bind(this),
      resetAllSelected: this.resetAllSelected.bind(this),
      addNodeToParent: this.addNodeToParent.bind(this),
      moveNodeOneLevelUp: this.moveNodeOneLevelUp.bind(this),
      removeNodeFromParent: this.removeNodeFromParent.bind(this),
      addCyLink: this.addCyLink.bind(this),
      initAnchors: this.initAnchors.bind(this),
      createFromMenu: this.createFromMenu,
      cy: this.cy!,
      isUnlinking: this.isUnlinking,
      isChangingLinkSource: this.isChangingLinkSource,
      hasNodesNoNodes: this.hasNodesNoNodes,
      contextMenuInstance: this.contextMenuInstance,
      menuSubscription: this.menuSubscription,
      undoRedoSubscription: this.undoRedoSubscription,
      edgeEditingInstance: this.edgeEditingInstance,
      undoRedoManager: this.undoRedoManager,
      settingsService: this.settingsService,
      appWideStateService: this.appWideStateService,
      DashboardComponent: DashboardComponent,
      componentInstance: this
    }).then();
    this.onTap(cy);
    this.dashboardService.setupNodeLinking(cy, {
      hasMapPermission: this.hasMapPermission.bind(this),
      componentInstance: this,
      nodeService: this.nodeService,
      cyService: this.cyService,
      mapStateService: this.mapStateService,
      linkEndpointSignal: this.linkEndpointSignal.bind(this),
      addCyLink: this.addCyLink.bind(this),
      selectedLinks: this.selectedLinks,
      isSideBarOpen: this.isSideBarOpen,
      DashboardComponent: DashboardComponent,
      loadAllForMap: this.loadAllForMap.bind(this),
      edgeEditingInstance: this.edgeEditingInstance,
      eh: this.eh
    });
    this.handleNodePositionChange(cy);
    this.handleElementSelection(cy);
    this.handleElementTap(cy);
    this.cy = cy;
    this.cyService.init(cy);

    // Store reference for toolbar/button-press service access
    (window as any).__dashboardComponentRef = this;

    // Initialize overlay element manager (feature-flag gated internally)
    this.overlayManager.initialize(cy);
    this.setupOverlayElementEvents();

    // Subscribe to undo/redo events to refresh sidebar
    this.undoRedoManager.onCommandExecuted().subscribe(({action}) => {
      if (action === 'undo' || action === 'redo') {
        this.refreshTappedElements();
      }
    });

    this.listenToGptGeneration(cy);
    this.handleEdgeEditingEnd(cy);

    // Setup double tap handlers
    this.dashboardService.setupDoubleTapHandlers(cy, {
      selectedNodes: this.selectedNodes,
      updateNode: this.updateNode.bind(this),
      hasMapPermission: this.hasMapPermission.bind(this)
    });

    setTimeout(() => {
      if (this.shouldShowFTUETour()) {
        this.initializeAndStartFTUETour();
      }
    }, 3000);
    //Listen for new node creation events
    this.listenToNodeCreate(cy);
    this.listenToMapStateChanges();
    this.listenToLayoutChanges(cy);
    this.handleCanvasUpdateForLabelMarginOnDrag(cy);

    this.nodeService.addGptToSubject.subscribe(() => {
      this.saveGptNodes();
    });

    this.settingsService.getGeneralInfo().subscribe((info) => {
      this.buildNumber = info.buildNumber;
      this.settings = info;
      setGridPlugin(cy as any, {
        drawGrid: isInIframe() ? false : Boolean(info.showGrid),
        geometricGuideline: isInIframe() ? false : info.showAlignLines,
        snapToGridDuringDrag: isInIframe() ? false : info.snapToGrid,
        snapToAlignmentLocationDuringDrag: isInIframe() ? false : info.snapToAlignLines,
        gridSpacing: info.gridSize,
        panGrid: true,
        gridColor: this.getGridColor(),
        guidelinesStyle: {
          ...CyGridPluginOptions.guidelinesStyle,
          strokeStyle: this.getGridColor()
        }
      });
    });

    this.settingsService.generalInfoChanged.subscribe((info) => {
      // MAP-1017: cytoscape resolves the opacity style FUNCTIONS (dimmed links,
      // excluded-from-state elements) once and caches the result, so a new
      // filtering opacity only reaches the canvas through the same explicit
      // restyle + forceRender other global style changes use (MAP-1113).
      const filteringOpacityChanged =
        info.filteringOpacity !== undefined && info.filteringOpacity !== this.settings?.filteringOpacity;
      this.settings = info;
      if (filteringOpacityChanged) {
        this.cyService.updateStylesForAppearanceChange();
      }
      setGridPlugin(cy as any, {
        drawGrid: isInIframe() ? false : Boolean(info.showGrid),
        geometricGuideline: isInIframe() ? false : info.showAlignLines,
        snapToGridDuringDrag: isInIframe() ? false : info.snapToGrid,
        snapToAlignmentLocationDuringDrag: isInIframe() ? false : info.snapToAlignLines,
        gridSpacing: info.gridSize,
        gridColor: this.getGridColor(),
        guidelinesStyle: {
          ...CyGridPluginOptions.guidelinesStyle,
          strokeStyle: this.getGridColor()
        }
      });
    });


    this.handleLocalSettings();
    this.settingsService.localStorageSettingsChanged
      .subscribe(() => {
        this.handleLocalSettings();
        // Update grid color when localStorage settings change
        if (this.cy && this.settings) {
          setGridPlugin(this.cy as any, {
            drawGrid: isInIframe() ? false : Boolean(this.settings.showGrid),
            geometricGuideline: isInIframe() ? false : this.settings.showAlignLines,
            snapToGridDuringDrag: isInIframe() ? false : this.settings.snapToGrid,
            snapToAlignmentLocationDuringDrag: isInIframe() ? false : this.settings.snapToAlignLines,
            gridSpacing: this.settings.gridSize,
            panGrid: true,
            gridColor: this.getGridColor(),
            guidelinesStyle: {
              ...CyGridPluginOptions.guidelinesStyle,
              strokeStyle: this.getGridColor()
            }
          });
        }
      });

    //@ts-ignore
    cy.expandCollapse({
      animate: false, // whether to animate on drawing changes you can specify a function too
      expandCollapseCueSize: 0,
      expandCollapseCueLineSize: 0,
      fisheye: false,
      layoutBy: {
        name: 'preset',
        animate: false,
        randomize: false,
        fit: false
      }
    });

    this.buttonPressService.init(cy).subscribe();

    this.buttonPressService.initUndoRedo();

    cy.on('mouseover', 'edge', (event) => {
      const edge = event.target;
      const labelKey = edge.data('label');
      const label = edge.data(labelKey);
      if (!label || label === '') return;

      // Clear any previous timeout to avoid unexpected behavior
      if (this.tooltipTimeout) {
        clearTimeout(this.tooltipTimeout);
      }

      this.tooltipTimeout = setTimeout(() => {
        const mousePos = event.originalEvent; // Get the mouse position
        const containerPos = this.cytoscapeContainer?.nativeElement.getBoundingClientRect(); // Get the container position

        // Create and position the tooltip
        const tooltip = document.createElement('div');
        const data = edge.data();

        // Get source and target node names
        const sourceNode = edge.source();
        const targetNode = edge.target();
        const sourceName = sourceNode?.data('name') || 'Unknown';
        const targetName = targetNode?.data('name') || 'Unknown';

        // Get link name based on the label setting
        let linkName = '';
        if (data?.label === 'name') {
          linkName = data.name || '';
        } else {
          linkName = data.metadata?.find((meta: {
            key: string;
            value: string
          }) => meta.key === data.label)?.value || '';
        }

        // Create tooltip content with proper directionality indicators
        let tooltipContent = '';
        if (linkName) {
          if (data.direction === 'bidirected') {
            tooltipContent = `${sourceName} ← ${linkName} → ${targetName}`;
          } else if (data.direction === 'undirected') {
            tooltipContent = `${sourceName} — ${linkName} — ${targetName}`;
          } else {
            // directed (default)
            tooltipContent = `${sourceName} — ${linkName} → ${targetName}`;
          }
        } else {
          if (data.direction === 'bidirected') {
            tooltipContent = `${sourceName} ← → ${targetName}`;
          } else if (data.direction === 'undirected') {
            tooltipContent = `${sourceName} — ${targetName}`;
          } else {
            // directed (default)
            tooltipContent = `${sourceName} → ${targetName}`;
          }
        }
        tooltip.textContent = tooltipContent;

        tooltip.className = 'edge-tooltip';
        tooltip.style.left = `${mousePos.clientX - containerPos.left}px`; // Adjust for container position
        tooltip.style.top = `${mousePos.clientY - containerPos.top}px`; // Adjust for container position

        // Add the tooltip to the container
        // @ts-ignore
        this.cytoscapeContainer?.nativeElement.appendChild(tooltip);
      }, 1000);  // wait for 1 second
    });

    cy.on('mouseout', 'edge', () => {
      // Clear the timeout when mouseout to avoid displaying the tooltip if the mouse has already left
      if (this.tooltipTimeout) {
        clearTimeout(this.tooltipTimeout);
      }

      // @ts-ignore
      const tooltip = this.cytoscapeContainer?.nativeElement.querySelector('.edge-tooltip');

      if (tooltip) {
        // Remove the tooltip from the container
        // @ts-ignore
        this.cytoscapeContainer?.nativeElement.removeChild(tooltip);
      }
    });
    this.registerTooltipClearHandler(cy);
    this.applyLayout(cy, LayoutTypes.PRESET);
    if (!this.readModeAppState && !isEmpty(this.currentMap)) {
      const mapOwner = this.currentMap?.owner;
      const currentUser = this.userService.getCurrentUserFromStorage();
      if (mapOwner && currentUser && mapOwner.id !== currentUser.id) {
        this.updateElementsCommentsFlag();
      }
    }
    if (!this.copyPasteSub || this.copyPasteSub.closed) {
      this.copyPasteSub = new Subscription();
      this.copyPasteSub.add(
        this.buttonPressService.getNodeCreation()
          .subscribe((res) => {
            if (res) {
              this.loadCyElements(cy, false, true, res);
            }
          })
      );
    }

    //@ts-ignore
    this.edgeEditingInstance = cy.edgeEditing({
      undoable: false,
      bendRemovalSensitivity: 0,
      enableMultipleAnchorRemovalOption: false,
      initAnchorsAutomatically: false,
      ignoredClasses: ['ignore_control_points'],
      useTrailingDividersAfterContextMenuOptions: false,
      enableCreateAnchorOnDrag: false,
      addBendMenuItemTitle: false,
      removeBendMenuItemTitle: false,
      removeAllBendMenuItemTitle: false,
      addControlMenuItemTitle: false,
      removeControlMenuItemTitle: false,
      removeAllControlMenuItemTitle: false,
      handleReconnectEdge: false,
      bendCornersIsRoundFunction(e: any) {
        return e.data('round');
      },
      bendPositionsFunction: (ele: any) => {
        const currentState = this.mapStateService.getCurrentLocalState();
        const statedBendPoints = ele.data('states')?.find((s: any) => s.stateId === currentState?.id)?.link.bendPointPositions;
        return statedBendPoints || ele.data('bendPointPositions');
      },
      controlPositionsFunction: (ele: any) => {
        const currentState = this.mapStateService.getCurrentLocalState();
        const statedControlPoints = ele.data('states')?.find((s: any) => s.stateId === currentState?.id)?.link.controlPointPositions;
        return statedControlPoints || ele.data('controlPointPositions');
      },
      // A function parameter to set bend point positions
      bendPointPositionsSetterFunction: function(ele: any, bendPointPositions: any) {
        ele.data('bendPointPositions', bendPointPositions);
      },
      // A function parameter to set bend point positions
      controlPointPositionsSetterFunction: function(ele: any, controlPointPositions: any) {
        ele.data('controlPointPositions', controlPointPositions);
      },
      anchorColor: this.edgeEditingHandleColor(),
      endPointColor: this.edgeEditingHandleColor()
    });

    this.cyService.refreshEdgeEditingColors();

    // Sometimes styles need a redraw in init
    setTimeout(() => {
      cy.style().update();
      this.cyService.refreshEdgeEditingColors();
    }, 2500);
  }

  private edgeEditingHandleColor(): string {
    const map = this.currentMap ?? this.mapService.getSelectedMapSignal();
    return this.settingsService.isDarkMode(map?.styles?.appearance) ? '#ffffff' : '#000000';
  }

  private registerTooltipClearHandler(cy: cytoscape.Core) {
    cy.on('cxttap', () => {
      if (this.tooltipTimeout) {
        clearTimeout(this.tooltipTimeout);
      }

      // @ts-ignore
      const tooltip = this.cytoscapeContainer?.nativeElement.querySelector('.edge-tooltip');

      if (tooltip) {
        // Remove the tooltip from the container
        // @ts-ignore
        this.cytoscapeContainer?.nativeElement.removeChild(tooltip);
      }
    });
  }

  validateResize() {
    return (e: ResizeEvent) => {
      // @ts-ignore
      return e.rectangle.width > this.drawerMinWidth && e.rectangle.width < this.drawerMaxWidth;
    };
  }


  async callUpdateMultiple(event: MultiUpdateEvent) {
    if (!this.cy) return;
    if (isEmpty(event.node) && isEmpty(event.link) && isEmpty(event.general) && !event.metadata) return;
    const metadataFanOut = event.metadata;

    // Block multi-update during model refresh to prevent race conditions
    if (updateInProgress()) {
      return;
    }
    const allSelected = this.cy.$(':selected');
    const nodeProperties = { ...event.general?.properties, ...event.node?.properties };
    const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

    // Get selected node and link IDs first
    const selectedNodeIds = allSelected.filter((e: any) => e.isNode()).map((e: any) => e.id());
    const selectedLinkIds = allSelected.filter((e: any) => e.isEdge()).map((e: any) => e.id());

    const nodeDiff: any = {
      ...event.general,
      ...event.node,
      properties: nodeProperties
    };
    const linkDiff: any = {...event.general, ...event.link};

    const multiUpdatePayload = {
      nodeIds: selectedNodeIds,
      linkIds: selectedLinkIds,
      stateId: this.mapStateService.getCurrentLocalState()?.id as string,
      diff: {
        node: nodeDiff,
        link: linkDiff
      },
      ...(metadataFanOut ? {metadata: metadataFanOut} : {}),
    };
    const linkDiffType = linkDiff?.type;

    // Check if this is a metadata-only change (skip style snapshots if so)
    const isMetadataOnlyChange = event.metadata !== undefined &&
      isEmpty(event.node) && isEmpty(event.link) &&
      (isEmpty(event.general) || Object.keys(event.general || {}).every(k => k === 'properties' && isEmpty((event.general as any).properties)));

    // Capture before state for undo/redo
    const styleSnapshots: StyleChangeSnapshot[] = [];
    const nodeDiffKeys = isMetadataOnlyChange ? [] : Object.keys({...event.general, ...event.node});
    const linkDiffKeys = isMetadataOnlyChange ? [] : Object.keys({...event.general, ...event.link});

    // Exclude 'name', 'description' (text changes), 'metadata', 'properties' (tracked separately)
    // Also exclude 'states' - complex nested structure that causes false positive changes
    const excludedKeys = ['name', 'description', 'metadata', 'properties', 'states'];

    // Helper to check if values are equal (with numeric coercion)
    const areValuesEqual = (before: any, after: any): boolean => {
      if (before === after) return true;
      if (before == null && after == null) return true;
      if (before == null || after == null) return false;
      // Numeric coercion
      const numBefore = typeof before === 'string' ? parseFloat(before) : before;
      const numAfter = typeof after === 'string' ? parseFloat(after) : after;
      if (typeof numBefore === 'number' && typeof numAfter === 'number' &&
        !isNaN(numBefore) && !isNaN(numAfter)) {
        return numBefore === numAfter;
      }
      return JSON.stringify(before) === JSON.stringify(after);
    };

    // Helper to get state-aware value for excludedFromState
    // When viewing a MapState, excludedFromState is read from nested states[].link or states[].node
    const currentMapState = this.mapStateService.getCurrentLocalState();
    const getStateAwareValue = (elementData: any, key: string, elementType: 'node' | 'link'): any => {
      if (key === 'excludedFromState' && currentMapState?.id) {
        const states = elementData.states;
        if (Array.isArray(states)) {
          const stateEntry = states.find((s: any) => s.stateId === currentMapState.id);
          const stateData = elementType === 'link' ? stateEntry?.link : stateEntry?.node;
          if (stateData && 'excludedFromState' in stateData) {
            return stateData.excludedFromState;
          }
        }
      }
      return elementData[key];
    };

    // Capture node states
    for (const nodeId of multiUpdatePayload.nodeIds) {
      const cyNode = this.cy.$(`#${nodeId}`);
      if (cyNode.length) {
        const beforeStyles: Record<string, unknown> = {};
        const afterStyles: Record<string, unknown> = {};
        const nodeData = cyNode.data();

        for (const key of nodeDiffKeys) {
          // Handle properties specially - capture flattened keys BEFORE checking excludedKeys
          if (key === 'properties') {
            const propKeys = Object.keys(nodeProperties);
            for (const propKey of propKeys) {
              const beforeVal = (nodeData.properties as Record<string, unknown>)?.[propKey];
              const afterVal = (nodeProperties as Record<string, unknown>)[propKey];
              // Only capture if actually changed
              if (!areValuesEqual(beforeVal, afterVal)) {
                beforeStyles[`properties.${propKey}`] = beforeVal;
                afterStyles[`properties.${propKey}`] = afterVal;
              }
            }
            continue;
          }

          if (excludedKeys.includes(key)) continue;

          const beforeVal = getStateAwareValue(nodeData, key, 'node');
          const afterVal = (event.general as any)?.[key] ?? (event.node as any)?.[key];
          // Only capture if actually changed
          if (!areValuesEqual(beforeVal, afterVal)) {
            beforeStyles[key] = beforeVal;
            afterStyles[key] = afterVal;
          }
        }

        if (Object.keys(beforeStyles).length > 0) {
          styleSnapshots.push({
            elementId: nodeId,
            elementType: 'node',
            beforeStyles,
            afterStyles
          });
        }
      }
    }

    // Capture link states
    for (const linkId of multiUpdatePayload.linkIds) {
      const cyLink = this.cy.$(`#${linkId}`);
      if (cyLink.length) {
        const beforeStyles: Record<string, unknown> = {};
        const afterStyles: Record<string, unknown> = {};
        const linkData = cyLink.data();

        for (const key of linkDiffKeys) {
          if (excludedKeys.includes(key)) continue;
          const beforeVal = getStateAwareValue(linkData, key, 'link');
          const afterVal = (event.general as any)?.[key] ?? (event.link as any)?.[key];
          // Only capture if actually changed
          if (!areValuesEqual(beforeVal, afterVal)) {
            beforeStyles[key] = beforeVal;
            afterStyles[key] = afterVal;
          }
        }

        if (Object.keys(beforeStyles).length > 0) {
          styleSnapshots.push({
            elementId: linkId,
            elementType: 'link',
            beforeStyles,
            afterStyles
          });
        }
      }
    }

    // Capture text changes (name/description) for multi-selection undo
    // Check for actual defined values, not just key presence
    const nameValue = (event.general as any)?.name ?? (event.node as any)?.name ?? (event.link as any)?.name;
    const descriptionValue = (event.general as any)?.description ?? (event.node as any)?.description ?? (event.link as any)?.description;
    const hasNameChange = nameValue !== undefined;
    const hasDescriptionChange = descriptionValue !== undefined;

    let textSnapshots: MultiTextSnapshot[] = [];
    if (hasNameChange || hasDescriptionChange) {
      const field: 'name' | 'description' = hasNameChange ? 'name' : 'description';
      const newValue = hasNameChange ? nameValue : descriptionValue;

      // Capture node text changes
      for (const nodeId of multiUpdatePayload.nodeIds) {
        const cyNode = this.cy.$(`#${nodeId}`);
        if (cyNode.length) {
          textSnapshots.push({
            entityId: nodeId,
            entityType: 'node',
            field,
            before: cyNode.data(field) || '',
            after: newValue || ''
          });
        }
      }

      // Capture link text changes
      for (const linkId of multiUpdatePayload.linkIds) {
        const cyLink = this.cy.$(`#${linkId}`);
        if (cyLink.length) {
          textSnapshots.push({
            entityId: linkId,
            entityType: 'link',
            field,
            before: cyLink.data(field) || '',
            after: newValue || ''
          });
        }
      }
    }

    // Capture metadata changes for multi-selection undo
    // Check for actual defined values, not just key presence
    const metadataValue = event.metadata;
    const hasMetadataChange = metadataValue !== undefined;

    let propertySnapshots: MultiPropertySnapshot[] = [];
    if (hasMetadataChange) {
      // Helper to detect action type and changed field
      // Compares by position/order since IDs may not match between form and cytoscape
      const detectPropertyChange = (before: any[], after: any[]): {
        action: 'add' | 'remove' | 'update',
        changedField?: 'key' | 'value' | 'both' | 'row'
      } => {
        // Check if length changed - this is the definitive add/remove indicator
        if (after.length > before.length) {
          return {action: 'add', changedField: 'row'};
        } else if (after.length < before.length) {
          return {action: 'remove', changedField: 'row'};
        }

        // Same length - compare by position to detect key/value changes
        // Sort by order to ensure consistent comparison
        const sortedBefore = [...before].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        const sortedAfter = [...after].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

        for (let i = 0; i < sortedBefore.length; i++) {
          const beforeItem = sortedBefore[i] as any;
          const afterItem = sortedAfter[i] as any;

          // Compare only key and value (ignore id differences)
          const keyChanged = (beforeItem?.key ?? '') !== (afterItem?.key ?? '');
          const valueChanged = (beforeItem?.value ?? '') !== (afterItem?.value ?? '');

          if (keyChanged && valueChanged) {
            return {action: 'update', changedField: 'both'};
          } else if (keyChanged) {
            return {action: 'update', changedField: 'key'};
          } else if (valueChanged) {
            return {action: 'update', changedField: 'value'};
          }
        }
        return {action: 'update'};
      };

      // Deep copy helper for metadata
      const deepCopyMetadata = (metadata: any[]) => {
        if (!metadata || !Array.isArray(metadata)) return [];
        return metadata.map(m => ({...m}));
      };

      // Capture node metadata changes
      for (const nodeId of multiUpdatePayload.nodeIds) {
        const cyNode = this.cy.$(`#${nodeId}`);
        if (cyNode.length) {
          const beforeMetadata = cyNode.data('metadata') || [];
          const afterMetadata = metadataAfterFanOut(metadataValue!, nodeId, beforeMetadata);
          const {action, changedField} = detectPropertyChange(beforeMetadata, afterMetadata);
          // Only push snapshot if there's an actual change (changedField is defined)
          // When changedField is undefined with action 'update', it means no real change
          if (changedField) {
            propertySnapshots.push({
              entityId: nodeId,
              entityType: 'node',
              action,
              changedField,
              beforeMetadata: deepCopyMetadata(beforeMetadata),
              afterMetadata: deepCopyMetadata(afterMetadata)
            });
          }
        }
      }

      // Capture link metadata changes
      for (const linkId of multiUpdatePayload.linkIds) {
        const cyLink = this.cy.$(`#${linkId}`);
        if (cyLink.length) {
          const beforeMetadata = cyLink.data('metadata') || [];
          const afterMetadata = metadataAfterFanOut(metadataValue!, linkId, beforeMetadata);
          const {action, changedField} = detectPropertyChange(beforeMetadata, afterMetadata);
          // Only push snapshot if there's an actual change (changedField is defined)
          // When changedField is undefined with action 'update', it means no real change
          if (changedField) {
            propertySnapshots.push({
              entityId: linkId,
              entityType: 'link',
              action,
              changedField,
              beforeMetadata: deepCopyMetadata(beforeMetadata),
              afterMetadata: deepCopyMetadata(afterMetadata)
            });
          }
        }
      }
    }

    // IMPORTANT: Apply optimistic update BEFORE pushing commands
    // This ensures that when pushCommand triggers undoRedoStateChanged$,
    // the form refresh will read the NEW values from cytoscape
    this.applyOptimisticMultiUpdate(multiUpdatePayload);

    // Now push commands (form will refresh with correct new values)
    if (textSnapshots.length > 0) {
      const textCommand = new MultiTextChangeCommand(
        this.cyService,
        this.nodeService,
        textSnapshots
      );
      this.undoRedoManager.pushCommand(textCommand);
    }

    if (propertySnapshots.length > 0) {
      const propertyCommand = new MultiPropertyCommand(
        this.cyService,
        this.nodeService,
        propertySnapshots
      );
      this.undoRedoManager.pushCommand(propertyCommand);
      // Track IDs involved in this multi-selection metadata change
      const involvedIds = new Set(propertySnapshots.map(s => s.entityId));
      lastMultiSelectionMetadataIds.set(involvedIds);
      // Signal that multi-selection metadata change occurred - prevents duplicate singular commands on deselect
      multiSelectionMetadataChanged.set(true);
      // Reset after delay longer than form debounce (500ms) to allow deselect cleanup to complete
      setTimeout(() => {
        multiSelectionMetadataChanged.set(false);
        lastMultiSelectionMetadataIds.set(new Set());
      }, 800);
    }

    // Create undo/redo command if there are style changes
    // Skip if we already pushed property commands (metadata changes) to avoid false positive style changes
    // from multi-selection where nodes have different style values
    if (styleSnapshots.length > 0 && propertySnapshots.length === 0) {
      const description = `Update ${multiUpdatePayload.nodeIds.length + multiUpdatePayload.linkIds.length} element(s)`;
      const updateStyleCommand = new UpdateStyleCommand(
        this.cyService,
        this.nodeService,
        styleSnapshots,
        description,
        this.mapStateService
      );
      this.undoRedoManager.pushCommand(updateStyleCommand);
    }

    //@ts-ignore
    const updateStream = this.nodeService.multiUpdate(multiUpdatePayload);
    const streamSubscribe = (res: any) => {
      // Merge backend response with current cytoscape data to preserve optimistic updates
      // This prevents stale backend responses from overwriting recently-changed properties
      const mergedNodes = res.nodes?.map((node: any) => {
        const cyNode = this.cy?.$('#' + node.id);
        const currentProperties = cyNode?.data('properties') || {};
        return {
          ...node,
          properties: {...node.properties, ...currentProperties}
        };
      }) || [];

      // For links, merge current cytoscape data to preserve optimistic updates
      // Links don't have nested properties, so we merge top-level link fields
      const mergedLinks = res.links?.map((link: any) => {
        const cyLink = this.cy?.$('#' + link.id);
        const currentData = cyLink?.data() || {};
        // Preserve current values for fields that might have been optimistically updated
        return {
          ...link,
          textFontSize: currentData.textFontSize ?? link.textFontSize,
          linkLabelPosition: currentData.linkLabelPosition ?? link.linkLabelPosition,
          linkLabelOffset: currentData.linkLabelOffset ?? link.linkLabelOffset,
        };
      }) || [];

      this.selectedNodes = [];
      this.selectedNodes = mergedNodes;
      this.selectedLinks = [];
      this.selectedLinks = mergedLinks;

      if (mergedNodes.length > 0) {
        this.refreshNodes(mergedNodes);
      }

      if (mergedLinks.length > 0) {
        this.refreshLinks(mergedLinks);
      }
      Logger.log('Multi update sending update in progress signal - false');
      updateInProgress.set(false);
      // Trigger thumbnail generation for multi-update visual changes
      this.thumbnailService.markVisualChangeForCurrentMap();
    };

    if (linkDiffType && linkDiffType !== 'BEZIER') {
      // Track links that need individual anchor point updates
      const linksToUpdateAnchors: string[] = [];

      for (const linkId of multiUpdatePayload.linkIds) {
        const cyLink = this.cy.$('#' + linkId);
        cyLink.data('type', linkDiffType);
        cyLink.removeClass('ignore_control_points');
        this.resetAllAnchorPointsDistancesAndWeights(cyLink);
        await delay(300);
        const firstEdgeEditing = !this.hasAnyAnchorPoints(cyLink.data());
        if (firstEdgeEditing) {
          if (linkDiffType === 'STRAIGHT') {
            callAddAnchor(cyLink, 'bend');
          } else {
            callAddAnchor(cyLink, 'control');
          }
          await delay(300);
          // Track this link for individual anchor point update instead of mutating shared diff
          linksToUpdateAnchors.push(linkId);
        }
      }

      // Subscribe to main update stream
      updateStream.subscribe((res: any) => {
        streamSubscribe(res);

        // After batch update completes, save each link's individual anchor points
        // This ensures each link keeps its own unique anchor point positions
        if (linksToUpdateAnchors.length > 0 && !isInIframe()) {
          setTimeout(() => {
            linksToUpdateAnchors.forEach((linkId) => {
              const cyLink = this.cy?.$('#' + linkId);
              if (cyLink && cyLink.length) {
                const linkData = this.updateLinksPointPositionsData(cyLink.data());
                this.updateLink(linkData as GraphNodeLink, false).subscribe();
              }
            });
          }, 200);
        }
      });
    } else {
      if (linkDiffType === 'BEZIER') {
        for (const linkId of multiUpdatePayload.linkIds) {
          const cyLink = this.cy.$('#' + linkId);
          cyLink.addClass('ignore_control_points');
          this.resetAllAnchorPointsDistancesAndWeights(cyLink);
        }
      }
      await delay(300);
      updateStream.subscribe(streamSubscribe);
    }
  }

  private resetAllAnchorPointsDistancesAndWeights(cyLink: EdgeSingular) {
    cyLink.removeClass('edgecontrolediting-hascontrolpoints');
    cyLink.removeClass('edgebendediting-hasbendpoints');
    cyLink.removeClass('edgebendediting-hasmultiplebendpoints');
    cyLink.data('cyedgebendeditingDistances', []);
    cyLink.data('cyedgebendeditingWeights', []);
    cyLink.data('cyedgecontroleditingDistances', []);
    cyLink.data('cyedgecontroleditingWeights', []);
    cyLink.data('bendPointPositions', []);
    cyLink.data('controlPointPositions', []);
  }

  onResizeEnd(event: ResizeEvent): void {
    this.drawerWidth = event.rectangle.width as number;
    this.settingsService.storeSidebarWidth(this.drawerWidth);
    document.documentElement.style.setProperty('--sidebar-width', `${this.appliedSidebarWidth}px`);
  }

  selectMap() {
    this.mapService.openMapSheetSubject.next();
  }

  createNodeFromNoNodeMessage() {
    if (!this.cy) return;
    const bb = this.cy.extent();
    const centerX = (bb.x1 + bb.x2) / 2;
    const centerY = (bb.y1 + bb.y2) / 2;
    this.cyService.createDefaultNode({ x: centerX, y: centerY });
  }

  private listenToGptGeneration(cy: cytoscape.Core) {
    this.gptService.generatedSubject.subscribe((gpt) => {
      if (!cy?.elements()) {
        return;
      }
      cy.elements()?.remove();
      this.isReadOnly = true;
      this.buildGptElements(gpt, cy);
      this.applyLayout(cy, LayoutTypes.COSE_BILKENT);
    });
  }

  resetState($event: { node?: Partial<GraphNode>, link?: Partial<GraphNodeLink> }) {
    const currentState = this.mapStateService.getCurrentLocalState();
    const cy = this.cy!;
    if (currentState?.id === undefined) return;
    const entityType = $event.node ? 'node' : 'link';
    const entityId = $event.node?.id || $event.link?.id;

    if (!entityId) {
      return;
    }

    updateInProgress.set(true);
    DashboardComponent.tappedLink = null;
    DashboardComponent.tappedNode = null;
    cy.$(':selected').unselect();

    this.nodeService.resetState(entityType, entityId, currentState.id)
      .pipe(
        finalize(() => {
          setTimeout(() => {
            updateInProgress.set(false);
          }, 1500);
        })
      )
      .subscribe((res: GraphNode | GraphNodeLink) => {
        if (entityType === 'node') {
          this.refreshNodeRendering(res as GraphNode);
          DashboardComponent.tappedNode = res as GraphNode;
        } else {
          this.refreshLinkRendering(res as GraphNodeLink, true);
        }
        this.tapElement(res, entityType === 'node', this.cy!);
      });
  }

  public updateLink(link: GraphNodeLink, doUpdate = true) {
    let meta: MetaData[] = [];
    if (link?.metadata) {
      meta = link.metadata.map((m: MetaData) => {
        if (m.id) {
          return {
            id: m.id,
            key: m.key,
            value: m.value,
            order: m.order
          };
        } else {
          return {
            key: m.key,
            value: m.value
          } as MetaData;
        }
      });
    }
    const payload = {
      ...link,
      type: (link.type || 'BEZIER') as string,
      shape: (link.shape || 'solid') as string,
      metadata: meta
    } as GraphNodeLink;
    return this.nodeService.updateLink(omit(payload, 'originalEnds')).pipe(
      tap((response) => {
        const res = response;
        if (res.metadata && res.id === DashboardComponent.tappedLink?.id) {
          //@ts-ignore
          DashboardComponent.tappedLink.metadata = res.metadata;
        }
        if (doUpdate) {
          // @ts-ignore
          this.refreshLinkRendering(res);
        }
      })
    );
  }

  updateNode($event: Partial<GraphNode>, collapseExpand = false, ignoreUiBlock = false) {

    const payload = {
      ...$event
    };
    if (isEmpty(payload.id)) {
      return EMPTY;
    }
    // @ts-ignore
    delete payload['collapsedChildren'];
    payload.children?.forEach((c) => {
      // @ts-ignore
      delete c['collapsedChildren'];
    });
    if (collapseExpand) {
      delete payload['children'];
    }

    // Patch state entry's excludedFromState for immediate optimistic rendering
    // Must be done before refreshNodeRendering since the form emits old states
    const currentStateId = this.mapStateService.getCurrentLocalState()?.id;
    if (currentStateId !== undefined && payload.excludedFromState !== undefined && Array.isArray(payload.states)) {
      const stateEntry = (payload.states as any[]).find((s: any) => s.stateId === currentStateId);
      if (stateEntry?.node) {
        stateEntry.node.excludedFromState = payload.excludedFromState;
      }
    }

    this.refreshNodeRendering(payload as GraphNode);

    // @ts-ignore
    const isCurrentlyEditing = payload.id === DashboardComponent.tappedNode?.id;
    return this.nodeService.updateNode(payload as GraphNode, ignoreUiBlock)
      .pipe(
        switchMap((res) => {
          // Skip re-fetch for the node being actively edited — prevents stale server data
          // from overwriting the user's ongoing typing. The local state is authoritative.
          if (isCurrentlyEditing) {
            return of(res);
          }
          return this.nodeService.getOne(res.id!);
        }),
        tap((freshNode) => {
          if (!isCurrentlyEditing) {
            this.refreshNodeRendering(freshNode);
          }
        })
      );
  }

  private initButtPressService(cy: cytoscape.Core) {
    if (this.currentMap?.isReadonly && !this.hasMapPermission('edit')) {
      this.copyPasteSub?.unsubscribe();
      this.copyPasteSub = undefined;
      this.buttonPressService.ngOnDestroy();
    } else if (!this.copyPasteSub || this.copyPasteSub.closed) {
      this.copyPasteSub = this.buttonPressService.init(cy)
        .subscribe((res) => {
          this.loadCyElements(cy, false, true, res);
        });
    }
  }

  private listenToLayoutChanges(cy: cytoscape.Core) {
    this.nodeService.layoutSubject.subscribe((type: LayoutTypes) => {
      this.applyLayout(cy, type);
    });
  }

  updateSelectedMapMetadata(map: GraphMap) {
    if (!this.currentMap) return;
    this.currentMap.metadata = map.metadata;
  }

  createAnchorsForLink(edge: EdgeSingular): { dataKey: string, anchorsArray: { x: number, y: number }[] } {
    return createNewAnchorsAndInitiateEdgeEditing(edge, this.edgeEditingInstance);
  }

  async callLinkUpdate($event: Partial<GraphNodeLink>) {
    updateInProgress.set(true);
    const cyLink = this.cy?.$('#' + $event.id) as EdgeSingular;
    if (isInIframe() || !cyLink.length) {
      return;
    }

    // Capture before state for undo/redo
    const linkId = $event.id;
    const beforeStyles: Record<string, unknown> = {};
    const afterStyles: Record<string, unknown> = {};

    // Capture text field changes (name, description)
    let beforeName: string | undefined;
    let beforeDescription: string | undefined;

    // Capture metadata changes (deep clone to avoid reference issues)
    let beforeMetadata: unknown[] | undefined;

    let hasLinkTypeChanged = $event.type !== DashboardComponent.tappedLink?.type;
    if (DashboardComponent.tappedLink === undefined || DashboardComponent.tappedLink === null) {
      hasLinkTypeChanged = false;
    }

    if (cyLink && cyLink.length > 0) {
      // Capture current values for properties being changed
      // Exclude 'name', 'description' (text changes), 'id', 'source', 'target', 'metadata' (tracked separately)
      // Also exclude 'states' - complex nested structure that causes false positive changes
      const excludedKeys = ['id', 'source', 'target', 'name', 'description', 'metadata', 'shouldUpdate', 'states'];
      const linkData = cyLink.data();
      const currentState = this.mapStateService.getCurrentLocalState();

      // Helper to get state-aware value for excludedFromState
      const getStateAwareLinkValue = (key: string): any => {
        if (key === 'excludedFromState' && currentState?.id) {
          const states = linkData.states;
          if (Array.isArray(states)) {
            const stateEntry = states.find((s: any) => s.stateId === currentState.id);
            if (stateEntry?.link && 'excludedFromState' in stateEntry.link) {
              return stateEntry.link.excludedFromState;
            }
          }
        }
        return linkData[key];
      };

      Object.keys($event).forEach(key => {
        if (!excludedKeys.includes(key)) {
          beforeStyles[key] = getStateAwareLinkValue(key);
          afterStyles[key] = ($event as any)[key];
        }
      });

      // Capture text field before values
      if ('name' in $event) {
        beforeName = cyLink.data('name') ?? '';
      }
      if ('description' in $event) {
        beforeDescription = cyLink.data('description') ?? '';
      }

      // Capture metadata before values (ensure it's an array)
      try {
        if ('metadata' in $event) {
          const rawMeta = cyLink.data('metadata');
          if (Array.isArray(rawMeta)) {
            beforeMetadata = rawMeta.map(m => ({...m}));
          } else {
            beforeMetadata = [];
          }
        }
      } catch (e) {
        console.warn('Failed to capture link metadata before state:', e);
        beforeMetadata = undefined;
      }

      // When link type changes, also capture anchor point data for undo/redo
      if (hasLinkTypeChanged) {
        const anchorKeys = [
          'controlPointPositions', 'bendPointPositions',
          'cyedgebendeditingDistances', 'cyedgebendeditingWeights',
          'cyedgecontroleditingDistances', 'cyedgecontroleditingWeights'
        ];
        anchorKeys.forEach(key => {
          beforeStyles[key] = cyLink.data(key) || [];
        });
      }
    }
    let payload = {
      ...structuredClone($event)
    };
    if (payload.states) {
      payload.states = uniqBy(payload.states, 'stateId');
    }

    if (hasLinkTypeChanged) {
      payload = this.handleEdgeTypeChangeWithAnchorPoints($event, payload);
      // Capture the new anchor point data for afterStyles
      const anchorKeys = [
        'controlPointPositions', 'bendPointPositions',
        'cyedgebendeditingDistances', 'cyedgebendeditingWeights',
        'cyedgecontroleditingDistances', 'cyedgecontroleditingWeights'
      ];
      anchorKeys.forEach(key => {
        afterStyles[key] = (payload as any)[key] || [];
      });
    } else {
      // Include current anchor points from canvas when type hasn't changed
      payload = this.updateLinksPointPositionsData(payload);
    }
    this.updateLink(payload as GraphNodeLink)
      .subscribe({
        complete: () => {
          updateInProgress.set(false);
          // Trigger thumbnail generation for visual link changes
          this.thumbnailService.markVisualChangeForCurrentMap();

          // MAP-1547: fold a just-created link's initial model-applied edits into
          // its creation entry (see the node path for the rationale). Scoped to the
          // just-created window via isNew.
          const linkStillNew = !!linkId && !!this.cy?.$('#' + linkId)?.data('isNew');
          const foldIntoCreation = linkStillNew && this.undoRedoManager.lastCommandIsCreationOf(linkId!);

          // Check for actual style changes (not just presence of keys)
          const hasActualStyleChanges = Object.keys(beforeStyles).length > 0 &&
            Object.keys(beforeStyles).some(
              key => JSON.stringify(beforeStyles[key]) !== JSON.stringify(afterStyles[key])
            );

          // Create UpdateStyleCommand for undo/redo (only if there were actual changes)
          if (linkId && hasActualStyleChanges && !foldIntoCreation) {
            const styleSnapshot: StyleChangeSnapshot = {
              elementId: linkId,
              elementType: 'link',
              beforeStyles,
              afterStyles
            };
            const updateStyleCommand = new UpdateStyleCommand(
              this.cyService,
              this.nodeService,
              [styleSnapshot],
              undefined,
              this.mapStateService
            );
            this.undoRedoManager.pushCommand(updateStyleCommand);
          }

          // Create TextChangeCommand for name changes
          if (linkId && beforeName !== undefined && $event.name !== beforeName && !foldIntoCreation) {
            const textCommand = new TextChangeCommand(
              this.cyService,
              this.nodeService,
              {
                entityId: linkId,
                entityType: 'link',
                field: 'name',
                before: beforeName,
                after: $event.name ?? ''
              },
              // MAP-1547: the name field also pushes per-keystroke commands from
              // onInstantNameChange under the active focus session. The debounced
              // form save lags behind, so its value differs and it used to land as
              // its own (unique-session) entry, fragmenting the undo stack. Reuse
              // the active focus session id so it merges with the keystroke
              // commands into one entry (same approach as description below).
              this.focusSessionService.isSessionFor(linkId, 'link', 'name')
                ? this.focusSessionService.getCurrentSessionId()!
                : `link-name-${linkId}-${Date.now()}`
            );
            this.undoRedoManager.pushCommand(textCommand);
          }

          // Create TextChangeCommand for description changes
          if (linkId && beforeDescription !== undefined && $event.description !== beforeDescription && !foldIntoCreation) {
            const textCommand = new TextChangeCommand(
              this.cyService,
              this.nodeService,
              {
                entityId: linkId,
                entityType: 'link',
                field: 'description',
                before: beforeDescription,
                after: $event.description ?? ''
              },
              // Reuse the active focus session's id so repeated debounced saves
              // within one focus-to-blur edit merge into a single undo entry
              // instead of fragmenting on every pause (MAP-1547).
              this.focusSessionService.isSessionFor(linkId, 'link', 'description')
                ? this.focusSessionService.getCurrentSessionId()!
                : `link-description-${linkId}-${Date.now()}`
            );
            this.undoRedoManager.pushCommand(textCommand);
          }

          // Create PropertyCommand for metadata changes
          // Skip if actual style changes were made - metadata is always included in events but
          // we only want to track it when the user specifically edited metadata
          // Also skip if this link was part of a recent multi-selection metadata change
          try {
            const wasInMultiSelection = linkId && lastMultiSelectionMetadataIds().has(linkId);
            if (!hasActualStyleChanges && !undoRedoJustHappened() && !wasInMultiSelection && !foldIntoCreation && linkId && beforeMetadata !== undefined && $event.metadata && Array.isArray($event.metadata)) {
              const afterMetadata = $event.metadata.map(m => ({...m}));

              // Sort by 'order' property to handle reordering from form processing
              const sortedBefore = orderBy(beforeMetadata, ['order'], ['asc']);
              const sortedAfter = orderBy(afterMetadata, ['order'], ['asc']);

              // Only create command if metadata actually changed (using deep equality)
              if (!isEqual(sortedBefore, sortedAfter)) {
                // Determine action type based on raw length change (including empty rows)
                let action: 'add' | 'remove' | 'update';
                let changedField: 'key' | 'value' | 'both' | 'row' | undefined;

                if (sortedAfter.length > sortedBefore.length) {
                  action = 'add';
                  changedField = 'row';
                } else if (sortedAfter.length < sortedBefore.length) {
                  action = 'remove';
                  changedField = 'row';
                } else {
                  action = 'update';
                  // Detect which field changed (key vs value)
                  // Find the first entry that differs and check what changed
                  for (let i = 0; i < sortedBefore.length; i++) {
                    const before = sortedBefore[i] as any;
                    const after = sortedAfter[i] as any;
                    if (!isEqual(before, after)) {
                      const keyChanged = before?.key !== after?.key;
                      const valueChanged = before?.value !== after?.value;
                      if (keyChanged && valueChanged) {
                        changedField = 'both';
                      } else if (keyChanged) {
                        changedField = 'key';
                      } else if (valueChanged) {
                        changedField = 'value';
                      }
                      break;
                    }
                  }
                }

                const propertyCommand = new PropertyCommand(
                  this.cyService,
                  this.nodeService,
                  {
                    entityId: linkId,
                    entityType: 'link',
                    action,
                    beforeMetadata,
                    afterMetadata,
                    changedField
                  }
                );
                this.undoRedoManager.pushCommand(propertyCommand);
              }
            }
          } catch (e) {
            console.warn('Failed to create PropertyCommand for link metadata:', e);
          }
        },
        error: () => {
          setTimeout(() => {
            updateInProgress.set(false);
          }, 1000);
        }
      });
  }

  private handleEdgeTypeChangeWithAnchorPoints($event: Partial<GraphNodeLink>,
                                               payload: Partial<GraphNodeLink>): Partial<GraphNodeLink> {
    const cyLink = this.cy?.$('#' + $event.id) as EdgeSingular;
    cyLink.data('type', $event.type);
    const anchorsData = this.createAnchorsForLink(cyLink as EdgeSingular);
    if (anchorsData.dataKey === 'bendPointPositions') {
      payload['controlPointPositions'] = [];
      payload[anchorsData.dataKey] = anchorsData.anchorsArray;
    } else if (anchorsData.dataKey === 'controlPointPositions') {
      payload['bendPointPositions'] = [];
      payload[anchorsData.dataKey] = anchorsData.anchorsArray;
    } else if (anchorsData.dataKey === 'NO_DATA') {
      payload['controlPointPositions'] = [];
      payload['bendPointPositions'] = [];
    }

    return payload;
  }

  private listenToNodeCreate(cy: cytoscape.Core) {
    this.nodeService.loadSubject.subscribe((n) => {
      cy.nodes().unselect();
      this.selectedNodes = [];

      setTimeout(() => {
        const newPan = this.cyService.getVisibleViewportPan(true) || { x: 0, y: 0 };
        const nodePosition = {x: n.x || newPan.x, y: n.y || newPan?.y};
        const node = cy.add({
          group: 'nodes',
          data: {
            ...this.addCyNode(n, null) as any
          },
          classes: 'expanded',
          position: nodePosition
        });
        this.hasNodesNoNodes = false;
        node.data('isNew', true);
        node.select();
        DashboardComponent.tappedNode = node.data();

        // Create CreateNodeCommand for undo/redo
        if (n.id) {
          const nodeSnapshot: GraphNodeSnapshot = {
            id: n.id,
            data: {...node.data(), id: n.id},
            position: nodePosition,
            parentId: n.parent?.id || node.data('parent') || null,
            classes: node.classes()
          };
          const createNodeCommand = new CreateNodeCommand(
            this.cyService,
            this.nodeService,
            nodeSnapshot
          );
          this.undoRedoManager.pushCommand(createNodeCommand);
        }

        if (!this.createFromMenu) {
          //this.centerOnTarget(cy, node);
        }
        this.createFromMenu = false;
      }, 100);

    });
  }

  private handleLocalSettings() {
    document.documentElement.style.setProperty('--sidebar-width', `${this.appliedSidebarWidth}px`);
    timer(300)
      .subscribe(async () => {
        const localStorageSettings = JSON.parse(localStorage.getItem(SETTINGS_STORAGE_KEY) as string);
        this.isSideBarOpen = localStorageSettings?.isSidebarOpen && !localStorageSettings?.forceOpened || false;
        if (localStorageSettings?.isSidebarOpen) {
          this.drawerWidth = localStorageSettings?.sidebarWidth || DefaultSidebarWidth;
          await this.drawer?.open();
        } else {
          const drawerElement = document.getElementsByClassName('drawer')[0] as HTMLElement;
          this.lastKnownSidebarWidth = drawerElement?.offsetWidth;
          await this.drawer?.close();
        }
        document.documentElement.style.setProperty('--sidebar-width', `${this.appliedSidebarWidth}px`);
      });
  }

  private handleCanvasUpdateForLabelMarginOnDrag(cy: cytoscape.Core) {
    let pendingEdgeIds: Set<string> | null = null;
    let rafHandle: number | null = null;
    let usingTimeoutFallback = false;

    const flushPendingEdges = () => {
      if (!pendingEdgeIds || pendingEdgeIds.size === 0) {
        return;
      }

      const renderer = this.cyService.linkLabelRenderer;
      if (!renderer) {
        pendingEdgeIds.clear();
        return;
      }

      const edgesToUpdate: EdgeSingular[] = [];
      pendingEdgeIds.forEach((edgeId) => {
        const edge = cy.getElementById(edgeId);
        if (edge && edge.length && edge.isEdge()) {
          edgesToUpdate.push(edge);
        }
      });

      pendingEdgeIds.clear();

      if (edgesToUpdate.length === 0) {
        return;
      }

      renderer.updateLabelPositions(edgesToUpdate);
    };

    const scheduleEdgeFlush = () => {
      if (rafHandle !== null) {
        return;
      }

      if (typeof requestAnimationFrame === 'function') {
        rafHandle = requestAnimationFrame(() => {
          rafHandle = null;
          flushPendingEdges();
        });
        usingTimeoutFallback = false;
      } else {
        rafHandle = window.setTimeout(() => {
          rafHandle = null;
          usingTimeoutFallback = false;
          flushPendingEdges();
        }, 16);
        usingTimeoutFallback = true;
      }
    };

    // cytoscape fires 'drag' on the grabbed node only; nested links need descendants.
    const edgesAffectedByGrab = new Map<string, string[]>();

    const affectedEdgeIds = (node: any): string[] => {
      const cached = edgesAffectedByGrab.get(node.id());
      if (cached) return cached;
      const ids = node
        .union(node.descendants())
        .connectedEdges()
        .map((edge: EdgeSingular) => edge.id());
      edgesAffectedByGrab.set(node.id(), ids);
      return ids;
    };

    cy.on('grab', 'node', (event: any) => {
      const node = event?.target;
      if (!node) return;
      edgesAffectedByGrab.delete(node.id());
      affectedEdgeIds(node);
    });

    cy.on('drag', 'node', (event: any) => {
      const draggedNode = event?.target;
      if (!draggedNode) return;

      if (!pendingEdgeIds) {
        pendingEdgeIds = new Set<string>();
      }

      for (const edgeId of affectedEdgeIds(draggedNode)) {
        pendingEdgeIds.add(edgeId);
      }

      scheduleEdgeFlush();
    });

    cy.on('free', 'node', () => {
      if (rafHandle !== null) {
        if (!usingTimeoutFallback && typeof cancelAnimationFrame === 'function') {
          cancelAnimationFrame(rafHandle);
        } else {
          clearTimeout(rafHandle);
        }
        rafHandle = null;
        usingTimeoutFallback = false;
      }

      flushPendingEdges();

      if (pendingEdgeIds) {
        pendingEdgeIds.clear();
      }
      edgesAffectedByGrab.clear();
    });
  }

  private buildGptElements(data: GptMapResponse, cy: any) {
    data.nodes.forEach((node: any) => {
      cy.add({
        group: 'nodes',
        data: {
          id: node.id,
          name: node.name,
          label: 'name',
          color: node.color || DefaultValues.node.color,
          properties: DefaultNodeProperties
        }
      });
    });
    data.links.forEach((link: any) => {
      cy.add({
        group: 'edges',
        data: {
          id: link.id,
          label: link.label || 'name',
          source: link.from,
          target: link.to,
          textFontSize: link.textFontSize,
          direction: link.direction || 'directed',
          name: link.name || '',
          color: link.color || 'black'
        }
      });
    });
  }

  private resetAllSelected(cy: cytoscape.Core) {
    const idsToReset = new Set<string>();

    const addId = (id?: string | null) => {
      if (id) {
        idsToReset.add(id);
      }
    };

    this.selectedNodes.forEach((node) => addId(node?.id));
    this.selectedLinks.forEach((link) => addId(link?.id));
    DashboardComponent.multiSelectedElements.forEach((item) => addId(item?.id));

    if (cy) {
      cy.$(':selected').forEach((element) => addId(element.id()));
      cy.$('.highlighted').forEach((element) => addId(element.id()));
      cy.$('.multi').forEach((element) => addId(element.id()));
    }

    this.selectedNodes = [];
    this.selectedLinks = [];
    DashboardComponent.tappedLink = null;
    DashboardComponent.tappedNode = null;
    DashboardComponent.multiSelectedElements = [];
    // Stop selection sound when focus is cleared
    this.audioService.handleElementFocusCleared();
    if (!cy || idsToReset.size === 0) {
      return;
    }

    const elementsToReset: cytoscape.SingularElementReturnValue[] = [];

    idsToReset.forEach((id) => {
      const element = cy.getElementById(id);
      if (element && element.length) {
        elementsToReset.push(element);
      }
    });

    if (elementsToReset.length === 0) {
      return;
    }

    cy.batch(() => {
      elementsToReset.forEach((element) => {
        element.removeClass('highlighted');
        element.removeClass('multi');
        element.removeData('selected');
        // Don't remove 'isNew' here - it should persist until element is saved or edit mode is exited
        element.unselect();
      });
    });
  }

  private listenToMapStateChanges(state: MapStateDto | null = null) {
    if (!this.cyService.cy || !this.initialLoadComplete) {
      return;
    }

    const cy = this.cyService.cy;
    const newStateId = state?.id ?? null;

    // Preserve selection before state switch
    const selectedIds = cy.$(':selected').map((n: any) => n.id());
    const focused = {
      link: DashboardComponent?.tappedLink?.id,
      node: DashboardComponent?.tappedNode?.id
    };

    updateInProgress.set(true);

    // Switch overlay element states
    this.overlayManager.switchState(newStateId);

    // Use optimized in-place update instead of destroy/reload
    this.cyService.switchStateOptimized(cy, newStateId, {
      animate: true,
      animationDuration: 300,
      onComplete: () => {
        // Restore selection
        if (selectedIds.length > 0) {
          cy.elements(selectedIds.map((id: string) => `#${id}`).join(', ')).select();
          const focusedId = focused.link ?? focused.node;
          if (focusedId) {
            const el = cy.getElementById(focusedId);
            if (el.length) {
              this.tapElement(el.data(), el.isNode(), cy);
            }
          }
        }
        Logger.log('ListenToMapStateChange sending update in progress signal - false');
        updateInProgress.set(false);
      }
    });
  }

  private saveGptNodes() {
    if (!this.cy) return;
    const nodes = this.cy.nodes().map((n) => {
      const payload = n.data();
      return {
        ...payload,
        map: this.currentMap,
        x: n.position().x,
        y: n.position().y
      };
    });
    const links = this.cy.edges().map((e: any) => {
      return {
        ...e.data(),
        from: e.data('source'),
        to: e.data('target'),
        map: this.currentMap
      };
    });

    this.nodeService.createMultiple(nodes)
      .pipe(
        switchMap((nodes) => {
          return this.nodeService.createMultipleLinks(links);
        })
      )
      .subscribe((n) => {
        location.reload();
      });
  }

  private updateChildPositionRecursively(child: {
    x: number,
    y: number,
    id: string,
    children: any[]
  }, positionDifference: { x: number; y: number; }) {
    if (!this.cy) return;
    child.x += positionDifference.x;
    child.y += positionDifference.y;

    if (this.cyService.getLayoutChangedSignal() !== 'preset') return;

    // Update the child's position on the server
    this.nodeService.updateNodePosition(child.id, child.x, child.y)
      .subscribe((node) => this.updateNodeLinksControlPoints(node));
    if (child.children && child.children.length > 0) {
      child.children.forEach(c => this.updateChildPositionRecursively(c, positionDifference));
    }
  }

  private updateNodeLinksControlPoints(node: GraphNode) {
    if (!this.cy) return;

    const nodeLinks = this.cy.edges().filter((e) => e.data('source') === node.id || e.data('target') === node.id);
    nodeLinks.forEach((link) => {
      const linkData = link.data();

      // Skip BEZIER edges - they don't have control points to update
      if (linkData.type === 'BEZIER') {
        return;
      }

      const needsUpdate = Boolean(
        linkData?.cyedgecontroleditingWeights ||
        linkData?.cyedgecontroleditingDistances ||
        linkData?.controlPointPositions ||
        linkData?.cyedgebendeditingWeights ||
        linkData?.cyedgebendeditingDistances ||
        linkData?.bendPointPositions
      );

      if (needsUpdate) {
        this.pendingLinkUpdateIds.add(link.id());
      }
    });

    if (this.pendingLinkUpdateIds.size > 0) {
      this.scheduleLinkUpdates();
    }
  }

  private scheduleLinkUpdates() {
    if (this.pendingLinkUpdateTimer !== null) {
      return;
    }

    this.pendingLinkUpdateTimer = setTimeout(() => {
      this.pendingLinkUpdateTimer = null;
      this.flushPendingLinkUpdates();
    }, 0);
  }

  private flushPendingLinkUpdates() {
    if (!this.cy || this.pendingLinkUpdateIds.size === 0) {
      this.pendingLinkUpdateIds.clear();
      return;
    }

    const currentState = this.mapStateService.getCurrentLocalState();
    const idsToProcess = Array.from(this.pendingLinkUpdateIds);
    this.pendingLinkUpdateIds.clear();


    idsToProcess.forEach((linkId) => {
      const edge = this.cy?.getElementById(linkId);
      if (!edge || !edge.length) return;

      let linkData = edge.data();
      const statedLink = linkData.states?.find((s: any) => s.stateId === currentState?.id);
      let shouldUpdate = true;
      if (currentState?.id && statedLink) {
        if (statedLink.link.excludedFromState) {
          shouldUpdate = false;
        }
      }


      linkData = this.updateLinksPointPositionsData(linkData);

      if (shouldUpdate) {
        this.updateLink(linkData).subscribe();
      }
    });
  }

  /**
   * 'hidden' is opacity 0 + visibility hidden, so cytoscape drops the edge from
   * hit-testing and the selection rectangle; 'dimmed' renders at the configurable
   * filtering opacity and stays fully interactive. 'unlabeled' rides along with
   * 'dimmed' and takes the label out. In "No links" the choice does not apply.
   */
  private applyEdgeViewModeClasses(mode: LinkViewMode, otherLinksMode: OtherLinksMode) {
    const cy = this.cy;
    if (!cy) return;
    const effective = resolveOtherLinksMode(mode, otherLinksMode);

    if (mode === LinkViewMode.ALL) {
      clearOtherClasses(cy.edges());
    }
    if (mode === LinkViewMode.OFF || mode === LinkViewMode.RELATED || mode === LinkViewMode.SELECTION_ENCLOSED_LINKS) {
      applyOtherLinksClasses(cy.edges().not(':selected'), effective);
    }

    if (mode === LinkViewMode.RELATED) {
      clearOtherClasses(cy.nodes(':selected').edgesWith('*'));
    }

    if (mode === LinkViewMode.SELECTION_ENCLOSED_LINKS) {
      const selectedNodes = cy.nodes(':selected');
      cy.edges().forEach((edge) => {
        const source = edge.source();
        const target = edge.target();
        if (selectedNodes.contains(source) && selectedNodes.contains(target)) {
          clearOtherClasses(edge);
        }
      });
    }
    this.edgeViewMode = mode;
    this.applyNodeViewModeClasses();

    // Trigger redraw of link labels after visibility changes
    if (this.cyService.linkLabelRenderer) {
      setTimeout(() => {
        this.cyService.linkLabelRenderer?.redraw();
      }, 100);
    }
  }

  private resolvedOtherNodesMode(): OtherNodesMode {
    return resolveOtherNodesMode(this.settingsService.linkViewMode, this.settingsService.otherNodesMode);
  }

  private applyNodeViewModeClasses() {
    if (!this.cy) return;
    applyOtherNodesClasses(this.cy, this.resolvedOtherNodesMode());
  }

  /**
   * Box-select raises one event per element, so collapse a burst into a single
   * pass on the next frame.
   */
  private scheduleNodeViewModeClasses() {
    if (this.resolvedOtherNodesMode() === OtherNodesMode.SHOWN) return;
    if (this.nodeViewModePassScheduled) return;
    this.nodeViewModePassScheduled = true;
    requestAnimationFrame(() => {
      this.nodeViewModePassScheduled = false;
      this.applyNodeViewModeClasses();
    });
  }

  private toggleEdgeVisibility(cy: Core, deselect = false) {
    setTimeout(() => {
      const edgeLocalEdgeViewMode = this.settingsService.linkViewMode;
      const otherLinksMode = resolveOtherLinksMode(edgeLocalEdgeViewMode, this.settingsService.otherLinksMode);
      if (edgeLocalEdgeViewMode === LinkViewMode.RELATED) {
        if (deselect && cy.elements(':selected').data() == undefined) {
          applyOtherLinksClasses(
            cy.edges().filter((e) => e.id() !== DashboardComponent.tappedLink?.id),
            otherLinksMode
          );
        } else {
          applyOtherLinksClasses(cy.edges().not(':selected').not('.highlighted'), otherLinksMode);
          setTimeout(() => {
            clearOtherClasses(cy.edges(':selected'));
            this.applyNodeViewModeClasses();
          }, 200);
        }
        this.selectedNodes.forEach((node: any) => {
          const cyNode = cy.getElementById(node.id as string);
          clearOtherClasses(cyNode.edgesWith('*'));
        });
      }

      if (edgeLocalEdgeViewMode === LinkViewMode.SELECTION_ENCLOSED_LINKS) {
        applyOtherLinksClasses(cy.edges(), otherLinksMode);
        const selectedNodes = cy.nodes(':selected');
        const selectedEdges = cy.edges(':selected');
        cy.edges().forEach((edge) => {
          const source = edge.source();
          const target = edge.target();
          // Show edges where both endpoints are selected OR the edge itself is selected
          if ((selectedNodes.contains(source) && selectedNodes.contains(target)) || selectedEdges.contains(edge)) {
            clearOtherClasses(edge);
          }
        });
      }

      if (edgeLocalEdgeViewMode === LinkViewMode.OFF) {
        applyOtherLinksClasses(cy.edges(), otherLinksMode);
        clearOtherClasses(cy.edges(':selected'));
      }

      this.applyNodeViewModeClasses();

      // Trigger redraw of link labels after edge visibility changes
      if (this.cyService.linkLabelRenderer) {
        setTimeout(() => {
          this.cyService.linkLabelRenderer?.redraw();
        }, 100);
      }
    }, 300);

  }

  private handleNodePositionChange(cy: cytoscape.Core) {

    cy.on('free', 'node', (event: any) => {
      const layout = this.cyService.getLayoutChangedSignal();
      if (layout && layout !== 'preset') return;

      if (this.hasMapPermission('edit')) {
        const distanceChangeTolerance = 1;
        // check how far the position has changed
        const currentPosition = { ...event.target.position() };
        const grabbedPosition = event.target.data('grabbedPosition');
        const positionDifference = {
          x: currentPosition.x - grabbedPosition.x,
          y: currentPosition.y - grabbedPosition.y
        };
        const distance = Math.sqrt(Math.pow(positionDifference.x, 2) + Math.pow(positionDifference.y, 2));
        if (distance < distanceChangeTolerance) {
          return;
        }

        const node = event.target;
        if (!node.data('touched')) {
          node.data('touched', true);
        }

        if (node.data('collapsed')) {
          const currentPosition = { ...node.position() };
          const grabbedPosition = node.data('grabbedPosition');
          const positionDifference = {
            x: currentPosition.x - grabbedPosition.x,
            y: currentPosition.y - grabbedPosition.y
          };
          const children = node.data('children');

          if (children && children.length > 0) {
            children
              .forEach((child: any) => this.updateChildPositionRecursively(child, positionDifference));
          }

          this.nodeService.updateNodePosition(node.id(), currentPosition.x, currentPosition.y)
            .subscribe((responseNode) => {
              this.updateNodeLinksControlPoints(responseNode);
              // Keep local states data in sync for switchStateOptimized
              const cyNode = this.cy?.$('#' + responseNode.id);
              if (cyNode?.length) {
                if (responseNode.states) {
                  cyNode.data('states', responseNode.states);
                }
                cyNode.data('x', responseNode.x);
                cyNode.data('y', responseNode.y);
              }
              // Trigger thumbnail generation for position changes
              this.thumbnailService.markVisualChangeForCurrentMap();
            });
        } else {
          // Logic for standalone nodes or expanded compound nodes if any
          const position = node.position();
          this.nodeService.updateNodePosition(node.id(), position.x, position.y)
            .subscribe((responseNode) => {
              this.updateNodeLinksControlPoints(responseNode);
              // Keep local states data in sync for switchStateOptimized
              const cyNode = this.cy?.$('#' + responseNode.id);
              if (cyNode?.length) {
                if (responseNode.states) {
                  cyNode.data('states', responseNode.states);
                }
                cyNode.data('x', responseNode.x);
                cyNode.data('y', responseNode.y);
              }
              // Trigger thumbnail generation for position changes
              this.thumbnailService.markVisualChangeForCurrentMap();
            });
        }

      }
    });
  }

  private tapElement(data: GraphNode | GraphNodeLink, isNode: boolean, cy: cytoscape.Core, shiftPressed?: boolean) {
    // Skip heavy processing during animations
    if (ExpandCollapseInProgress() || centeringInProgress()) {
      return;
    }

    const targetData = data;

    // Early return if no element data
    if (!targetData?.id) {
      if (!this.audioService.isPlayingMapSound()) {
        this.resetAllSelected(cy);
      }
      return;
    }

    const isDefaultState = !this.mapStateService.getCurrentLocalState()?.id;
    let statedElement: any;

    if (!isDefaultState) {
      statedElement = targetData?.states?.find((s: any) => s.stateId === this.mapStateService.getCurrentLocalState()?.id);
    }

    const elementToWorkWith = isNode ? targetData || statedElement?.node : targetData || statedElement?.link;
    const isElementSelected = targetData?.id ? this.isElementSelectedById(targetData.id) : false;

    // A real click's event carries the live modifier state; prefer it over
    // the tracked static flag, which can get stuck true if a keyup is missed
    // (e.g. focus lost while Shift was held). Only synthetic/programmatic
    // taps (no shiftPressed arg) fall back to the static flag.
    const isShiftPressed = shiftPressed ?? ButtonPressService.isShiftPressed;

    if (isShiftPressed) {
      // MAP-1270: If shift-grab already handled selection (during drag), skip toggle
      if (CytoscapeService.shiftGrabOccurred) {
        CytoscapeService.shiftGrabOccurred = false;
        return;
      }
      this.handleShiftClick(elementToWorkWith, isElementSelected, isNode, cy);
    } else {
      this.handleRegularClick(elementToWorkWith, isElementSelected, isNode, cy, targetData, statedElement);
    }
  }

  private isElementSelectedById(elementId: string): boolean {
    if (!elementId) return false;

    // Check if element is in multi-selection
    const isInMultiSelection = DashboardComponent.multiSelectedElements.some((e: any) => e.id === elementId);

    // Check if element is the current focused node or link
    const isFocusedNode = DashboardComponent.tappedNode?.id === elementId;
    const isFocusedLink = DashboardComponent.tappedLink?.id === elementId;

    // Also check the legacy arrays for consistency
    const isInSelectedNodes = this.selectedNodes.some((n: any) => n.id === elementId);
    const isInSelectedLinks = this.selectedLinks.some((l: any) => l.id === elementId);

    return isInMultiSelection || isFocusedNode || isFocusedLink || isInSelectedNodes || isInSelectedLinks;
  }

  private handleShiftClick(element: any, isSelected: boolean, isNode: boolean, cy: cytoscape.Core) {
    if (!element) return;
    if (isSelected) {
      this.deselectElement(element, isNode, cy);
    } else {
      this.selectElement(element, isNode, cy, false);
    }
  }

  private handleRegularClick(element: any, isSelected: boolean, isNode: boolean, cy: cytoscape.Core, targetData: any, statedElement: any) {
    if (!element) {
      return;
    }

    if (isSelected) {
      // Only set focus if element is not already the current focus
      if (!this.isCurrentFocus(element, isNode)) {
        this.setSelectionFocus(element, isNode);
      }
    } else {
      // Clear selection only if not empty
      if (DashboardComponent.multiSelectedElements.length > 0 || DashboardComponent.tappedNode || DashboardComponent.tappedLink) {
        this.resetAllSelected(cy);
      }
      this.selectElement(element, isNode, cy, true);
    }
  }

  private selectElement(element: any, isNode: boolean, cy: cytoscape.Core, clearOthers = false) {
    if (isEmpty(element)) {
      return;
    }
    if (clearOthers) {
      this.resetAllSelected(cy);
    }

    const existingElement = DashboardComponent.multiSelectedElements.find((e: any) => e.id === element.id);
    if (!existingElement) {
      DashboardComponent.multiSelectedElements.push(element);
    }

    this.setSelectionFocus(element, isNode);

    // Update the legacy selection arrays
    if (isNode) {
      const existingNode = this.selectedNodes.find((n: any) => n.id === element.id);
      if (!existingNode) {
        this.selectedNodes.push(element);
      }
    } else {
      const existingLink = this.selectedLinks.find((l: any) => l.id === element.id);
      if (!existingLink) {
        this.selectedLinks.push(element);
      }
    }

    cy.getElementById(element.id).select();
  }

  private deselectElement(element: any, isNode: boolean, cy: cytoscape.Core) {
    if (!element) return;
    setTimeout(() => {
      const wasCurrentFocus = this.isCurrentFocus(element, isNode);
      // Manually handle deselection to avoid the complex unselect event handler
      DashboardComponent.multiSelectedElements = DashboardComponent.multiSelectedElements.filter((e: any) => e.id !== element.id);
      // Update the legacy selection arrays
      if (isNode) {
        this.selectedNodes = this.selectedNodes.filter((n: any) => n.id !== element.id);
      } else {
        this.selectedLinks = this.selectedLinks.filter((l: any) => l.id !== element.id);
      }

      // Update cytoscape element
      const cyElement = cy.getElementById(element.id);
      cyElement.removeClass('highlighted');
      cyElement.removeClass('multi');
      cyElement.removeData('selected');

      // Temporarily disable the unselect handler to avoid conflicts
      cyElement.addClass('manual-deselect');
      cyElement.unselect();
      cyElement.removeClass('manual-deselect');


      if (wasCurrentFocus && DashboardComponent.multiSelectedElements.length > 0) {
        this.updateFocusAfterDeselection(isNode);
      } else if (wasCurrentFocus) {
        DashboardComponent.tappedNode = null;
        DashboardComponent.tappedLink = null;
        // Stop selection sound when focus is cleared
        this.audioService.handleElementFocusCleared();
      }
    }, 300);
  }

  private setSelectionFocus(element: any, isNode: boolean) {
    if (isNode) {
      // Get state-specific data for the node
      const currentStateId = this.mapStateService.getCurrentLocalState()?.id;
      const isDefaultState = currentStateId === undefined;
      if (!isDefaultState) {
        const stateNode = element.states?.find((s: NodeStateData) => s.stateId === currentStateId)?.node;
        if (stateNode) {
          // Include the full states array on the state-specific node so updates can preserve all states
          DashboardComponent.tappedNode = {...stateNode, states: element.states};
        } else {
          // Create new object reference to trigger Angular change detection
          DashboardComponent.tappedNode = {...element};
        }
      } else {
        // Create new object reference to trigger Angular change detection
        DashboardComponent.tappedNode = {...element};
      }
      DashboardComponent.tappedLink = null;
    } else {
      // Get state-specific data for the link
      const currentStateId = this.mapStateService.getCurrentLocalState()?.id;
      const isDefaultState = currentStateId === undefined;
      if (!isDefaultState) {
        const stateLink = element.states?.find((s: LinkStateData) => s.stateId === currentStateId)?.link;
        if (stateLink) {
          // Include the full states array on the state-specific link so updates can preserve all states
          DashboardComponent.tappedLink = {...stateLink, states: element.states};
        } else {
          // Create new object reference to trigger Angular change detection
          DashboardComponent.tappedLink = {...element};
        }
      } else {
        // Create new object reference to trigger Angular change detection
        DashboardComponent.tappedLink = {...element};
      }
      DashboardComponent.tappedNode = null;

      // Trigger a data update on the cy edge to refresh edge editing handles
      // This causes the edge editing plugin's eData handler to update edgeToHighlight
      // based on the new tappedLink and call refreshDraws()
      if (element?.id && this.cy) {
        const cyEdge = this.cy.$('#' + element.id);
        if (cyEdge && cyEdge.length) {
          // Trigger a harmless data change to invoke the eData handler
          cyEdge.data('_focusTimestamp', Date.now());
        }
      }
    }
    // Handle element focus change with appropriate sound
    const focusedElement = isNode ? DashboardComponent.tappedNode : DashboardComponent.tappedLink;
    this.audioService.handleElementFocusChange(focusedElement);
  }

  private isCurrentFocus(element: any, isNode: boolean): boolean {
    if (isNode) {
      return DashboardComponent.tappedNode?.id === element.id;
    } else {
      return DashboardComponent.tappedLink?.id === element.id;
    }
  }

  /**
   * Refresh the tapped node/link with current data from cytoscape.
   * Called after undo/redo to ensure sidebar reflects the updated state.
   */
  public refreshTappedElements(): void {
    if (!this.cy) return;

    if (DashboardComponent.tappedNode?.id) {
      const cyNode = this.cy.$('#' + DashboardComponent.tappedNode.id);
      if (cyNode && cyNode.length) {
        const nodeData = cyNode.data();
        this.setSelectionFocus(nodeData, true);
      } else {
        // MAP-1547: the focused node was removed from the graph (e.g. undoing its
        // creation). A .remove() does not fire the 'unselect' handler, so the
        // sidebar would keep showing the now-deleted node instead of the smap
        // info panel. Clear the focus explicitly so the sidebar falls back to it.
        DashboardComponent.tappedNode = null;
        this.selectedNodes = [];
        this.audioService.handleElementFocusCleared();
      }
    }

    if (DashboardComponent.tappedLink?.id) {
      const cyEdge = this.cy.$('#' + DashboardComponent.tappedLink.id);
      if (cyEdge && cyEdge.length) {
        const linkData = cyEdge.data();
        this.setSelectionFocus(linkData, false);
      } else {
        // MAP-1547: same as above for a removed focused link.
        DashboardComponent.tappedLink = null;
        this.selectedLinks = [];
        this.audioService.handleElementFocusCleared();
      }
    }

    // Trigger change detection to update sidebar bindings
    this.cdr.detectChanges();
  }

  private updateFocusAfterDeselection(wasNode: boolean) {
    const remainingElements = DashboardComponent.multiSelectedElements;
    if (remainingElements.length === 0) {
      DashboardComponent.tappedNode = null;
      DashboardComponent.tappedLink = null;
      // Stop selection sound when focus is cleared
      this.audioService.handleElementFocusCleared();
      return;
    }

    const firstElement = remainingElements[0];
    const isFirstElementNode = this.isElementNode(firstElement);
    this.setSelectionFocus(firstElement, isFirstElementNode);
  }

  private isElementNode(element: any): boolean {
    return element && 'children' in element && !('from' in element) && !('to' in element);
  }

  private handleElementTap(cy: cytoscape.Core) {
    cy.on('tap', (event) => {
      if (!event?.target.container && event?.target?.hasClass('hidden')) {
        return;
      }

      // Skip tap handling during double-tap detection to prevent cascade
      if (isDoubleTapInProgress()) {
        return;
      }

      const focusBefore = this.focusSubjectKey();
      this.tapElement(
        event.target.data(),
        event?.target?.isNode !== undefined && event.target.isNode(),
        cy,
        event.originalEvent?.shiftKey
      );
      this.openSidebarIfFocusChanged(focusBefore);
    });
  }

  private focusSubjectKey(): string {
    if (DashboardComponent.tappedNode?.id) return `node:${DashboardComponent.tappedNode.id}`;
    if (DashboardComponent.tappedLink?.id) return `link:${DashboardComponent.tappedLink.id}`;
    if (DashboardComponent.tappedOverlayElement?.id) return `overlay:${DashboardComponent.tappedOverlayElement.id}`;
    return 'smap';
  }

  private openSidebarIfFocusChanged(focusBefore: string) {
    if (isInIframe()) return;
    const focusAfter = this.focusSubjectKey();
    if (focusAfter === focusBefore || focusAfter.startsWith('overlay:')) return;
    this.settingsService.openSidebarOnFocusChange();
  }

  centerCanvas() {
    if (this.cyService.cy && !this.isCentering) {
      this.isCentering = true;
      this.cyService.centerCanvas(true, false, true);
      setTimeout(() => this.isCentering = false, 300);
    }
  }

  //TODO: Possible duplicate , see cyservice
  private centerOnTarget(cy: Core, ele: any) {
    const sidebarElement = document.getElementsByClassName('drawer')[0] as HTMLElement;
    const toolbarElement = document.getElementsByClassName('navbar')[0] as HTMLElement;

    const sidebarWidth = sidebarElement?.offsetWidth || 0;
    const toolbarHeight = toolbarElement?.offsetHeight || 0;
    const isSidebarToggled = sidebarWidth > 0;

    const viewportSize = {
      width: isSidebarToggled ? cy.width() - sidebarWidth : cy.width(),
      height: cy.height() + toolbarHeight
    };

    let targetPosition;

    if (ele.isNode()) {
      targetPosition = ele.position();
    } else if (ele.isEdge()) {
      const sourcePos = ele.source().position();
      const targetPos = ele.target().position();

      targetPosition = {
        x: (sourcePos.x + targetPos.x) / 2,
        y: (sourcePos.y + targetPos.y) / 2
      };
    }
    const currentPan = cy.pan();
    const currentZoom = cy.zoom();

    const zoomedNodePosition = {
      x: targetPosition.x * currentZoom,
      y: targetPosition.y * currentZoom
    };

    const newPan = {
      x: currentPan.x - (zoomedNodePosition.x + currentPan.x - viewportSize.width / 2),
      y: currentPan.y - (zoomedNodePosition.y + currentPan.y - viewportSize.height / 2)
    };

    cy.animate({
      pan: newPan,
      duration: 500,
      easing: 'ease',
      complete: () => {
        // Save pan/zoom state immediately after centering animation completes
        savePanZoomState();
      }
    });
    this.isCentering = false;
  }

  private centerOnElements(cy: Core, elements: any) {
    this.cyService.centerOnElements(cy, elements);
  }


  private async loadCyElements(cy: cytoscape.Core,
                               refreshLayout = true,
                               paste = false,
                               nodes?: GraphNode[]) {
    if (this.hasNoMap) return;
    if (this.isLoading) return;
    this.isLoading = true;
    // Re-engage the loading overlay for every map switch, not just the very
    // first one this session: canvasLoadedSignal only ever went back to
    // false via its own initial value, so switching to another already-
    // running session's map left the canvas visible with no overlay, and
    // the render-then-viewport-correction sequence played out uncovered.
    if (!paste) {
      this.appWideStateService.setCanvasLoaded(false);
    }
    if (!nodes) {
      this.loadAllForMap(cy, refreshLayout);
    } else {
      this.renderCyElements(cy, nodes, refreshLayout, paste);
    }
  }

  private loadAllForMap(cy: Core, refreshLayout = true) {
    const stream = this.readModeAppState ?
      this.nodeService.getPublicNodeForMap(this.currentMap?.id as string) :
      this.nodeService.getAllForMap(this.currentMap?.id as string);
    stream
      .pipe(
        catchError((err) => {
          this.isLoading = false;
          return throwError(err);
        })
      )
      .subscribe((res) => {
        if (cy) {
          this.renderCyElements(cy, res, refreshLayout);
          // Load overlay elements alongside nodes
          this.loadOverlayElements();
          // Update labels on position change - register only once to prevent handler accumulation
          if (!this.renderHandlerRegistered) {
            this.renderHandlerRegistered = true;
            cy.on('render', () => {
              cy.nodes().forEach(node => {
                const boundingBox = node.boundingBox({includeLabels: true});
                const position = {x: (boundingBox.x1 + boundingBox.x2) / 2, y: boundingBox.y1}; // Label position
                const labelDiv = document.querySelector(`.node-label[data-id="${node.id()}"]`) as any;
                if (labelDiv) {
                  labelDiv.style.left = `${position.x}px`;
                  labelDiv.style.top = `${position.y}px`;
                }
              });
            });
          }
        }
      });
  }

  callNodeUpdate($event: Partial<GraphNode>) {
    updateInProgress.set(true);
    BlockUiInterceptor.isLoadingSubject.next(false);

    // Capture before state for undo/redo
    const nodeId = $event.id;
    const cyNode = nodeId ? this.cy?.$(`#${nodeId}`) : null;
    const beforeStyles: Record<string, unknown> = {};
    const afterStyles: Record<string, unknown> = {};

    // Capture text field changes (name, description)
    let beforeName: string | undefined;
    let beforeDescription: string | undefined;

    // Capture metadata changes (deep clone to avoid reference issues)
    let beforeMetadata: unknown[] | undefined;

    if (cyNode && cyNode.length > 0) {
      // Capture current values for properties being changed
      // Exclude 'name', 'description' (text changes), 'id', 'metadata', 'properties' (tracked separately)
      // Also exclude 'states' - complex nested structure that causes false positive changes
      const excludedKeys = ['id', 'name', 'description', 'metadata', 'properties', 'states'];
      const nodeData = cyNode.data();
      const currentState = this.mapStateService.getCurrentLocalState();

      // Helper to get state-aware value for excludedFromState
      const getStateAwareNodeValue = (key: string): any => {
        if (key === 'excludedFromState' && currentState?.id) {
          const states = nodeData.states;
          if (Array.isArray(states)) {
            const stateEntry = states.find((s: any) => s.stateId === currentState.id);
            if (stateEntry?.node && 'excludedFromState' in stateEntry.node) {
              return stateEntry.node.excludedFromState;
            }
          }
        }
        return nodeData[key];
      };

      Object.keys($event).forEach(key => {
        if (!excludedKeys.includes(key)) {
          beforeStyles[key] = getStateAwareNodeValue(key);
          afterStyles[key] = ($event as any)[key];
        }
      });

      // Capture properties changes with flattened keys (e.g., 'properties.scale')
      // Only capture properties that have actually changed to avoid false positives
      if ('properties' in $event && $event.properties) {
        const beforeProps = cyNode?.data('properties') || {};
        const afterProps = $event.properties;

        // Capture only properties that have actually changed
        Object.keys(afterProps).forEach(propKey => {
          const beforeVal = beforeProps[propKey];
          const afterVal = (afterProps as any)[propKey];

          // Skip if values are equal (handles string/number coercion)
          if (beforeVal === afterVal) return;
          if (beforeVal == null && afterVal == null) return;

          // Skip if both are "empty" (undefined, null, empty string are all considered empty)
          const isBeforeEmpty = beforeVal == null || beforeVal === '';
          const isAfterEmpty = afterVal == null || afterVal === '';
          if (isBeforeEmpty && isAfterEmpty) return;

          // Handle numeric comparison with type coercion
          if (beforeVal != null && afterVal != null) {
            const numBefore = typeof beforeVal === 'string' ? parseFloat(beforeVal) : beforeVal;
            const numAfter = typeof afterVal === 'string' ? parseFloat(afterVal) : afterVal;
            if (typeof numBefore === 'number' && typeof numAfter === 'number' &&
              !isNaN(numBefore) && !isNaN(numAfter) && numBefore === numAfter) {
              return;
            }
          }

          const flatKey = `properties.${propKey}`;
          beforeStyles[flatKey] = beforeVal;
          afterStyles[flatKey] = afterVal;
        });
      }

      // Capture text field before values
      if ('name' in $event) {
        beforeName = cyNode.data('name') ?? '';
      }
      if ('description' in $event) {
        beforeDescription = cyNode.data('description') ?? '';
      }

      // Capture metadata before values (ensure it's an array)
      try {
        if ('metadata' in $event) {
          const rawMeta = cyNode.data('metadata');
          if (Array.isArray(rawMeta)) {
            beforeMetadata = rawMeta.map(m => ({...m}));
          } else {
            beforeMetadata = [];
          }
        }
      } catch (e) {
        console.warn('Failed to capture metadata before state:', e);
        beforeMetadata = undefined;
      }
    }

    this.updateNode($event)
      .pipe(
        finalize(() => {
          updateInProgress.set(false);
          // Trigger thumbnail generation for visual changes
          if (this.isVisualNodeChange($event)) {
            this.thumbnailService.markVisualChangeForCurrentMap();
          }

          // MAP-1547: if this node was just created (still flagged isNew AND its
          // creation is the top undo entry), fold the model-applied style/name into
          // that entry rather than pushing separate ones. Otherwise undoing a
          // model-created node reverts the extra style change first and appears to
          // "do nothing". Requiring isNew keeps this scoped to the just-created
          // window, exactly like the name-fold guard in node-form.
          const nodeStillNew = !!nodeId && !!this.cy?.$('#' + nodeId)?.data('isNew');
          const foldIntoCreation = nodeStillNew && this.undoRedoManager.lastCommandIsCreationOf(nodeId!);

          // Check for actual style changes (not just presence of keys)
          const hasActualStyleChanges = Object.keys(beforeStyles).length > 0 &&
            Object.keys(beforeStyles).some(
              key => JSON.stringify(beforeStyles[key]) !== JSON.stringify(afterStyles[key])
            );

          // Create UpdateStyleCommand for undo/redo (only if there were actual changes)
          if (nodeId && hasActualStyleChanges && !foldIntoCreation) {
            const styleSnapshot: StyleChangeSnapshot = {
              elementId: nodeId,
              elementType: 'node',
              beforeStyles,
              afterStyles
            };
            const updateStyleCommand = new UpdateStyleCommand(
              this.cyService,
              this.nodeService,
              [styleSnapshot],
              undefined,
              this.mapStateService
            );
            this.undoRedoManager.pushCommand(updateStyleCommand);
          }

          // Create TextChangeCommand for name changes
          if (nodeId && beforeName !== undefined && $event.name !== beforeName && !foldIntoCreation) {
            const textCommand = new TextChangeCommand(
              this.cyService,
              this.nodeService,
              {
                entityId: nodeId,
                entityType: 'node',
                field: 'name',
                before: beforeName,
                after: $event.name ?? ''
              },
              // MAP-1547: reuse the active name focus session so this debounced
              // form save merges with the per-keystroke commands from
              // onInstantNameChange instead of fragmenting into its own entry
              // (mirrors the description handling below).
              this.focusSessionService.isSessionFor(nodeId, 'node', 'name')
                ? this.focusSessionService.getCurrentSessionId()!
                : `node-name-${nodeId}-${Date.now()}`
            );
            this.undoRedoManager.pushCommand(textCommand);
          }

          // Create TextChangeCommand for description changes
          if (nodeId && beforeDescription !== undefined && $event.description !== beforeDescription && !foldIntoCreation) {
            const textCommand = new TextChangeCommand(
              this.cyService,
              this.nodeService,
              {
                entityId: nodeId,
                entityType: 'node',
                field: 'description',
                before: beforeDescription,
                after: $event.description ?? ''
              },
              // Reuse the active focus session's id so repeated debounced saves
              // within one focus-to-blur edit merge into a single undo entry
              // instead of fragmenting on every pause (MAP-1547).
              this.focusSessionService.isSessionFor(nodeId, 'node', 'description')
                ? this.focusSessionService.getCurrentSessionId()!
                : `node-description-${nodeId}-${Date.now()}`
            );
            this.undoRedoManager.pushCommand(textCommand);
          }

          // Create PropertyCommand for metadata changes
          // Skip if actual style changes were made - metadata is always included in events but
          // we only want to track it when the user specifically edited metadata
          // Also skip if this node was part of a recent multi-selection metadata change
          try {
            const wasInMultiSelection = nodeId && lastMultiSelectionMetadataIds().has(nodeId);
            if (!hasActualStyleChanges && !undoRedoJustHappened() && !wasInMultiSelection && !foldIntoCreation && nodeId && beforeMetadata !== undefined && $event.metadata && Array.isArray($event.metadata)) {
              const afterMetadata = $event.metadata.map(m => ({...m}));

              // Sort by 'order' property to handle reordering from form processing
              const sortedBefore = orderBy(beforeMetadata, ['order'], ['asc']);
              const sortedAfter = orderBy(afterMetadata, ['order'], ['asc']);

              // Only create command if metadata actually changed (using deep equality)
              if (!isEqual(sortedBefore, sortedAfter)) {
                // Determine action type based on raw length change (including empty rows)
                let action: 'add' | 'remove' | 'update';
                let changedField: 'key' | 'value' | 'both' | 'row' | undefined;

                if (sortedAfter.length > sortedBefore.length) {
                  action = 'add';
                  changedField = 'row';
                } else if (sortedAfter.length < sortedBefore.length) {
                  action = 'remove';
                  changedField = 'row';
                } else {
                  action = 'update';
                  // Detect which field changed (key vs value)
                  // Find the first entry that differs and check what changed
                  for (let i = 0; i < sortedBefore.length; i++) {
                    const before = sortedBefore[i] as any;
                    const after = sortedAfter[i] as any;
                    if (!isEqual(before, after)) {
                      const keyChanged = before?.key !== after?.key;
                      const valueChanged = before?.value !== after?.value;
                      if (keyChanged && valueChanged) {
                        changedField = 'both';
                      } else if (keyChanged) {
                        changedField = 'key';
                      } else if (valueChanged) {
                        changedField = 'value';
                      }
                      break;
                    }
                  }
                }

                const propertyCommand = new PropertyCommand(
                  this.cyService,
                  this.nodeService,
                  {
                    entityId: nodeId,
                    entityType: 'node',
                    action,
                    beforeMetadata,
                    afterMetadata,
                    changedField
                  }
                );
                this.undoRedoManager.pushCommand(propertyCommand);
              }
            }
          } catch (e) {
            console.warn('Failed to create PropertyCommand for node metadata:', e);
          }
        })
      )
      .subscribe();
  }

  private isVisualNodeChange(payload: Partial<GraphNode>): boolean {
    const visualProps = [
      'color', 'type', 'weight', 'imageUrl', 'x', 'y',
      'properties'
    ];
    return Object.keys(payload).some(key => visualProps.includes(key));
  }

  hasMapPermission(permision: Permission) {
    return this.mapRoleService.hasCurrentUserPermission(permision);
  }

  private addNodeToParent(parent: cytoscape.NodeSingular, position?: { x: number, y: number }) {

    const parentBB = parent.boundingBox();
    const centerPositionFromParent = {
      x: parentBB.x1 + (parentBB.w / 2),
      y: parentBB.y1 + (parentBB.h / 2)
    };
    const node: Partial<GraphNode> = {
      name: '',
      parent: parent.data().id,
      weight: 1,
      type: 'ELLIPSE',
      x: position ? position.x : centerPositionFromParent.x,
      y: position ? position.y : centerPositionFromParent.y,
      map: this.currentMap,
      color: DefaultValues.node.color,
      properties: DefaultNodeProperties
    };
    this.nodeService.create(node as GraphNode).subscribe((res) => {
      if (res) {
        this.cy?.batch(() => {
          const newNode = this.cy?.add({
            // @ts-ignore
            group: 'nodes',
            // @ts-ignore
            data: {
              id: res.id,
              children: res.children,
              // @ts-ignore
              name: res.name,
              label: res.name,
              parent: res.parent,
              properties: res.properties,
              type: res.type,
              position: { x: res.x as number, y: res.y as number },
              color: res.color,
              weight: res.weight,
              isNew: true
            }
          });
          if (parent.data('collapsed')) {
            //@ts-ignore
            const api = this.cy.expandCollapse('get');
            parent.removeClass('collapsed');
            parent.addClass('expanded');
            parent.data('collapsed', false);
            api.expand(parent);
          }
          newNode?.position({ x: res.x as number, y: res.y as number });
          newNode?.move({ parent: parent.id() });

          // @ts-ignore
          this.cy.elements().unselect();
          // @ts-ignore
          this.resetAllSelected(this.cy);
          this.nodes = [];
          this.nodes.push(res);

          if (isEmpty(parent.data('children'))) {
            parent.data('children', [res]);
          }

          setTimeout(() => {
            newNode?.select();
            DashboardComponent.tappedNode = newNode?.data();

            // Create undo command for node creation within parent
            if (res.id && newNode) {
              const nodeSnapshot: GraphNodeSnapshot = {
                id: res.id,
                data: {...newNode.data(), id: res.id},
                position: {x: res.x as number, y: res.y as number},
                parentId: parent.id(),
                classes: newNode.classes()
              };
              const createNodeCommand = new CreateNodeCommand(
                  this.cyService,
                  this.nodeService,
                  nodeSnapshot
              );
              this.undoRedoManager.pushCommand(createNodeCommand);
            }
          }, 100);
        });
      }
    });
  }

  //TODO: Target for refactoring
  private renderCyElements(cy: Core,
                           res: GraphNode[],
                           refreshLayout = true,
                           paste = false) {
    if (!cy) return;
    if (!paste) {
      destroyPanZoom(cy);
    }
    const fadeInNodes = (complete: () => void) => {
      cy.nodes().animate({
        style: {
          opacity: (node: NodeSingular) => {
            const data = this.cyService.getNodeDataBasedOnState(node);
            // MAP-1017: was hardcoded 0.4, now the configurable filtering opacity.
            return data.excludedFromState ? SettingsService.sharedValues.filteringOpacity : 1;
          }
        }
      }, { duration: 300, complete });
    };
    //@ts-ignore
    if (res) {
      const doCyBatch = async () => {
        // First, load all nodes including children
        for (const rootNode of res) {
          await this.loadChildNodesRecursively(cy, rootNode);
        }

        // Verify all nodes are properly indexed by Cytoscape
        await this.waitForNodesIndexing(cy, res);
        await this.loadAllLinks(cy, res);


        cy.batch(() => {
          // Then move children to their parents
          for (const rootNode of res) {
            this.moveChildNodesToParentRecursively(cy, rootNode);
          }
        });
        // Finally, load all links with retry logic
        const isDefaultMapState = this.mapStateService.isDefaultState();
        const allNodes = cy.nodes();
        // When pasting, only process the newly added nodes, not all existing nodes
        const nodesToProcess = paste ?
          allNodes.filter(node => res.some(newNode => newNode.id === node.id())) :
          allNodes;
        // Pre-fetch collapse API once
        // @ts-ignore
        const expandCollapse = cy.expandCollapse ? cy.expandCollapse('get') : null;
        nodesToProcess.forEach((node: any) => {
          // Determine the desired position: from state or initial data
          let desiredPos = node.data('position');
          if (!isDefaultMapState && node.data('states')) {
            const currentStateId = this.mapStateService.getCurrentLocalState()?.id;
            const nodeState = node.data('states').find((s: any) => s.stateId === currentStateId);
            if (nodeState && nodeState.node.x !== undefined) {
              desiredPos = { x: nodeState.node.x, y: nodeState.node.y };
              node.data('position', desiredPos);
            }
          }
          // Apply collapse first, then enforce position to avoid plugin resetting
          // Determine collapsed state: from custom state or base state
          let isCollapsed = node.data('collapsed');
          if (!isDefaultMapState && node.data('states')) {
            const currentStateId = this.mapStateService.getCurrentLocalState()?.id;
            const nodeState = node.data('states').find((s: any) => s.stateId === currentStateId);
            if (nodeState && nodeState.node.collapsed !== undefined) {
              isCollapsed = nodeState.node.collapsed;
            }
          }
          // Use backend data instead of DOM state to avoid race condition
          const hasChildrenInData = node.data('children') && node.data('children').length > 0;
          if (isCollapsed && expandCollapse && hasChildrenInData) {
            expandCollapse.collapse(node);
            node.addClass('collapsed');
          }
          // Reapply desired position (state or initial)
          if (desiredPos) {
            node.position(desiredPos);
          }
        });
        // Endpoint centres only settle once parenting and positioning are done.
        const anchoredEdges = cy.edges();
        if (this.edgeEditingInstance && anchoredEdges.length > 0) {
          this.edgeEditingInstance.initAnchorPoints(anchoredEdges);
        }
        if (this.settingsService.linkViewMode !== LinkViewMode.OFF) {
          this.toggleEdgeVisibility(cy);
        }
        // "No links" never reaches toggleEdgeVisibility, and the nodes still
        // have to follow the edges the initial class pass just hid.
        this.applyNodeViewModeClasses();

        setTimeout(() => {
          const centerMostNode = this.cyService.getCenterMostNode(this.drawerWidth);
          if (centerMostNode) {
            const bb = centerMostNode.renderedBoundingBox({
              includeLabels: true,
              includeOverlays: true
            });
            const properties = centerMostNode.data('properties') || {};
            const textFontSize = parseFloat(properties.textFontSize ?? 12); // fallback if undefined
            const textMarginY = parseFloat(properties.textMarginY ?? 0);
            // Estimate label height in pixels
            const estimatedLabelHeight = textFontSize + textMarginY;
            const hasLabel = centerMostNode.data('name') && centerMostNode.data('name').length > 0;
            const width = bb.w;
            const height = bb.h;
            // Get the top-left of the Cytoscape container
            const containerRect = this.cy!.container()!.getBoundingClientRect();
            const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
            const scrollY = window.pageYOffset || document.documentElement.scrollTop;

            // Final anchor positions
            const anchorLeft = containerRect.left + scrollX + bb.x1;
            const anchorTop = containerRect.top + scrollY + bb.y1;
            const topLeft = this.cyService.getNodePagePosition(centerMostNode);
            const isExpandedParent = centerMostNode.data('collapsed') === false && centerMostNode.data('children')?.length > 0;
            if (topLeft) {
              document.documentElement.style.setProperty('--tour-anchor-left', `${isExpandedParent ? topLeft.left - 10 : anchorLeft + 10}px`);
              document.documentElement.style.setProperty('--tour-anchor-top', `${isExpandedParent ? anchorTop - estimatedLabelHeight * 2 : anchorTop - estimatedLabelHeight}px`);
              document.documentElement.style.setProperty('--tour-anchor-width', `${isExpandedParent ? width + 10 : width - 25}px`);
              document.documentElement.style.setProperty('--tour-anchor-height', `${isExpandedParent ? height + 10 : height - (hasLabel ? 50 : 25)}px`);
            }
          }
        }, 2000);

      };
      if (cy) {
        cy.batch(doCyBatch);
        setTimeout(() => {
          // Update styles if nodes exist
          if (cy.nodes().length > 0) {
            // Update styles and re-render
            cy.style().update();
            cy.forceRender();

            // For new maps, centering is handled in fadeInNodes callback to avoid double animation
          }

          // Reinitialize HTML edge labels if needed
          if (!this.cyService.linkLabelRenderer && cy.edges().length > 0) {
            this.cyService.init(cy);
          } else if (this.cyService.linkLabelRenderer && cy.edges().length > 0) {
            this.cyService.linkLabelRenderer.redraw();
          }
        }, 100);
      } else {
        setTimeout(() => {
          //@ts-ignore
          cy.batch(doCyBatch);
          setTimeout(() => {
            //@ts-ignore
            if (this.cyService.linkLabelRenderer && cy.edges().length > 0) {
              this.cyService.linkLabelRenderer.redraw();
            }
          }, 0);
        }, 300);
      }

      if (refreshLayout && cy) {
        this.applyLayout(cy);
      }
    }

    // For new maps, set initial zoom to target level so centerOnElements only pans (no zoom animation)
    //@ts-ignore
    const isNewMap = this.currentMap?.isNew || this.route.snapshot?.queryParams['isNew'] === 'true';
    if (isNewMap && !paste) {
      cy.zoom(this.cyService.ZOOM_PERCENTAGE_NODE);
    }
    drawGradientNode(cy, this.mapStateService);
    this.isLoading = false;
    this.initialLoadComplete = true;
    if (!paste) {
      storePanZoomDynamic(cy);
    }
    this.viewService.viewHasLoaded.next(true);
    if (!paste) {
      let isRestoring = false;
      //@ts-ignore
      const isNew = this.currentMap?.isNew || this.route.snapshot?.queryParams['isNew'] === 'true';

      // Restore pan/zoom BEFORE fade animation to prevent visual jump
      if (!isNew) {
        isRestoring = restorePanZoomDynamic(cy, false);
      }
      // Capture global state NOW, before fadeInNodes delay
      const wasRestoringGlobally = isRestoringPanZoom();

      fadeInNodes(() => {
        if (!isRestoring && !wasRestoringGlobally) {
          if (isNew) {
            // Center on the default node with same zoom as double-tap
            const firstNode = this.cy?.nodes().first();
            if (firstNode && firstNode.length > 0) {
              this.cyService.centerOnElements(this.cy!, firstNode, true, this.cyService.ZOOM_PERCENTAGE_NODE);
              // Delay setup until after 500ms centering animation completes
              setTimeout(() => {
                this.clearIsNewQueryParam();
                const setupNewNode = () => {
                  if (!this.cy) return;
                  const newNode = this.cy.nodes().first();
                  if (newNode) {
                    this.sidebarService.enableEditMode();
                    // if sidebar is closed (drawer) open it
                    if (!this.isSideBarOpen) {
                      this.settingsService.toggleSidebar({forceOpen: true});
                    }
                    newNode.select();
                    newNode.data('isNew', true);
                    this.tapElement(newNode.data(), true, this.cy);
                    setTimeout(() => {
                      document.getElementById('titleInput')?.focus();
                    }, 1300);
                  }
                };

                setupNewNode();

                // Use interval to ensure setup completes successfully
                const setupInterval = setInterval(() => {
                  if (!this.cy) return;
                  const titleInput = document.getElementById('titleInput');
                  const selectedNodes = this.cy.$(':selected');
                  const selectedNode = selectedNodes.length === 1 ? selectedNodes[0] : null;

                  if (DashboardComponent.tappedNode &&
                    selectedNode &&
                    selectedNode.isNode() &&
                    selectedNode.id() === DashboardComponent.tappedNode.id &&
                    titleInput &&
                    document.activeElement === titleInput) {
                    clearInterval(setupInterval);
                  } else {
                    setupNewNode();
                  }
                }, 300);
              }, 550);
            } else {
              this.centerCanvas();
              this.clearIsNewQueryParam();
            }
          } else {
            this.centerCanvas();
          }
          isRestoring = true;
        }
        // Long enough to cover the slower of the two branches above (the
        // centerCanvas() pan/zoom animation is 500ms; the async pan/zoom
        // restore settles well under that), short enough not to keep the
        // loading animation up after the smap is actually ready to show.
        setTimeout(() => {
          this.appWideStateService.setCanvasLoaded(true);
        }, 600);
      });
    }

    setTimeout(() => {
      this.appWideStateService.canvasLoadedSignal.set(true);
      this.appWideStateService.setCanvasLoaded(true);
    }, 10500);
  }


  private initAnchors(edges: EdgeSingular[], type: string) {
    this.cyService.initAnchors(edges, type);
  }

  private async loadChildNodesRecursively(cy: cytoscape.Core, rootNode: GraphNode) {
    if (!rootNode) return;
    // Check if cytoscape already has the element
    const nodeExists = cy.$(`#${rootNode.id}`).length > 0;
    if (nodeExists) {
      // If node exists but we have children to process, still recurse
      if (rootNode.children && rootNode.children.length > 0) {
        for (const child of rootNode.children) {
          await this.loadChildNodesRecursively(cy, child);
        }
      }
      return;
    }
    const newNode = this.addCyNode(rootNode, null);
    const a = cy.add({
      group: 'nodes',
      data: {
        isSaved: true,
        ...newNode
      },
      classes: 'expanded',
      position: {x: rootNode?.x ?? 0, y: rootNode?.y ?? 0}
    });

    if (rootNode.children && rootNode.children.length > 0) {
      for (const child of rootNode.children) {
        await this.loadChildNodesRecursively(cy, child);
      }
    }

  }

  private clearIsNewQueryParam() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { isNew: null },
      queryParamsHandling: 'merge'
    });
  }

  private moveChildNodesToParentRecursively(cy: cytoscape.Core, rootNode: GraphNode) {
    if (!rootNode.children) return;
    rootNode.children.forEach((child: GraphNode) => {
      //@ts-ignore
      cy.getElementById(child.id).move({ parent: rootNode.id });
      if (child.children && child.children.length > 0) {
        this.moveChildNodesToParentRecursively(cy, child);
      }
    });
  }

  private async waitForNodesIndexing(cy: cytoscape.Core, nodes: GraphNode[]): Promise<void> {
    // Collect all node IDs that should exist
    const allNodeIds: string[] = [];

    function collectNodeIds(node: GraphNode) {
      if (node.id) {
        allNodeIds.push(node.id);
      }
      if (node.children && node.children.length > 0) {
        node.children.forEach(child => collectNodeIds(child));
      }
    }

    nodes.forEach(node => collectNodeIds(node));

    // Wait for all nodes to be properly indexed
    let attempts = 0;
    const maxAttempts = 10;

    while (attempts < maxAttempts) {
      const missingNodes = allNodeIds.filter(id => cy.$(`#${id}`).length === 0);

      if (missingNodes.length === 0) {
        // All nodes are indexed
        return;
      }

      // Wait a bit more and try again
      await new Promise(resolve => setTimeout(resolve, 50));
      attempts++;
    }

    // Log warning if some nodes are still missing after all attempts
    const finalMissingNodes = allNodeIds.filter(id => cy.$(`#${id}`).length === 0);
    if (finalMissingNodes.length > 0) {
      console.warn(`Some nodes could not be indexed after ${maxAttempts} attempts:`, finalMissingNodes);
    }
  }

  private refreshLinkRendering(res: GraphNodeLink, shouldRebuild = false) {
    this.cy?.batch(() => {
      if (shouldRebuild) {
        this.cy?.remove('#' + res.id);
        const t = this.addCyLink(this.cy!, res);
        if (t) {
          this.edgeEditingInstance.initAnchorPoints([t]);
        }
        return;
      }
      const edge = this.cy?.$('#' + res.id);
      edge?.data('name', res.name);
      edge?.data('description', res.description);
      edge?.data('label', res.label);
      edge?.data('color', res.color);
      edge?.data('weight', res.weight);
      edge?.data('type', res.type);
      edge?.data('edgeArrowType', res.edgeArrowType);
      edge?.data('hasComments', res.hasComments);
      edge?.data('textFontSize', res.textFontSize);
      edge?.data('direction', res.direction);
      edge?.data('excludedFromState', res.excludedFromState);
      edge?.data('labelSlope', res.labelSlope);
      edge?.data('linkLabelPosition', res.linkLabelPosition);
      edge?.data('linkLabelOffset', res.linkLabelOffset);
      // MAP-1547: propagate the saved sound URL back onto the cy edge. Without
      // this the value never round-trips into cy/the form, so it reads back as
      // empty after reselect (appearing "not saved") and a later save wipes it.
      edge?.data('soundUrl', res.soundUrl);
      if (res.states) {
        edge?.data('states', res.states);
      }
      edge?.data('shape', res.shape?.toLowerCase());
      edge?.data('metadata', res.metadata);
      if (edge?.data('type') !== 'BEZIER') {
        // Don't overwrite anchor points if they're being initialized
        if (!edge?.hasClass('anchor-init-in-progress')) {
          // Only update anchor points if backend has data or canvas has no data
          const currentControlPoints = edge?.data('controlPointPositions');
          const currentBendPoints = edge?.data('bendPointPositions');

          if ((res.controlPointPositions && res.controlPointPositions.length > 0) || !currentControlPoints?.length) {
            edge?.data('controlPointPositions', res.controlPointPositions);
          }
          if ((res.bendPointPositions && res.bendPointPositions.length > 0) || !currentBendPoints?.length) {
            edge?.data('bendPointPositions', res.bendPointPositions);
          }
        }
      }

      // Update tappedLink with state-specific data if this is the currently selected link
      if (res.id === DashboardComponent.tappedLink?.id) {
        setTimeout(() => {
          const currentStateId = this.mapStateService.getCurrentLocalState()?.id;
          const isDefaultState = currentStateId === undefined;
          const edgeData = edge?.data();
          if (!isDefaultState && edgeData?.states) {
            const stateLink = edgeData.states.find((s: LinkStateData) => s.stateId === currentStateId)?.link;
            if (stateLink) {
              // Include the full states array on the state-specific link so updates can preserve all states
              DashboardComponent.tappedLink = {...stateLink, states: edgeData.states};
            } else {
              DashboardComponent.tappedLink = edgeData;
            }
          } else {
            DashboardComponent.tappedLink = edgeData;
          }
        }, 100);
      }
    });

    // Force style update to apply changes immediately
    this.cy?.style().update();
  }

  private refreshNodeRendering(res: GraphNode) {
    if (!res) return;
    this.cy?.batch(() => {
      const node = this.cy?.$('#' + res.id);
      // @ts-ignore
      node?.data('name', res.name);
      node?.data('description', res.description);
      node?.data('collapsed', res.collapsed);
      node?.data('color', res.color);
      node?.data('label', res.name);
      node?.data('imageUrl', res.imageUrl);
      node?.data('metadata', res.metadata);
      node?.data('hasComments', res.hasComments);
      node?.data('properties', res.properties);
      node?.data('excludedFromState', res.excludedFromState);
      if (res.states) {
        node?.data('states', res.states);
      }
      node?.data('type', res.type);
      node?.data('weight', res.weight);
      const nodeIndex = this.nodes.findIndex((n) => n.id === res.id);
      this.nodes[nodeIndex] = res;

      // Update tappedNode with state-specific data if this is the currently selected node
      if (res.id === DashboardComponent.tappedNode?.id) {
        setTimeout(() => {
          const currentStateId = this.mapStateService.getCurrentLocalState()?.id;
          const isDefaultState = currentStateId === undefined;
          const nodeData = node?.data();
          if (!isDefaultState && nodeData?.states) {
            const stateNode = nodeData.states.find((s: NodeStateData) => s.stateId === currentStateId)?.node;
            if (stateNode) {
              // Include the full states array on the state-specific node so updates can preserve all states
              DashboardComponent.tappedNode = {...stateNode, states: nodeData.states};
            } else {
              DashboardComponent.tappedNode = nodeData;
            }
          } else {
            DashboardComponent.tappedNode = nodeData;
          }
        }, 100);
      }
    });

    // Force style update to apply changes immediately (matches refreshLinkRendering)
    this.cy?.style().update();
  }




  private addCyNode(c: GraphNode, p: GraphNode | null): NodeDataDefinition {
    return {
      id: c.id,
      label: c.name,
      type: c.type,
      parent: p?.id || undefined,
      name: c.name,
      description: c.description,
      weight: c.weight,
      links: c.links,
      excludedFromState: c.excludedFromState,
      metadata: c.metadata,
      imageUrl: c.imageUrl,
      properties: c.properties,
      states: c.states,
      collapsed: c.collapsed,
      children: c.children,
      hasComments: c.hasComments,
      x: c?.x ?? 0,
      y: c?.y ?? 0,
      position: {x: c?.x ?? 0, y: c?.y ?? 0},
      color: c?.color || '#696969',
      touched: c?.x !== 0 || c?.y !== 0,
      aiGeneratedDescription: c.aiGeneratedDescription
    };
  }

  private applyLayout(cy: cytoscape.Core, type: LayoutTypes = LayoutTypes.PRESET) {
    switch (type) {
      case LayoutTypes.PRESET:
        this.applyPresetLayout(cy);
        break;
      case LayoutTypes.CONCENTRIC:
        this.applyConcentricLayout(cy);
        break;
      case LayoutTypes.COSE_BILKENT:
        this.applyCoseBilkentLayout(cy);
        break;
    }
    // @ts-ignore
    this.cyLayout.run();
  }

  private applyPresetLayout(cy: cytoscape.Core) {
    if (!cy.layout) {
      return;
    }
    this.cyLayout = cy.layout({
      name: 'preset', positions: (e: any) => {
        return {
          x: e.data('position').x,
          y: e.data('position').y
        };
      }
    });
  }

  private cleanupCurrentMap() {
    if (!this.cy) return;

    // Cancel any pending loads by setting isLoading to false
    this.isLoading = false;

    // Clear selections
    this.cy.nodes().unselect();
    this.cy.edges().unselect();

    // Remove all elements in batch for performance
    this.cy.batch(() => {
      this.cy?.elements().remove();
    });

    // Clear any cached link label signals in cyService
    this.cyService.clearLinkLabelSignals();

    // Force a style update to clear any cached state
    this.cy.style().update();

    // Clear any forced visibility styles
    this.cy.nodes().removeStyle('display');
    this.cy.nodes().removeStyle('opacity');
    this.cy.nodes().removeStyle('visibility');
    this.cy.edges().removeStyle('display');
    this.cy.edges().removeStyle('opacity');
    this.cy.edges().removeStyle('visibility');
  }

  //TODO: Does not fire up on route change
  private listenToMapChanges(cy: cytoscape.Core, eh: any, map: GraphMap | undefined) {
    // Don't call savePanZoomState() here - localStorage already has the NEW map
    // so it would save the OLD map's pan/zoom to the NEW map's ID
    // The pan/zoom listener will save during normal operation with the correct map

    // Destroy the listener - renderCyElements will handle re-initialization and restoration
    destroyPanZoom(cy);

    // Check if we're truly switching to the same, fully loaded map
    const nodeCount = this.cy?.nodes()?.length || 0;
    const isSameMap = this.currentMap?.id === map?.id;
    const isMapFullyLoaded = !this.isLoading && nodeCount > 0;

    // Only skip if it's the same map, fully loaded, and not in "no map" state
    if (!this.hasNoMap && isSameMap && isMapFullyLoaded) {
      return;
    }

    // Always perform cleanup when switching maps or reloading
    this.cleanupCurrentMap();
    this.initialLoadComplete = false;

    this.currentMap = map;
    this.isReadOnly = map?.isReadonly || false;
    this.selectedNodes = [];
    this.selectedLinks = [];
    this.initButtPressService(cy);

    this.cy?.autolock(this.packageService.isDefaulter() || ((this.currentMap?.isReadonly || isInIframe()) && !this.hasMapPermission('edit')));
    this.hasNoMap = this.mapService.hasNoStoredMap();
    if (this.contextMenuInstance) {
      this.contextMenuInstance.destroy();
    }
    if (!this.hasNoMap && cy) {
      this.dashboardService.initCtxMenu2(cy, eh, {
        hasNoMap: this.hasNoMap,
        currentMap: this.currentMap,
        hasMapPermission: this.hasMapPermission.bind(this),
        selectedNodes: this.selectedNodes,
        selectedLinks: this.selectedLinks,
        centerCanvas: this.centerCanvas.bind(this),
        centerOnTarget: this.centerOnTarget.bind(this),
        updateNode: this.updateNode.bind(this),
        resetAllSelected: this.resetAllSelected.bind(this),
        addNodeToParent: this.addNodeToParent.bind(this),
        moveNodeOneLevelUp: this.moveNodeOneLevelUp.bind(this),
        removeNodeFromParent: this.removeNodeFromParent.bind(this),
        addCyLink: this.addCyLink.bind(this),
        initAnchors: this.initAnchors.bind(this),
        createFromMenu: this.createFromMenu,
        cy: this.cy!,
        isUnlinking: this.isUnlinking,
        isChangingLinkSource: this.isChangingLinkSource,
        hasNodesNoNodes: this.hasNodesNoNodes,
        contextMenuInstance: this.contextMenuInstance,
        menuSubscription: this.menuSubscription,
        undoRedoSubscription: this.undoRedoSubscription,
        edgeEditingInstance: this.edgeEditingInstance,
        undoRedoManager: this.undoRedoManager,
        settingsService: this.settingsService,
        appWideStateService: this.appWideStateService,
        DashboardComponent: DashboardComponent,
        componentInstance: this
      }).then();
    }

    // Load new map after cleanup
    requestAnimationFrame(() => {
      this.loadCyElements(cy);
    });

    // @ts-ignore
    if (map?.isNew) {
      const localSettings = this.settingsService.getLocalStorageSettings();
      localSettings.isEditMode = true;
      this.sidebarService.enableEditMode();
      this.settingsService.setLocalStorageSettings(localSettings);
      if (!localSettings.isSidebarOpen) {
        this.settingsService.toggleSidebar();
      }
      setTimeout(() => {
        const node = cy.nodes().first();
        if (node) {
          node.data('isNew', true);
          node.select();
          this.tapElement(node.data(), true, cy);
        }
        //@ts-ignore
        map.isNew = false;
        this.mapService.setCurrentSelectedMapToStore(map);
      }, 1000);
    }

  }

  private updateElementsCommentsFlag() {
    this.hasCommentsPollingSub.unsubscribe();
    this.hasCommentsPollingSub = new Subscription();
    this.hasCommentsPollingSub.add(
      timer(0, 6 * 10 * 1000)
        .pipe(
          switchMap(() => this.nodeService.getElementHasComments(this.currentMap!.id))
        )
        .subscribe((res) => {
          if (!this.cy) return;
          this.cy.batch(() => {
            res.elements.forEach((element) => {
              if (!this.cy) return;
              const cyElement = this.cy.$(`#${element.id}`);
              cyElement.data('hasComments', element.hasComments);
            });
          });
        })
    );
  }

  private initDrawMode(cy: cytoscape.Core) {
    // the default values of each option are outlined below:
    const defaults = {
      canConnect: function (
        sourceNode: { same: (arg0: any) => any },
        targetNode: any,
      ) {
        // whether an edge can be created between source and target
        return !sourceNode.same(targetNode); // e.g. disallow loops
      },
      edgeParams: function (sourceNode: any, targetNode: any, i: any) {
        // for edges between the specified source and target
        // return element object to be passed to cy.add() for edge
        return {};
      },
      handleColor: '#000000', // the colour of the edge handle
      previewColor: '#000000', // the colour of the handle's preview
      hoverDelay: 50, // time spent hovering over a target node before it is considered selected
      snap: false, // when enabled, the edge can be drawn by just moving close to a target node (can be confusing on compound graphs)
      snapThreshold: 1, // the target node must be less than or equal to this many pixels away from the cursor/finger
      snapFrequency: 30, // the number of times per second (Hz) that snap checks done (lower is less expensive)
      noEdgeEventsInDraw: true, // set events:no to edges during draws, prevents mouseouts on compounds
      disableBrowserGestures: true, // during an edge drawing gesture, disable browser gestures such as two-finger trackpad swipe and pinch-to-zoom
    };

    // @ts-ignore
    return cy['edgehandles'](defaults);
  }

  private applyConcentricLayout(cy: cytoscape.Core) {
    this.cyLayout = cy.layout({
      name: 'concentric', fit: true,
      animate: true,
      avoidOverlap: true,
      levelWidth: () => {
        return 1;
      }
    });
  }

  private applyCoseBilkentLayout(cy: cytoscape.Core) {
    this.cyLayout = cy.layout({
      name: 'cose-bilkent',
      // @ts-ignore - cose-bilkent options
      idealEdgeLength: 150,
      nodeRepulsion: 6500,
      edgeElasticity: 0.45,
      nestingFactor: 0.1,
      gravity: 0.2,
      numIter: 2500,
      tile: true,
      animate: true,
      animationDuration: 800,
      fit: true,
      padding: 50,
    });
  }


  private loadAllLinks(cy: Core, rootNode: GraphNode[], linksToDraw?: GraphNodeLink[]): Promise<void> {
    return new Promise<void>((resolve) => {
      const links: any[] = linksToDraw || [];

      function getAllLinks(node: any) {
        let allLinks = [...node.links];

        if (node.children && node.children.length > 0) {
          for (const child of node.children) {
            allLinks = allLinks.concat(getAllLinks(child));
          }
        }

        return allLinks;
      }

      // If we have provided linksToDraw, use them, otherwise get all links from the root node
      if (!linksToDraw) {
        rootNode.forEach((e: any) => {
          links.push(getAllLinks(e));
        });
      }

      const failedLinks: GraphNodeLink[] = [];

      // Process all links synchronously - cy.batch() handles performance optimization
      cy.batch(() => {
        const allLinks = linksToDraw ? links : ([] as GraphNodeLink[]).concat(...links);

        allLinks.forEach((link: GraphNodeLink) => {
          const result = this.addCyLink(cy, link);
          if (!result) {
            failedLinks.push(link);
          }
        });
      });

      // Retry failed links with multiple attempts
      if (failedLinks.length > 0) {
        let retryCount = 0;
        const maxRetries = 3;

        const retryFailedLinks = () => {
          if (retryCount >= maxRetries || failedLinks.length === 0) {
            // Initialize anchor points after all links are added
            const allEdges = cy.edges();
            if (this.edgeEditingInstance && allEdges.length > 0) {
              this.edgeEditingInstance.initAnchorPoints(allEdges);
            }

            if (failedLinks.length > 0) {
              console.warn(`Could not add ${failedLinks.length} links after ${maxRetries} retries:`, failedLinks);
            }
            return;
          }

          retryCount++;
          const stillFailedLinks: GraphNodeLink[] = [];

          cy.batch(() => {
            failedLinks.forEach((link: GraphNodeLink) => {
              const result = this.addCyLink(cy, link);
              if (!result) {
                stillFailedLinks.push(link);
              }
            });
          });

          failedLinks.length = 0;
          failedLinks.push(...stillFailedLinks);

          if (failedLinks.length > 0) {
            setTimeout(retryFailedLinks, 100 * retryCount);
          } else {
            // All links added successfully
            const allEdges = cy.edges();
            if (this.edgeEditingInstance && allEdges.length > 0) {
              this.edgeEditingInstance.initAnchorPoints(allEdges);
            }
          }
        };

        setTimeout(retryFailedLinks, 100);
      } else {
        // No failed links, initialize anchor points
        const allEdges = cy.edges();
        if (this.edgeEditingInstance && allEdges.length > 0) {
          this.edgeEditingInstance.initAnchorPoints(allEdges);
        }
      }

      // CRITICAL FIX: Update link labels after edges are added to the graph
      // The linkLabelRenderer is initialized before edges exist, so we need to explicitly
      // trigger label creation after cy.batch() completes. Use setTimeout to ensure
      // the batch operation has fully rendered before updating labels.
      setTimeout(() => {
        if (this.cyService.linkLabelRenderer && cy.edges().length > 0) {
          this.cyService.linkLabelRenderer.redraw();
        }
        resolve();
      }, 0);

    });
  }

  private updateLinksPointPositionsData($event: Partial<GraphNodeLink>) {
    if (this.edgeEditingInstance && this.cy) {
      const canvasLink = this.cy.$('#' + $event.id);
      const positions = this.edgeEditingInstance.getAnchorsAsArray(canvasLink);
      const type = this.edgeEditingInstance.getEdgeType(canvasLink) as 'bend' | 'control';

      if (!positions) return $event;
      if (type === 'bend') {
        set($event, 'bendPointPositions', mapAnchorsToXYArray(positions));
      } else if (type === 'control') {
        set($event, 'controlPointPositions', mapAnchorsToXYArray(positions));
      }
    }
    return $event;
  }

  private hasAnyAnchorPoints(link: GraphNodeLink) {
    const hasControlPointsOrWeightsOrDistances = !isEmpty(link?.controlPointPositions) || !isEmpty(link?.cyedgecontroleditingWeights) || !isEmpty(link?.cyedgecontroleditingDistances);
    const hasBendPointsOrWeightsOrDistances = !isEmpty(link?.bendPointPositions) || !isEmpty(link?.cyedgebendeditingWeights) || !isEmpty(link?.cyedgebendeditingDistances);
    return hasControlPointsOrWeightsOrDistances || hasBendPointsOrWeightsOrDistances;
  }

  multiReset($event: { nodeIds: string[], linkIds: string[] }) {
    const currentStateId = this.mapStateService.getCurrentLocalState()?.id;
    if (currentStateId === undefined) {
      return;
    }
    this.nodeService.multiReset({
      nodeIds: $event.nodeIds,
      linkIds: $event.linkIds,
      stateId: currentStateId
    })
      .subscribe((res) => {
        this.selectedLinks = [];
        this.selectedNodes = [];
        this.selectedLinks = res.links;
        this.selectedNodes = res.nodes;

        this.refreshNodes(res.nodes);
        this.refreshLinks(res.links);
      });
  }

  private processNodeSelection(event: any, cy: cytoscape.Core) {
    const currentState = this.mapStateService.getCurrentLocalState();
    const nodeData = event.target.data() as GraphNode;
    let dataToPush;
    this.isDetailsOpen = true;
    dataToPush = nodeData;

    if (currentState?.id) {
      const nodesStateBasedOnSelectedState = nodeData.states?.find((s: NodeStateData) => s.stateId === currentState.id);
      if (nodesStateBasedOnSelectedState) {
        const statedNode = { ...nodesStateBasedOnSelectedState.node } as any;
        const originalNode = {
          id: nodeData.id,
          states: nodeData.states
        };
        statedNode.originalNode = originalNode as unknown as StateLessNode;
        statedNode.states = nodeData.states;
        dataToPush = statedNode;
      }
    }
    this.selectedNodes.push(dataToPush);
    event.target.data('selected', true);
    this.selectedLinks = [];
    this.toggleEdgeVisibility(cy, true);

    const selectedElements = cy.$(':selected');
    if (selectedElements.length > 1) {
      selectedElements.forEach((e: any) => e.addClass('multi'));
    }
  }

  private handleElementSelection(cy: cytoscape.Core) {
    // Selecting a link has to undim its endpoints, and only the node handlers
    // below reach toggleEdgeVisibility.
    cy.on('select unselect', 'node, edge', () => this.scheduleNodeViewModeClasses());

    cy.on('select', 'node, edge', () => {
      // When any Cytoscape element selected, deselect overlay elements
      if (this.overlayManager.getSelectedElement()) {
        DashboardComponent.tappedOverlayElement = null;
        this.overlayManager.deselectAll();
      }
    });

    cy.on('select', 'node', async (event: any) => {
      // Defer heavy select operations during animations to prevent frame drops
      if (ExpandCollapseInProgress() || centeringInProgress()) {
        setTimeout(() => {
          // Re-trigger the select event after animation completes
          if (event.target.selected()) {
            this.processNodeSelection(event, cy);
          }
        }, 100);
        return;
      }

      this.processNodeSelection(event, cy);
    });
    cy.on('select', 'edge', (event: any) => {
      const edge = event.target;
      if (edge.hasClass('hidden')) {
        return;
      }
      const edgeData = edge.data();
      const currentState = this.mapStateService.getCurrentLocalState();
      edge.addClass('highlighted');
      this.isDetailsOpen = true;
      let dataToPush;
      dataToPush = edgeData;

      if (currentState?.id) {
        const edgesStateBasedOnSelectedState = edgeData.states?.find((s: LinkStateData) => s.stateId === currentState.id);
        if (edgesStateBasedOnSelectedState) {
          const statedLink = { ...edgesStateBasedOnSelectedState.link } as any;
          const originalLink = {
            id: edgeData.id,
            states: edgeData.states
          };
          statedLink.origin = originalLink as unknown as StateLessLink;
          statedLink.states = edgeData.states;
          dataToPush = statedLink;
        }
      }

      this.selectedLinks.push(dataToPush);
      this.selectedNodes = [];
      const selectedElements = cy.$(':selected');
      if (selectedElements.length > 1) {
        selectedElements.forEach((e: any) => e.addClass('multi'));
      }
    });

    cy.on('unselect', 'node', (event: any) => {
      // Skip processing if this is a manual deselection
      if (event.target.hasClass('manual-deselect')) {
        return;
      }

      if (DashboardComponent.multiSelectedElements.length > 0) {
        DashboardComponent.multiSelectedElements.forEach((n) => {
          const element = cy.$('#' + n.id);
          if (element.hasClass('force-unselect')) {
            element.unselect();
            element.removeClass('force-unselect');
            element.removeClass('highlighted');
            element.removeData('selected');

            DashboardComponent.multiSelectedElements.splice(DashboardComponent.multiSelectedElements.indexOf(n), 1);
          } else {
            element.select();
          }
        });
        return;
      }

      const target = event.target;
      const nodeData = target.data() as GraphNode;

      // Stop node sound effect
      if (nodeData.id) {
        this.audioService.stopElementSound(nodeData.id);
      }

      this.selectedNodes = [];
      this.selectedLinks = [];
      event.target.removeClass('highlighted');
      this.toggleEdgeVisibility(cy, true);
      if (target.data('savedViewSelected')) {
        target.data('isNew', false);
      }
      target.removeData('selected');
    });

    cy.on('unselect', 'edge', (event: any) => {
      // Skip processing if this is a manual deselection
      if (event.target.hasClass('manual-deselect')) {
        return;
      }
      if (DashboardComponent.multiSelectedElements.length > 0) {
        DashboardComponent.multiSelectedElements.forEach((n) => {
          const element = cy.$('#' + n.id);
          if (element.hasClass('force-unselect')) {
            element.unselect();
            element.removeClass('force-unselect');
            element.removeClass('highlighted');
            element.removeData('selected');

            DashboardComponent.multiSelectedElements.splice(DashboardComponent.multiSelectedElements.indexOf(n), 1);
          } else {
            element.select();
          }
        });
        return;
      }
      const target = event.target;
      const edgeData = target.data() as GraphNodeLink;

      // Stop edge sound effect
      if (edgeData.id) {
        this.audioService.stopElementSound(edgeData.id);
      }

      this.selectedNodes = [];
      this.selectedLinks = [];
      target.removeClass('highlighted');
      this.toggleEdgeVisibility(cy, true);
      if (target.data('isNew') || target.data('savedViewSelected')) {
        target.data('isNew', false);
      }
      target.removeData('selected');
    });
  }

  private refreshLinks(links: GraphNodeLink[]) {
    const cyLinks: EdgeSingular[] = [];
    links.forEach((link) => {
      this.cy?.batch(() => {
        const cyLink = this.cy?.$('#' + link.id);

        // Get current canvas anchor point data to preserve if backend has no data
        const currentControlPoints = cyLink?.data('controlPointPositions');
        const currentBendPoints = cyLink?.data('bendPointPositions');

        // Only update anchor points if backend has data OR canvas has no data
        // This preserves link-specific anchor points that may not be in the backend response yet
        const controlPointsToUse = (link.controlPointPositions && link.controlPointPositions.length > 0)
          ? link.controlPointPositions
          : currentControlPoints;
        const bendPointsToUse = (link.bendPointPositions && link.bendPointPositions.length > 0)
          ? link.bendPointPositions
          : currentBendPoints;

        cyLink?.data({
          source: link.from?.id || link.from,
          targetEndpoint: link.targetEndpoint,
          sourceEndpoint: link.sourceEndpoint,
          label: link.label || DefaultValues.link.label,
          name: link.name || '',
          ...(link.description !== undefined ? {description: link.description} : {}),
          target: link.to?.id || link.to,
          color: link?.color || DefaultValues.link.color,
          type: link.type,
          textFontSize: link.textFontSize,
          shape: link.shape || DefaultValues.link.shape,
          excludedFromState: link.excludedFromState,
          direction: link.direction || DefaultValues.link.direction,
          weight: link.weight,
          metadata: link.metadata,
          states: link.states,
          id: link.id,
          controlPointPositions: controlPointsToUse,
          bendPointPositions: bendPointsToUse,
          edgeArrowType: link.edgeArrowType || DefaultValues.link.edgeArrowType,
          labelSlope: link.labelSlope || DefaultValues.link.labelSlope,
          linkLabelPosition: link.linkLabelPosition || 0,
          linkLabelOffset: link.linkLabelOffset ?? DefaultValues.link.linkLabelOffset,
          // MAP-1547: carry the sound URL onto the cy edge on (re)load so it is
          // shown in the form and preserved by subsequent saves.
          soundUrl: link.soundUrl,
        });
        if (cyLink) {
          cyLinks.push(cyLink);
        }

        //@ts-ignore
        const isMultiSelection = this.cy?.$(':selected').length > 1;
        if (link.id === DashboardComponent.tappedLink?.id && !isMultiSelection) {
          setTimeout(() => {
            const statedLink = this.cyService.getLinkDataBasedOnState(cyLink as EdgeSingular);
            DashboardComponent.tappedLink = statedLink;
            cyLink?.select();
          }, 100);
        }
      });
    });

    // Force style update to ensure visual changes are applied immediately
    if (this.cy && cyLinks.length > 0) {
      this.cy.style().update();
    }

    setTimeout(() => {
      Logger.log('Refreshing links , sending update in progress - false');
      updateInProgress.set(false);
    }, 5000);
  }

  private applyOptimisticMultiUpdate(payload: any) {
    if (!this.cy) return;

    // Apply node changes by creating updated nodes and calling refreshNodeRendering
    if (payload.nodeIds?.length > 0 && payload.diff?.node) {
      const nodeDiff = payload.diff.node;

      // Apply style changes directly to Cytoscape in a single batch
      // without going through refreshNodeRendering (which overwrites name/label data)
      this.cy?.batch(() => {
        payload.nodeIds.forEach((nodeId: string) => {
          const cyNode = this.cy?.$('#' + nodeId);
          if (!cyNode) return;

          // Only update the changed properties, never touch name/description/label
          if (nodeDiff.color) cyNode.data('color', nodeDiff.color);
          if (nodeDiff.weight) cyNode.data('weight', nodeDiff.weight);
          if (nodeDiff.type) cyNode.data('type', nodeDiff.type);
          if (nodeDiff.imageUrl !== undefined) cyNode.data('imageUrl', nodeDiff.imageUrl);
          if (nodeDiff.excludedFromState !== undefined) cyNode.data('excludedFromState', nodeDiff.excludedFromState);
          if (nodeDiff.properties) {
            const merged = {...cyNode.data('properties'), ...nodeDiff.properties};
            cyNode.data('properties', merged);
          }
        });
      });
      this.cy?.style().update();
    }

    // Apply link changes optimistically
    if (payload.linkIds?.length > 0 && payload.diff?.link) {
      const linkDiff = payload.diff.link;
      payload.linkIds.forEach((linkId: string) => {
        const cyLink = this.cy?.$('#' + linkId);
        if (cyLink) {
          const currentData = cyLink.data();
          const updatedData = {
            ...currentData,
            ...linkDiff,
            properties: {
              ...currentData.properties,
              ...linkDiff.properties
            }
          };

          this.cy?.batch(() => {
            cyLink.data(updatedData);
          });
        }
      });
    }
  }

  private refreshNodes(nodes: GraphNode[]) {
    nodes.forEach((node: any) => {
      this.cy?.batch(() => {
        const cyNode = this.cy?.$('#' + node.id);
        cyNode?.data({
          name: node.name,
          ...(node.description !== undefined ? {description: node.description} : {}),
          collapsed: node.collapsed,
          color: node.color,
          label: node.name,
          imageUrl: node.imageUrl,
          metadata: node.metadata,
          properties: node.properties,
          excludedFromState: node.excludedFromState,
          states: node.states,
          type: node.type,
          weight: node.weight
        });

        if (node.id === DashboardComponent.tappedNode?.id) {
          setTimeout(() => {
            const statedNode = this.cyService.getNodeDataBasedOnState(cyNode as NodeSingular);
            DashboardComponent.tappedNode = statedNode;
            cyNode?.select();
          }, 100);
        }
      });
    });

    // Force style update to apply changes immediately (matches refreshLinks)
    this.cy?.style().update();
  }

  private addCyLink(cy: cytoscape.Core, link: GraphNodeLink): cytoscape.EdgeSingular | null {
    const fromId = link.from?.id || link.from;
    const toId = link.to?.id || link.to;
    const target = cy.$(`#${toId}`);
    const source = cy.$(`#${fromId}`);

    if (target.length === 0 || source.length === 0) return null;
    const linkExists = cy.$(`#${link.id}`).length > 0;
    if (linkExists) {
      cy.$(`#${link.id}`).remove();
    }
    const data = {
      source: link.from?.id || link.from,
      label: link.label || DefaultValues.link.label,
      name: link.name || '',
      description: link.description,
      target: link.to?.id || link.to,
      color: link?.color || DefaultValues.link.color,
      type: link.type,
      textFontSize: link.textFontSize,
      shape: link.shape || DefaultValues.link.shape,
      excludedFromState: link.excludedFromState,
      direction: link.direction || DefaultValues.link.direction,
      weight: link.weight,
      metadata: link.metadata,
      hasComments: link.hasComments,
      targetEndpoint: link.targetEndpoint,
      sourceEndpoint: link.sourceEndpoint,
      states: link.states,
      id: link.id,
      edgeArrowType: link.edgeArrowType,
      labelSlope: link.labelSlope || DefaultValues.link.labelSlope,
      linkLabelPosition: link.linkLabelPosition || 0,
      linkLabelOffset: link.linkLabelOffset ?? DefaultValues.link.linkLabelOffset,
      // MAP-1547: include the sound URL so a link built/rebuilt from backend
      // data keeps its saved sound instead of losing it.
      soundUrl: link.soundUrl,
      cyedgebendeditingDistances: link.cyedgebendeditingDistances,
      cyedgebendeditingWeights: link.cyedgebendeditingWeights,
      bendPointPositions: link.bendPointPositions,
      controlPointPositions: link.controlPointPositions,
      aiGeneratedDescription: link.aiGeneratedDescription
    };

    // Generate control points for UNBUNDLED_BEZIER links if empty
    if (data.type === 'UNBUNDLED_BEZIER' && (data.controlPointPositions?.length === 0)) {
      // Create temporary edge to calculate positions
      const tempEdge = cy.add({
        group: 'edges',
        data: { source: data.source, target: data.target, id: 'temp-' + data.id },
      });

      // Calculate straight control points
      const anchorPoints = getEqualDistributedAnchorPoints(tempEdge);
      data.controlPointPositions = [anchorPoints.source, anchorPoints.target];

      // Remove temporary edge
      tempEdge.remove();
    }

    let classes = 'bottom-center';
    const currentEdgeViewMode = this.settingsService.linkViewMode;
    if (currentEdgeViewMode !== LinkViewMode.ALL) {
      // The RELATED/SELECTION_ENCLOSED_LINKS re-selection pass (toggleEdgeVisibility)
      // corrects matching edges right after load.
      classes += ' ' + otherLinksClassNames(
        resolveOtherLinksMode(currentEdgeViewMode, this.settingsService.otherLinksMode)
      ).join(' ');
    }

    const isDefaultState = this.mapStateService.isDefaultState();
    //@ts-ignore
    if (!isDefaultState) {
      classes += ' not-default-state';
      const currentStateData = data.states?.find((s: LinkStateData) => s.stateId === this.mapStateService.getCurrentLocalState()?.id);
      if (currentStateData?.link?.type === 'BEZIER') {
        classes += ' ignore_control_points';
      }
    }

    if (data.type === 'BEZIER' && isDefaultState) {
      classes += ' ignore_control_points';
    }


    const currentMap = this.mapService.getCurrentSelectedMapFromStore();
    //@ts-ignore
    if (isInIframe() || (this.isReadOnly || currentMap?.isReadonly) && !this.hasMapPermission('edit')) {
      classes += ' cant-edit';
    }

    const edge = cy.add({
      group: 'edges',
      data,
      classes
    });

    return edge;
  }

  private removeNodeFromParent(node: cytoscape.NodeSingular) {
    const beforeParentId = node.parent().first()?.id() || null;
    const beforePosition = {...node.position()};

    return this.nodeService.removeParent({ childIds: [node.id()], mapId: this.currentMap?.id as string })
      .pipe(
        finalize(() => {
          const afterPosition = {...node.position()};
          node.data('parent', null);
          node.move({ parent: null });
          node.data('draggedByParent', false);

          // Create undo command for extract to canvas
          const change: ContainerChangeSnapshot = {
            nodeId: node.id(),
            beforeParentId,
            afterParentId: null,
            beforePosition,
            afterPosition
          };
          const containerCommand = new ChangeContainerCommand(
              this.cyService,
              this.nodeService,
              this.mapService,
              'extract-to-canvas',
              [change]
          );
          this.undoRedoManager.pushCommand(containerCommand);
        })
      );
  }

  private moveNodeOneLevelUp(node: cytoscape.NodeSingular) {
    const parent = node.parent().first();
    const grandParent = parent?.parent().first() as NodeSingular;
    const beforeParentId = parent?.id() || null;
    const beforePosition = {...node.position()};

    if (grandParent.data() !== undefined) {
      node.move({ parent: grandParent.id() });
      node.data('draggedByParent', false);

      // Create undo command for extract from container
      const change: ContainerChangeSnapshot = {
        nodeId: node.id(),
        beforeParentId,
        afterParentId: grandParent.id(),
        beforePosition,
        afterPosition: {...node.position()}
      };
      const containerCommand = new ChangeContainerCommand(
          this.cyService,
          this.nodeService,
          this.mapService,
          'extract-from-container',
          [change]
      );
      this.undoRedoManager.pushCommand(containerCommand);

      return this.nodeService.updateParent({ childIds: [node.id()], parentId: grandParent.id() });
    } else {
      return this.removeNodeFromParent(node);
    }
  }

  private onTap(cy: cytoscape.Core) {
    cy.on('cxttap', (event) => {

      if (!isEmpty(event.target.data())) {
        const isDefaultState = !this.mapStateService.getCurrentLocalState()?.id;
        const currentStateId = this.mapStateService.getCurrentLocalState()?.id;
        const isMultiSelect = cy.$(':selected').length > 1;
        const data = event.target.data();
        const isShiftPressed = (event.originalEvent as MouseEvent)?.shiftKey ?? ButtonPressService.isShiftPressed;
        if (!isShiftPressed && !isMultiSelect) {
          DashboardComponent.tappedNode = null;
          DashboardComponent.tappedLink = null;
          DashboardComponent.multiSelectedElements = [];
          cy.$(':selected').unselect();
        }
        event.target.select();
        if (event.target.isNode()) {
          DashboardComponent.tappedLink = null;
          if (!isDefaultState) {
            DashboardComponent.tappedNode = data.states?.find((s: NodeStateData) => s.stateId === currentStateId)?.node;
          } else {
            DashboardComponent.tappedNode = data;
          }
        } else {
          DashboardComponent.tappedNode = null;
          DashboardComponent.tappedLink = this.cyService.getLinkDataBasedOnState(event.target as EdgeSingular);
        }
      }
    });
  }

  private createTooltipForEdge(edge: any, mousePosition: { x: number, y: number }) {
    const labelKey = edge.data('label');
    const label = edge.data(labelKey);
    if (!label || label === '') return;

    // Clear any previous timeout to avoid unexpected behavior
    if (this.tooltipTimeout) {
      clearTimeout(this.tooltipTimeout);
    }

    this.tooltipTimeout = setTimeout(() => {
      const containerPos = this.cytoscapeContainer?.nativeElement.getBoundingClientRect();

      // Create and position the tooltip
      const tooltip = document.createElement('div');
      const data = edge.data();

      // Get source and target node names
      const sourceNode = edge.source();
      const targetNode = edge.target();
      const sourceName = sourceNode?.data('name') || 'Unknown';
      const targetName = targetNode?.data('name') || 'Unknown';

      // Get link name based on the label setting
      let linkName = '';
      if (data?.label === 'name') {
        linkName = data.name || '';
      } else {
        linkName = data.metadata?.find((meta: {
          key: string;
          value: string
        }) => meta.key === data.label)?.value || '';
      }

      // Create tooltip content with proper directionality indicators
      let tooltipContent = '';
      if (linkName) {
        if (data.direction === 'bidirected') {
          tooltipContent = `${sourceName} ← ${linkName} → ${targetName}`;
        } else if (data.direction === 'undirected') {
          tooltipContent = `${sourceName} — ${linkName} — ${targetName}`;
        } else {
          // directed (default)
          tooltipContent = `${sourceName} — ${linkName} → ${targetName}`;
        }
      } else {
        if (data.direction === 'bidirected') {
          tooltipContent = `${sourceName} ← → ${targetName}`;
        } else if (data.direction === 'undirected') {
          tooltipContent = `${sourceName} — ${targetName}`;
        } else {
          // directed (default)
          tooltipContent = `${sourceName} → ${targetName}`;
        }
      }
      tooltip.textContent = tooltipContent;

      tooltip.className = 'edge-tooltip';
      tooltip.style.left = `${mousePosition.x - containerPos.left}px`;
      tooltip.style.top = `${mousePosition.y - containerPos.top}px`;

      // Add the tooltip to the container
      this.cytoscapeContainer?.nativeElement.appendChild(tooltip);
    }, 1000);  // wait for 1 second
  }

  private handleEdgeEditingEnd(cy: cytoscape.Core) {
    // Clear tooltip when dragging starts (mousedown/touchstart events)
    const clearTooltip = () => {
      if (this.tooltipTimeout) {
        clearTimeout(this.tooltipTimeout);
      }
      const tooltip = this.cytoscapeContainer?.nativeElement.querySelector('.edge-tooltip');
      if (tooltip) {
        this.cytoscapeContainer?.nativeElement.removeChild(tooltip);
      }
    };

    // Listen for mousedown and touchstart to clear tooltip when dragging anchor points
    cy.on('mousedown touchstart', clearTooltip);

    // Re-trigger tooltip on mouseup/touchend if still hovering over an edge
    cy.on('mouseup touchend', (event) => {
      // Small delay to ensure the drag operation has fully completed
      setTimeout(() => {
        const renderedPosition = event.renderedPosition || event.position;
        if (renderedPosition) {
          // Get elements at the mouse position
          const elementsAtPosition = cy.$(`:grabbable`).filter((element) => {
            const bb = element.renderedBoundingBox();
            return renderedPosition.x >= bb.x1 &&
                   renderedPosition.x <= bb.x2 &&
                   renderedPosition.y >= bb.y1 &&
                   renderedPosition.y <= bb.y2;
          });

          // Find the first edge at this position
          const edgeAtPosition = elementsAtPosition.edges().first();
          if (edgeAtPosition.length > 0) {
            // Call tooltip creation directly
            this.createTooltipForEdge(edgeAtPosition, renderedPosition);
          }
        }
      }, 100); // Small delay to let drag operations complete
    });

    cy.on('edgeediting_moveend', (_event: any, link: EdgeSingular) => {
      if (updateInProgress()) {
        return;
      }
      if (link) {

        // Skip BEZIER edges - they don't need backend updates
        if (link.data('type') === 'BEZIER') {
          return;
        }
        const currentState = this.mapStateService.getCurrentLocalState();

        // Clone edge data, skipping originalEnds (cytoscape-expand-collapse stores Element refs there)
        const safeClone = (data: any) => {
          const cleaned: any = {};
          for (const key in data) {
            if (key === 'originalEnds' || key.startsWith('_')) continue;
            const value = data[key];
            if (value === undefined || typeof value === 'function') continue;
            cleaned[key] = value;
          }
          return structuredClone(cleaned);
        };

        let payload = safeClone(link.data());
        if (payload.states) {
          payload.states = uniqBy(payload.states, 'stateId');
        }
        if (currentState?.id !== undefined) {
          const cleanLinkData = safeClone(link.data());
          if (cleanLinkData.states) {
            cleanLinkData.states = uniqBy(cleanLinkData.states, 'stateId');
          }
          const linkState = payload?.states?.find((s: LinkStateData) => s.stateId === currentState.id);
          const index = payload?.states?.indexOf(linkState);
          const pointData = getPointDataFromAnchorsArray(link, this.edgeEditingInstance, index);
          if (pointData) {
            const statedLinkNoStatesCopy = omit(safeClone(linkState.link), ['states']);
            set(statedLinkNoStatesCopy, pointData.dataKey, pointData.pointData);
            set(statedLinkNoStatesCopy, 'states', cleanLinkData.states);
            payload = statedLinkNoStatesCopy;
          }
        } else {
          const pointData = getPointDataFromAnchorsArray(link, this.edgeEditingInstance);
          if (pointData) {
            set(payload, pointData.dataKey, pointData.pointData);
          }
        }
        if (!isInIframe()) {
          this.updateLink(payload).subscribe();
        }
      }
    });
  }

  private filterNodeProperties(element: any): any {
    // Remove link-specific properties that shouldn't be applied to nodes
    const {
      shape, // Link property
      direction, // Link property
      edgeArrowType, // Link property
      labelSlope, // Link property
      linkLabelPosition, // Link property
      linkLabelOffset, // Link property
      textFontSize, // Link property (nodes use properties.textFontSize)
      from, // Link property
      to, // Link property
      ...nodeProperties
    } = element;

    return nodeProperties;
  }

  private refreshCanvasElements(updatedElements: any[]) {
    if (!this.cy) return;

    // Block sidebar auto-save during model refresh to prevent race conditions
    updateInProgress.set(true);

    // Store current selection and focused element IDs before refresh
    const focusedNodeId = DashboardComponent.tappedNode?.id;
    const focusedLinkId = DashboardComponent.tappedLink?.id;
    const selectedElementIds = this.cy.$(':selected').map(ele => ele.id());


    this.cy.batch(() => {
      updatedElements.forEach((element, index) => {

        // Check if this element is currently focused/tapped
        const isFocusedNode = focusedNodeId === element.id;
        const isFocusedLink = focusedLinkId === element.id;

        // Find existing element in canvas
        const existingElement = this.cy!.$(`#${element.id}`) as NodeSingular | EdgeSingular;

        if (existingElement.length > 0) {
          // Determine if it's a node or link based on the existing element type in canvas
          if (existingElement.isEdge()) {
            // Store old type to detect changes
            const oldType = existingElement.data('type');

            // Preserve existing anchor point data before updating
            const existingAnchorData = {
              cyedgebendeditingDistances: existingElement.data('cyedgebendeditingDistances'),
              cyedgebendeditingWeights: existingElement.data('cyedgebendeditingWeights'),
              cyedgecontroleditingDistances: existingElement.data('cyedgecontroleditingDistances'),
              cyedgecontroleditingWeights: existingElement.data('cyedgecontroleditingWeights'),
              bendPointPositions: existingElement.data('bendPointPositions'),
              controlPointPositions: existingElement.data('controlPointPositions')
            };

            // It's a link - replace the entire data object with the updated element data
            existingElement.data(element);

            // Restore anchor points if they existed and type hasn't changed to BEZIER
            const newType = element.type;
            if (newType !== 'BEZIER' && oldType === newType && !existingElement.hasClass('anchor-init-in-progress')) {
              // Only restore if type hasn't changed and not initializing anchors
              if (existingAnchorData.cyedgebendeditingDistances?.length > 0) {
                existingElement.data('cyedgebendeditingDistances', existingAnchorData.cyedgebendeditingDistances);
                existingElement.data('cyedgebendeditingWeights', existingAnchorData.cyedgebendeditingWeights);
              }
              if (existingAnchorData.cyedgecontroleditingDistances?.length > 0) {
                existingElement.data('cyedgecontroleditingDistances', existingAnchorData.cyedgecontroleditingDistances);
                existingElement.data('cyedgecontroleditingWeights', existingAnchorData.cyedgecontroleditingWeights);
              }
              if (existingAnchorData.bendPointPositions?.length > 0) {
                existingElement.data('bendPointPositions', existingAnchorData.bendPointPositions);
              }
              if (existingAnchorData.controlPointPositions?.length > 0) {
                existingElement.data('controlPointPositions', existingAnchorData.controlPointPositions);
              }
            }

            // Update visual styles for links
            if (element.color) {
              existingElement.style({
                'line-color': element.color,
                'target-arrow-color': element.color,
                'source-arrow-color': element.color
              });
            }
            if (element.weight) {
              existingElement.style('width', element.weight);
            }

            // Initialize anchor points if type changed to a curved type
            if (oldType !== newType) {
              if (newType === 'BEZIER') {
                // Switching to BEZIER - remove anchor points
                existingElement.addClass('ignore_control_points');
                this.resetAllAnchorPointsDistancesAndWeights(existingElement as EdgeSingular);
              } else {
                // Switching to STRAIGHT or UNBUNDLED_BEZIER - add anchor points
                existingElement.removeClass('ignore_control_points');
                this.resetAllAnchorPointsDistancesAndWeights(existingElement as EdgeSingular);

                // Check if anchors need to be created
                const firstEdgeEditing = !this.hasAnyAnchorPoints(existingElement.data());
                if (firstEdgeEditing) {
                  // Create and set anchor points using the helper function
                  const result = createNewAnchorsAndInitiateEdgeEditing(
                    existingElement as EdgeSingular,
                    this.edgeEditingInstance
                  );

                  // If anchor points were created, persist them to backend
                  if (result.dataKey !== 'NO_DATA' && result.anchorsArray?.length > 0) {
                    // Wait a moment for the visual initialization to complete
                    setTimeout(() => {
                      const linkData = existingElement.data();
                      const updatedLinkData = this.updateLinksPointPositionsData(linkData);
                      if (!isInIframe()) {
                        // Set a flag to prevent the response from overwriting our anchor data
                        existingElement.addClass('anchor-init-in-progress');
                        this.updateLink(updatedLinkData as GraphNodeLink).subscribe({
                          complete: () => {
                            setTimeout(() => {
                              existingElement.removeClass('anchor-init-in-progress');
                            }, 500);
                          }
                        });
                      }
                    }, 200);
                  }
                }
              }
            }

            if (isFocusedLink) {
              DashboardComponent.tappedLink = null;
              setTimeout(() => {
                DashboardComponent.tappedLink = { ...element };
              }, 10);
            }
          } else {
            // It's a node - update the existing node data in place without removing
            // Filter out any link-specific properties that might have been applied incorrectly
            const cleanElement = this.filterNodeProperties(element);

            // Replace the entire data object with the updated element data
            (existingElement as any).data(cleanElement);

            // Update static focused node if this was the focused element
            if (isFocusedNode) {
              // Force change detection by nullifying first, then setting fresh data
              DashboardComponent.tappedNode = null;
              // Use setTimeout to ensure change detection cycle completes
              setTimeout(() => {
                DashboardComponent.tappedNode = { ...cleanElement }; // Create new object reference with filtered data
              }, 10);
            }
          }
        }

        // Update component data arrays to prevent stale data
        const existingElementForArray = this.cy!.$(`#${element.id}`);
        if (existingElementForArray.length > 0 && !existingElementForArray.isEdge()) {
          // It's a node - update nodes array with filtered properties
          const nodeIndex = this.nodes.findIndex(n => n.id === element.id);
          if (nodeIndex !== -1) {
            this.nodes[nodeIndex] = this.filterNodeProperties(element);
          }
        }

        // Update multiSelectedElements if this element is in the selection
        const multiSelectedIndex = DashboardComponent.multiSelectedElements.findIndex(el => el.id === element.id);
        if (multiSelectedIndex !== -1) {
          // Use filtered data for nodes, original data for links
          const existingForMulti = this.cy!.$(`#${element.id}`);
          DashboardComponent.multiSelectedElements[multiSelectedIndex] = existingForMulti.length > 0 && !existingForMulti.isEdge()
            ? this.filterNodeProperties(element)
            : element;
        }
      });
    });

    // Restore selection state after refresh (without triggering update events)
    // Only restore selection for elements that were originally selected (nodes in this case)
    if (selectedElementIds.length > 0) {
      selectedElementIds.forEach(id => {
        const element = this.cy!.$(`#${id}`);
        // Only select if the element exists and was in our updatedElements list (not a restored link)
        if (element.length > 0 && updatedElements.some(el => el.id === id)) {
          element.select();
        }
      });
    }

    // Re-enable sidebar auto-save after model refresh is complete
    // Increased timeout to prevent race conditions with form auto-save
    setTimeout(() => {
      updateInProgress.set(false);
    }, 500);
  }

  // --- Overlay Element Methods ---

  private loadOverlayElements(): void {
    if (!this.overlayManager.isEnabled() || !this.currentMap?.id) return;

    this.overlayElementService.getAllForMap(this.currentMap.id)
      .subscribe(elements => {
        this.overlayManager.loadElements(elements);
      });
  }

  private setupOverlayElementEvents(): void {
    if (!this.overlayManager.isEnabled()) return;

    this.overlayManager.events$.subscribe((event: OverlayElementEvent) => {
      switch (event.type) {
        case 'select':
          this.onOverlayElementSelect(event.element!);
          break;
        case 'deselect':
          this.onOverlayElementDeselect();
          break;
        case 'move':
          this.onOverlayElementMove(event);
          break;
        case 'transform':
          this.onOverlayElementTransform(event);
          break;
      }
    });
  }

  private onOverlayElementSelect(element: OverlayElement): void {
    // Deselect cytoscape elements
    if (this.cy) {
      this.cy.$(':selected').unselect();
    }
    DashboardComponent.tappedNode = null;
    DashboardComponent.tappedLink = null;
    DashboardComponent.tappedOverlayElement = element;

    this.selectedNodes = [];
    this.selectedLinks = [];

    this.sidebarService.changeSelectedOverlayElement(element);
  }

  private onOverlayElementDeselect(): void {
    DashboardComponent.tappedOverlayElement = null;
    this.sidebarService.changeSelectedOverlayElement(null);
  }

  private onOverlayElementMove(event: OverlayElementEvent): void {
    if (!event.element?.id || !event.before || !event.after) return;

    const snapshot: OverlayElementMoveSnapshot = {
      elementId: event.element.id,
      before: event.before as { x: number; y: number },
      after: event.after as { x: number; y: number }
    };

    const command = new MoveOverlayElementCommand(
      this.overlayElementService,
      this.overlayManager,
      snapshot
    );
    this.undoRedoManager.pushCommand(command);

    // Persist position to backend and sync local data from server response
    this.overlayElementService.updatePosition(
      event.element.id,
      event.after.x!,
      event.after.y!
    ).subscribe(updated => {
      this.overlayManager.updateElement(updated);
    });
  }

  private onOverlayElementTransform(event: OverlayElementEvent): void {
    if (!event.element?.id || !event.before || !event.after) return;

    const snapshot: OverlayElementResizeSnapshot = {
      elementId: event.element.id,
      before: {
        width: event.before.width || 100,
        height: event.before.height || 100,
        rotation: event.before.rotation || 0,
        x: event.element.x || 0,
        y: event.element.y || 0
      },
      after: {
        width: event.after.width!,
        height: event.after.height!,
        rotation: event.after.rotation!,
        x: event.after.x || event.element.x || 0,
        y: event.after.y || event.element.y || 0
      }
    };

    const command = new ResizeOverlayElementCommand(
      this.overlayElementService,
      this.overlayManager,
      snapshot
    );
    this.undoRedoManager.pushCommand(command);

    // Persist to backend and sync local data from server response
    this.overlayElementService.update({
      id: event.element.id,
      width: event.after.width,
      height: event.after.height,
      rotation: event.after.rotation,
      x: event.after.x,
      y: event.after.y
    }).subscribe(updated => {
      this.overlayManager.updateElement(updated);
    });
  }

  createOverlayElement(x: number, y: number): void {
    if (!this.currentMap?.id || !this.overlayManager.isEnabled()) return;

    const element: OverlayElement = {
      name: 'Rectangle',
      x,
      y,
      width: 100,
      height: 100,
      rotation: 0,
      type: 'RECTANGLE',
      properties: {
        opacity: 1,
        backgroundColor: '#d3d3d3',
        borderColor: '#000000',
        borderWidth: 2,
        borderStyle: 'solid',
        zIndex: 0
      },
      map: {id: this.currentMap.id} as any
    };

    this.overlayElementService.create(element).subscribe(created => {
      this.overlayManager.addElement(created);
      this.overlayManager.selectElement(created.id!);

      // Push undo command
      const snapshot: OverlayElementSnapshot = {element: created};
      const command = new CreateOverlayElementCommand(
        this.overlayElementService,
        this.overlayManager,
        snapshot
      );
      this.undoRedoManager.pushCommand(command);
    });
  }

  deleteOverlayElement(elementId: string): void {
    const element = this.overlayManager.getElements().find(e => e.id === elementId);
    if (!element) return;

    const snapshot: OverlayElementSnapshot = {element: structuredClone(element)};
    const command = new DeleteOverlayElementCommand(
      this.overlayElementService,
      this.overlayManager,
      snapshot
    );

    this.undoRedoManager.executeCommand(command);
    DashboardComponent.tappedOverlayElement = null;
    this.sidebarService.changeSelectedOverlayElement(null);
  }

}
